import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import html2canvas from "html2canvas";
import { computeAsMixedRatio } from "./engine/composition";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { setRoute } from "./nav/useRoute";
import { summariseBlend, BlendItem, Bucket } from "./engine/goldenRatio";
import {
  TargetBucket,
  loadGrTargets,
  saveGrTargets,
  grTargetNumbers,
  grTargetsAreDefault,
  GR_DEFAULT_TARGETS,
} from "./engine/goldenRatioTargets";

/** The three target buckets, in the order the spec states them. Targets are
 *  editable on the card and the full screen; both share one localStorage copy
 *  (see engine/goldenRatioTargets.ts). Defaults are Joana's 25/55/20. */
const GOLDEN_RATIO_ROWS: { bucket: Bucket; label: string }[] = [
  { bucket: "hydro", label: "Hydrolysed" },
  { bucket: "isolate", label: "Isolate" },
  { bucket: "pulse", label: "Pulse flour" },
];

/** Chronological sort of formulas (F001, F002, ... F222, F223, ...). */
const compareFormulaCodes = (a: any, b: any) => {
  const na = parseInt(String(a?.code || "").replace(/\D/g, ""), 10) || 0;
  const nb = parseInt(String(b?.code || "").replace(/\D/g, ""), 10) || 0;
  if (na !== nb) return na - nb;
  return String(a?.code || "").localeCompare(String(b?.code || ""));
};
import { isSupabaseConfigured, updateRow, updateRowByColumn } from "./supabase/client";
import { supabase } from "./supabase/auth";
import { loadSupabaseData, loadFormulaComponents } from "./supabase/loader";
import type { SupabaseData, AppIngredient } from "./supabase/loader";
import { useAuth } from "./AuthProvider";
import { logActivity } from "./activityLog";
import { getDataSource } from "./helpers/getDataSource";
import IngredientPicker from "./IngredientSearchEditor";
import WholeFoodsComplianceCard from "./WholeFoodsComplianceCard";
import { useTranslation } from 'react-i18next';

// Error boundary to isolate chart rendering failures from crashing the whole app
class ChartErrorBoundary extends React.Component<
  { children: React.ReactNode; fallbackHeight?: string },
  { hasError: boolean; error: Error | null }
> {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            height: this.props.fallbackHeight || "100px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#9ca3af",
            fontSize: "11px",
            backgroundColor: "#f9fafb",
            borderRadius: "8px",
            border: "1px dashed #d1d5db",
          }}
        >
          Chart unavailable
        </div>
      );
    }
    return this.props.children;
  }
}

// Canada/WHO Reference Pattern (mg/g protein)
const REFERENCE_PATTERN = {
  histidine: 19,
  isoleucine: 28,
  leucine: 66,
  lysine: 58,
  methionine_cysteine: 25,
  phenylalanine_tyrosine: 63,
  threonine: 34,
  tryptophan: 11,
  valine: 35,
};

const AA_NAMES = {
  histidine: "His",
  isoleucine: "Ile",
  leucine: "Leu",
  lysine: "Lys",
  methionine_cysteine: "M+C",
  phenylalanine_tyrosine: "P+T",
  threonine: "Thr",
  tryptophan: "Trp",
  valine: "Val",
};

const AA_FULL_NAMES = {
  histidine: "Histidine",
  isoleucine: "Isoleucine",
  leucine: "Leucine",
  lysine: "Lysine",
  methionine_cysteine: "Met + Cys",
  phenylalanine_tyrosine: "Phe + Tyr",
  threonine: "Threonine",
  tryptophan: "Tryptophan",
  valine: "Valine",
};

// ============================================
// EMBEDDED DEMO INGREDIENT DATA (fallback when Supabase is unavailable)
// Last synced: 2026-01-30
// ============================================

const DB_INGREDIENTS = {
  "Wheat Flour": {
    id: "WHEAT_FLOUR",
    protein_per_100g: 10.3,
    digestibility: 0.86,
    color: "#f59e0b",
    cost_per_kg: 0.8,
    fat: 1.5,
    satfat: 0.2,
    transfat: 0,
    cholesterol: 0,
    carbs: 72,
    fiber: 2.7,
    sugar: 0.3,
    sodium: 2,
    moisture: 11.9,
    vitaminD: 0,
    calcium: 15,
    iron: 1.2,
    potassium: 107,
    fdaName: "Wheat Flour",
    amino_acids: {
      histidine: 22,
      isoleucine: 36,
      leucine: 69,
      lysine: 27,
      methionine_cysteine: 37,
      phenylalanine_tyrosine: 80,
      threonine: 29,
      tryptophan: 12,
      valine: 44,
    },
  },
  "Wheat Gluten": {
    id: "WHEAT_GLUTEN",
    protein_per_100g: 75.2,
    digestibility: 0.99,
    color: "#eab308",
    cost_per_kg: 4.5,
    fat: 1.8,
    satfat: 0.3,
    transfat: 0,
    cholesterol: 0,
    carbs: 14,
    fiber: 0.6,
    sugar: 0,
    sodium: 30,
    moisture: 8.2,
    vitaminD: 0,
    calcium: 50,
    iron: 1.5,
    potassium: 100,
    fdaName: "Vital Wheat Gluten",
    amino_acids: {
      histidine: 23,
      isoleucine: 38,
      leucine: 70,
      lysine: 18,
      methionine_cysteine: 38,
      phenylalanine_tyrosine: 88,
      threonine: 27,
      tryptophan: 10,
      valine: 42,
    },
  },
  "Soy Protein Isolate": {
    id: "SOY_PROTEIN_ISOLATE",
    protein_per_100g: 90,
    digestibility: 0.95,
    color: "#22c55e",
    cost_per_kg: 8,
    fat: 0.5,
    satfat: 0.1,
    transfat: 0,
    cholesterol: 0,
    carbs: 2,
    fiber: 0,
    sugar: 0,
    sodium: 1000,
    moisture: 5,
    vitaminD: 0,
    calcium: 200,
    iron: 14.5,
    potassium: 80,
    fdaName: "Soy Protein Isolate",
    amino_acids: {
      histidine: 26,
      isoleucine: 49,
      leucine: 82,
      lysine: 63,
      methionine_cysteine: 26,
      phenylalanine_tyrosine: 90,
      threonine: 38,
      tryptophan: 14,
      valine: 50,
    },
  },
  "Chickpea (Dry)": {
    id: "CHICKPEA_DRY",
    protein_per_100g: 20.5,
    digestibility: 0.72,
    color: "#a855f7",
    cost_per_kg: 2.5,
    fat: 6,
    satfat: 0.6,
    transfat: 0,
    cholesterol: 0,
    carbs: 51,
    fiber: 12.2,
    sugar: 10.7,
    sodium: 24,
    moisture: 7.7,
    vitaminD: 0,
    calcium: 105,
    iron: 6.2,
    potassium: 875,
    fdaName: "Chickpea Flour",
    amino_acids: {
      histidine: 29,
      isoleucine: 43,
      leucine: 73,
      lysine: 67,
      methionine_cysteine: 25,
      phenylalanine_tyrosine: 85,
      threonine: 37,
      tryptophan: 10,
      valine: 43,
    },
  },
  "Chickpea (Cooked)": {
    id: "CHICKPEA_COOKED",
    protein_per_100g: 8.9,
    digestibility: 0.84,
    color: "#ec4899",
    cost_per_kg: 4,
    fat: 2.6,
    satfat: 0.3,
    transfat: 0,
    cholesterol: 0,
    carbs: 27,
    fiber: 7.6,
    sugar: 4.8,
    sodium: 7,
    moisture: 60.2,
    vitaminD: 0,
    calcium: 49,
    iron: 2.9,
    potassium: 291,
    fdaName: "Cooked Chickpeas",
    amino_acids: {
      histidine: 29,
      isoleucine: 43,
      leucine: 73,
      lysine: 67,
      methionine_cysteine: 25,
      phenylalanine_tyrosine: 85,
      threonine: 37,
      tryptophan: 10,
      valine: 43,
    },
  },
  "Pea Protein": {
    id: "PEA_PROTEIN",
    protein_per_100g: 80,
    digestibility: 0.89,
    color: "#3b82f6",
    cost_per_kg: 10,
    fat: 2,
    satfat: 0.4,
    transfat: 0,
    cholesterol: 0,
    carbs: 4,
    fiber: 5,
    sugar: 0,
    sodium: 1100,
    moisture: 6,
    vitaminD: 0,
    calcium: 60,
    iron: 8,
    potassium: 100,
    fdaName: "Pea Protein",
    amino_acids: {
      histidine: 25,
      isoleucine: 45,
      leucine: 82,
      lysine: 72,
      methionine_cysteine: 21,
      phenylalanine_tyrosine: 86,
      threonine: 36,
      tryptophan: 10,
      valine: 48,
    },
  },
  "Rice Protein": {
    id: "RICE_PROTEIN",
    protein_per_100g: 80,
    digestibility: 0.79,
    color: "#06b6d4",
    cost_per_kg: 12,
    fat: 3.5,
    satfat: 0.7,
    transfat: 0,
    cholesterol: 0,
    carbs: 7,
    fiber: 3,
    sugar: 0,
    sodium: 50,
    moisture: 6,
    vitaminD: 0,
    calcium: 20,
    iron: 3,
    potassium: 50,
    fdaName: "Rice Protein",
    amino_acids: {
      histidine: 20,
      isoleucine: 32,
      leucine: 66,
      lysine: 30,
      methionine_cysteine: 35,
      phenylalanine_tyrosine: 78,
      threonine: 28,
      tryptophan: 9,
      valine: 46,
    },
  },
  Water: {
    id: "WATER",
    protein_per_100g: 0,
    digestibility: 0,
    color: "#60a5fa",
    cost_per_kg: 0.01,
    fat: 0,
    satfat: 0,
    transfat: 0,
    cholesterol: 0,
    carbs: 0,
    fiber: 0,
    sugar: 0,
    sodium: 0,
    moisture: 100,
    vitaminD: 0,
    calcium: 0,
    iron: 0,
    potassium: 0,
    fdaName: "Water",
    amino_acids: {
      histidine: 0,
      isoleucine: 0,
      leucine: 0,
      lysine: 0,
      methionine_cysteine: 0,
      phenylalanine_tyrosine: 0,
      threonine: 0,
      tryptophan: 0,
      valine: 0,
    },
  },
  "Sunflower Oil": {
    id: "SUNFLOWER_OIL",
    protein_per_100g: 0,
    digestibility: 0,
    color: "#fcd34d",
    cost_per_kg: 2,
    fat: 100,
    satfat: 10.3,
    transfat: 0,
    cholesterol: 0,
    carbs: 0,
    fiber: 0,
    sugar: 0,
    sodium: 0,
    moisture: 0,
    vitaminD: 0,
    calcium: 0,
    iron: 0,
    potassium: 0,
    fdaName: "Sunflower Oil",
    amino_acids: {
      histidine: 0,
      isoleucine: 0,
      leucine: 0,
      lysine: 0,
      methionine_cysteine: 0,
      phenylalanine_tyrosine: 0,
      threonine: 0,
      tryptophan: 0,
      valine: 0,
    },
  },
  "Tapioca Starch": {
    id: "TAPIOCA_STARCH",
    protein_per_100g: 0.2,
    digestibility: 0.5,
    color: "#e5e7eb",
    cost_per_kg: 3,
    fat: 0.02,
    satfat: 0,
    transfat: 0,
    cholesterol: 0,
    carbs: 88,
    fiber: 0.9,
    sugar: 3.4,
    sodium: 1,
    moisture: 11,
    vitaminD: 0,
    calcium: 20,
    iron: 1.6,
    potassium: 11,
    fdaName: "Tapioca Starch",
    amino_acids: {
      histidine: 0,
      isoleucine: 0,
      leucine: 0,
      lysine: 0,
      methionine_cysteine: 0,
      phenylalanine_tyrosine: 0,
      threonine: 0,
      tryptophan: 0,
      valine: 0,
    },
  },
  "Psyllium Husk": {
    id: "PSYLLIUM_HUSK",
    protein_per_100g: 2.4,
    digestibility: 0.5,
    color: "#d97706",
    cost_per_kg: 15,
    fat: 0.6,
    satfat: 0.1,
    transfat: 0,
    cholesterol: 0,
    carbs: 1.7,
    fiber: 85,
    sugar: 0,
    sodium: 11,
    moisture: 8,
    vitaminD: 0,
    calcium: 40,
    iron: 4,
    potassium: 600,
    fdaName: "Psyllium Husk",
    amino_acids: {
      histidine: 20,
      isoleucine: 30,
      leucine: 50,
      lysine: 40,
      methionine_cysteine: 20,
      phenylalanine_tyrosine: 60,
      threonine: 30,
      tryptophan: 8,
      valine: 35,
    },
  },
  "Seasoning Blend": {
    id: "SEASONING_BLEND",
    protein_per_100g: 10,
    digestibility: 0.7,
    color: "#f97316",
    cost_per_kg: 8,
    fat: 5,
    satfat: 1,
    transfat: 0,
    cholesterol: 0,
    carbs: 50,
    fiber: 8,
    sugar: 3,
    sodium: 19500,
    moisture: 8,
    vitaminD: 0,
    calcium: 100,
    iron: 5,
    potassium: 200,
    fdaName: "Seasoning (Salt, Spices, Garlic Powder, Onion Powder)",
    amino_acids: {
      histidine: 20,
      isoleucine: 30,
      leucine: 55,
      lysine: 45,
      methionine_cysteine: 22,
      phenylalanine_tyrosine: 65,
      threonine: 30,
      tryptophan: 9,
      valine: 38,
    },
  },
};

// Build lookup by ID
const DB_INGREDIENTS_BY_ID: Record<string, any> = {};
Object.entries(DB_INGREDIENTS).forEach(([name, data]) => {
  DB_INGREDIENTS_BY_ID[data.id] = { ...data, commonName: name };
});


// EMBEDDED DEMO FORMULAS (fallback when Supabase is unavailable)
const DB_FORMULAS = [
  {
    code: "F001",
    displayName: "Test Formula",
    owner: "Meagan",
    date: "2026-01-30",
    notes: "crazy test",
    pieceWeight: 9,
    servingSize: 84,
    doughRatio: 50,
    doughFormula: {
      "Wheat Flour": 55,
      "Wheat Gluten": 5,
      "Chickpea (Dry)": 8,
      Water: 25,
      "Sunflower Oil": 4,
      "Tapioca Starch": 3,
      "Rice Protein": 12,
    },
    fillingFormula: {
      "Chickpea (Dry)": 15,
      "Chickpea (Cooked)": 35,
      Water: 20,
      "Psyllium Husk": 3,
      "Sunflower Oil": 5,
      "Seasoning Blend": 2,
      "Soy Protein Isolate": 15,
    },
  },
  {
    code: "F002",
    displayName: "Test 2",
    owner: "",
    date: "",
    notes: "",
    pieceWeight: 9.2,
    servingSize: 84,
    doughRatio: 50,
    doughFormula: {
      "Wheat Flour": 55,
      "Wheat Gluten": 5,
      "Chickpea (Dry)": 8,
      Water: 25,
      "Sunflower Oil": 4,
      "Tapioca Starch": 3,
    },
    fillingFormula: {
      "Chickpea (Dry)": 15,
      "Chickpea (Cooked)": 35,
      Water: 20,
      "Psyllium Husk": 3,
      "Sunflower Oil": 5,
      "Seasoning Blend": 2,
      "Soy Protein Isolate": 15,
      "Pea Protein": 0,
      "Rice Protein": 0,
    },
  },
  {
    code: "F003",
    displayName: "Blank Template",
    owner: "",
    date: "",
    notes: "",
    pieceWeight: 9.2,
    servingSize: 84,
    doughRatio: 50,
    doughFormula: {
      "Wheat Flour": 60,
      Water: 30,
      "Sunflower Oil": 5,
      "Tapioca Starch": 5,
    },
    fillingFormula: {
      "Chickpea (Cooked)": 50,
      Water: 30,
      "Sunflower Oil": 10,
      "Seasoning Blend": 10,
    },
  },
];

// DEMO DATA (separate from database)
const DEMO_INGREDIENTS = { ...DB_INGREDIENTS };
const DEMO_DOUGH_FORMULA = {
  "Wheat Flour": 55,
  "Wheat Gluten": 5,
  "Chickpea (Dry)": 8,
  Water: 25,
  "Sunflower Oil": 4,
  "Tapioca Starch": 3,
};
const DEMO_FILLING_FORMULA = {
  "Chickpea (Dry)": 15,
  "Chickpea (Cooked)": 35,
  Water: 20,
  "Psyllium Husk": 3,
  "Sunflower Oil": 5,
  "Seasoning Blend": 2,
  "Soy Protein Isolate": 15,
  "Pea Protein": 0,
  "Rice Protein": 0,
};

// FDA Rounding Rules (21 CFR 101.9)
const fdaRound = {
  calories: (val) => {
    if (val < 5) return 0;
    if (val <= 50) return Math.round(val / 5) * 5;
    return Math.round(val / 10) * 10;
  },
  fat: (val) => {
    if (val < 0.5) return 0;
    if (val < 5) return Math.round(val * 2) / 2;
    return Math.round(val);
  },
  satfat: (val) => {
    if (val < 0.5) return 0;
    if (val < 5) return Math.round(val * 2) / 2;
    return Math.round(val);
  },
  transfat: (val) => {
    if (val < 0.5) return 0;
    if (val < 5) return Math.round(val * 2) / 2;
    return Math.round(val);
  },
  cholesterol: (val) => {
    if (val < 2) return 0;
    if (val <= 5) return "<5";
    return Math.round(val / 5) * 5;
  },
  sodium: (val) => {
    if (val < 5) return 0;
    if (val <= 140) return Math.round(val / 5) * 5;
    return Math.round(val / 10) * 10;
  },
  carbs: (val) => (val < 0.5 ? 0 : Math.round(val)),
  fiber: (val) => (val < 0.5 ? 0 : Math.round(val)),
  sugar: (val) => (val < 0.5 ? 0 : Math.round(val)),
  protein: (val) => (val < 0.5 ? 0 : Math.round(val)),
  // Micronutrient rounding rules per 21 CFR 101.9(c)(8)(ii)
  vitaminD: (val) => {
    if (val < 0.1) return 0;
    if (val < 10) return Math.round(val * 10) / 10;
    return Math.round(val);
  },
  calcium: (val) => {
    if (val < 10) return 0;
    if (val <= 100) return Math.round(val / 10) * 10;
    if (val <= 1000) return Math.round(val / 50) * 50;
    return Math.round(val / 100) * 100;
  },
  iron: (val) => {
    if (val < 0.5) return 0;
    if (val < 2) return Math.round(val * 10) / 10;
    return Math.round(val * 2) / 2;
  },
  potassium: (val) => {
    if (val < 5) return 0;
    if (val <= 140) return Math.round(val / 5) * 5;
    return Math.round(val / 10) * 10;
  },
  // FDA 21 CFR 101.9(b)(8) - Servings per container rounding
  // Between 2-5 servings: round to nearest 0.5 with "about" prefix
  // Above 5 servings: round to nearest whole number with "about" prefix
  servingsPerContainer: (val) => {
    if (val < 2)
      return {
        value: Math.round(val),
        display: String(Math.round(val)),
        needsAbout: false,
      };
    if (val <= 5) {
      const rounded = Math.round(val * 2) / 2;
      return { value: rounded, display: String(rounded), needsAbout: true };
    }
    const rounded = Math.round(val);
    return { value: rounded, display: String(rounded), needsAbout: true };
  },
  // Pieces per serving should be whole numbers for discrete unit products
  piecesPerServing: (val) => Math.round(val),
};

const DRV = {
  fat: 78,
  satfat: 20,
  cholesterol: 300,
  sodium: 2300,
  carbs: 275,
  fiber: 28,
  protein: 50,
  vitaminD: 20,
  calcium: 1300,
  iron: 18,
  potassium: 4700,
};

const FDA_INFO_KEYS = {
  calories: {
    reg: "21 CFR 101.9(c)(1)",
    ruleKey: "fdaInfo.caloriesRule",
    drvKey: "fdaInfo.caloriesDrv",
  },
  fat: {
    reg: "21 CFR 101.9(c)(2)",
    ruleKey: "fdaInfo.fatRule",
    drvKey: "fdaInfo.fatDrv",
  },
  satfat: {
    reg: "21 CFR 101.9(c)(2)(i)",
    ruleKey: "fdaInfo.satfatRule",
    drvKey: "fdaInfo.satfatDrv",
  },
  transfat: {
    reg: "21 CFR 101.9(c)(2)(ii)",
    ruleKey: "fdaInfo.transfatRule",
    drvKey: "fdaInfo.transfatDrv",
  },
  cholesterol: {
    reg: "21 CFR 101.9(c)(3)",
    ruleKey: "fdaInfo.cholesterolRule",
    drvKey: "fdaInfo.cholesterolDrv",
  },
  sodium: {
    reg: "21 CFR 101.9(c)(4)",
    ruleKey: "fdaInfo.sodiumRule",
    drvKey: "fdaInfo.sodiumDrv",
  },
  carbs: {
    reg: "21 CFR 101.9(c)(6)",
    ruleKey: "fdaInfo.carbsRule",
    drvKey: "fdaInfo.carbsDrv",
  },
  fiber: {
    reg: "21 CFR 101.9(c)(6)(i)",
    ruleKey: "fdaInfo.fiberRule",
    drvKey: "fdaInfo.fiberDrv",
  },
  sugar: {
    reg: "21 CFR 101.9(c)(6)(ii)",
    ruleKey: "fdaInfo.sugarRule",
    drvKey: "fdaInfo.sugarDrv",
  },
  protein: {
    reg: "21 CFR 101.9(c)(7)",
    ruleKey: "fdaInfo.proteinRule",
    drvKey: "fdaInfo.proteinDrv",
    noteKey: "fdaInfo.proteinNote",
  },
  vitaminD: {
    reg: "21 CFR 101.9(c)(8)(ii)",
    ruleKey: "fdaInfo.vitaminDRule",
    drvKey: "fdaInfo.vitaminDDrv",
    noteKey: "fdaInfo.vitaminDNote",
  },
  calcium: {
    reg: "21 CFR 101.9(c)(8)(ii)",
    ruleKey: "fdaInfo.calciumRule",
    drvKey: "fdaInfo.calciumDrv",
    noteKey: "fdaInfo.calciumNote",
  },
  iron: {
    reg: "21 CFR 101.9(c)(8)(ii)",
    ruleKey: "fdaInfo.ironRule",
    drvKey: "fdaInfo.ironDrv",
    noteKey: "fdaInfo.ironNote",
  },
  potassium: {
    reg: "21 CFR 101.9(c)(8)(ii)",
    ruleKey: "fdaInfo.potassiumRule",
    drvKey: "fdaInfo.potassiumDrv",
    noteKey: "fdaInfo.potassiumNote",
  },
};

const Icons = {
  trash: (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </svg>
  ),
  plus: (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  ),
  check: (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  ),
  alert: (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  ),
  zap: (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </svg>
  ),
  sparkles: (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="m12 3-1.9 5.8a2 2 0 0 1-1.3 1.3L3 12l5.8 1.9a2 2 0 0 1 1.3 1.3L12 21l1.9-5.8a2 2 0 0 1 1.3-1.3L21 12l-5.8-1.9a2 2 0 0 1-1.3-1.3L12 3Z" />
    </svg>
  ),
  info: (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  ),
  chevron: (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  ),
  beaker: (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M4.5 3h15M6 3v16a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V3M6 14h12" />
    </svg>
  ),
  database: (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <ellipse cx="12" cy="5" rx="9" ry="3" />
      <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
      <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
    </svg>
  ),
  gitCompare: (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <circle cx="18" cy="18" r="3" />
      <circle cx="6" cy="6" r="3" />
      <path d="M13 6h3a2 2 0 0 1 2 2v7" />
      <path d="M11 18H8a2 2 0 0 1-2-2V9" />
    </svg>
  ),
  link: (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  ),
  externalLink: (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  ),
  droplet: (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z" />
    </svg>
  ),
  flame: (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z" />
    </svg>
  ),
  lock: (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  ),
  settings: (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <circle cx="12" cy="12" r="3" />
      <path d="M12 1v6m0 6v10M4.22 4.22l4.24 4.24m7.08 7.08l4.24 4.24M1 12h6m6 0h10M4.22 19.78l4.24-4.24m7.08-7.08l4.24-4.24" />
    </svg>
  ),
  layers: (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <polygon points="12 2 2 7 12 12 22 7 12 2" />
      <polyline points="2 17 12 22 22 17" />
      <polyline points="2 12 12 17 22 12" />
    </svg>
  ),
  arrowDown: (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <line x1="12" y1="5" x2="12" y2="19" />
      <polyline points="19 12 12 19 5 12" />
    </svg>
  ),
  arrowUp: (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <line x1="12" y1="19" x2="12" y2="5" />
      <polyline points="5 12 12 5 19 12" />
    </svg>
  ),
  trendUp: (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
      <polyline points="17 6 23 6 23 12" />
    </svg>
  ),
  trendDown: (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <polyline points="23 18 13.5 8.5 8.5 13.5 1 6" />
      <polyline points="17 18 23 18 23 12" />
    </svg>
  ),
  refresh: (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <polyline points="23 4 23 10 17 10" />
      <polyline points="1 20 1 14 7 14" />
      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
    </svg>
  ),
  minus: (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  ),
  package: (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="m7.5 4.27 9 5.15M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
      <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
      <line x1="12" y1="22.08" x2="12" y2="12" />
    </svg>
  ),
  dollarSign: (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <line x1="12" y1="1" x2="12" y2="23" />
      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </svg>
  ),
  award: (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <circle cx="12" cy="8" r="7" />
      <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88" />
    </svg>
  ),
  target: (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <circle cx="12" cy="12" r="10" />
      <circle cx="12" cy="12" r="6" />
      <circle cx="12" cy="12" r="2" />
    </svg>
  ),
  move: (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <polyline points="5 9 2 12 5 15" />
      <polyline points="9 5 12 2 15 5" />
      <polyline points="15 19 12 22 9 19" />
      <polyline points="19 9 22 12 19 15" />
      <line x1="2" y1="12" x2="22" y2="12" />
      <line x1="12" y1="2" x2="12" y2="22" />
    </svg>
  ),
  maximize: (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <polyline points="15 3 21 3 21 9" />
      <polyline points="9 21 3 21 3 15" />
      <line x1="21" y1="3" x2="14" y2="10" />
      <line x1="3" y1="21" x2="10" y2="14" />
    </svg>
  ),
  minimize: (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <polyline points="4 14 10 14 10 20" />
      <polyline points="20 10 14 10 14 4" />
      <line x1="14" y1="10" x2="21" y2="3" />
      <line x1="3" y1="21" x2="10" y2="14" />
    </svg>
  ),
  ninja: (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M8 15h8" />
      <circle cx="9" cy="9" r="1" />
      <circle cx="15" cy="9" r="1" />
    </svg>
  ),
  home: (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  ),
  arrowLeft: (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <line x1="19" y1="12" x2="5" y2="12" />
      <polyline points="12 19 5 12 12 5" />
    </svg>
  ),
  // Packaging Dashboard Icons
  clipboardList: (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
      <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
      <line x1="9" y1="12" x2="15" y2="12" />
      <line x1="9" y1="16" x2="15" y2="16" />
    </svg>
  ),
  pieChart: (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M21.21 15.89A10 10 0 1 1 8 2.83" />
      <path d="M22 12A10 10 0 0 0 12 2v10z" />
    </svg>
  ),
  calculator: (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <rect x="4" y="2" width="16" height="20" rx="2" />
      <line x1="8" y1="6" x2="16" y2="6" />
      <line x1="8" y1="10" x2="8" y2="10.01" />
      <line x1="12" y1="10" x2="12" y2="10.01" />
      <line x1="16" y1="10" x2="16" y2="10.01" />
      <line x1="8" y1="14" x2="8" y2="14.01" />
      <line x1="12" y1="14" x2="12" y2="14.01" />
      <line x1="16" y1="14" x2="16" y2="14.01" />
      <line x1="8" y1="18" x2="8" y2="18.01" />
      <line x1="12" y1="18" x2="12" y2="18.01" />
      <line x1="16" y1="18" x2="16" y2="18.01" />
    </svg>
  ),
  speedometer: (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M12 2a10 10 0 1 0 10 10H12V2z" />
      <path d="M12 12l6-6" />
      <circle cx="12" cy="12" r="2" />
    </svg>
  ),
  bookOpen: (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
      <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
    </svg>
  ),
  barChart: (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <line x1="12" y1="20" x2="12" y2="10" />
      <line x1="18" y1="20" x2="18" y2="4" />
      <line x1="6" y1="20" x2="6" y2="16" />
    </svg>
  ),
  servings: (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2" />
      <path d="M7 2v20" />
      <path d="M21 15V2a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3v7" />
    </svg>
  ),
};

const BACKGROUNDS = {
  aurora: `radial-gradient(at 20% 30%, hsla(280, 85%, 25%, 1) 0px, transparent 50%),radial-gradient(at 80% 20%, hsla(320, 75%, 30%, 1) 0px, transparent 50%),radial-gradient(at 50% 80%, hsla(200, 80%, 25%, 1) 0px, transparent 50%),linear-gradient(180deg, hsla(270, 50%, 12%, 1) 0%, hsla(280, 60%, 8%, 1) 100%)`,
  neon: `radial-gradient(at 30% 20%, hsla(180, 100%, 30%, 0.4) 0px, transparent 40%),radial-gradient(at 70% 80%, hsla(280, 100%, 35%, 0.4) 0px, transparent 40%),linear-gradient(180deg, hsla(260, 40%, 15%, 1) 0%, hsla(280, 50%, 10%, 1) 100%)`,
  cosmic: `radial-gradient(at 0% 0%, hsla(200, 80%, 30%, 0.5) 0px, transparent 50%),radial-gradient(at 100% 100%, hsla(300, 80%, 30%, 0.5) 0px, transparent 50%),linear-gradient(135deg, hsla(240, 50%, 12%, 1) 0%, hsla(280, 60%, 10%, 1) 100%)`,
};


const InfoTooltip = ({ children, content }) => {
  const [show, setShow] = useState(false);
  return (
    <span
      style={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        cursor: "help",
      }}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      {children}
      {show && (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "100%",
            transform: "translateY(-50%)",
            marginLeft: "10px",
            padding: "10px 14px",
            backgroundColor: "rgba(30,20,50,0.97)",
            border: "1px solid rgba(139,92,246,0.4)",
            borderRadius: "8px",
            fontSize: "10px",
            color: "#e0e7ff",
            width: "220px",
            zIndex: 100,
            boxShadow: "0 4px 20px rgba(0,0,0,0.4)",
            lineHeight: "1.5",
          }}
        >
          {content}
          <div
            style={{
              position: "absolute",
              top: "50%",
              right: "100%",
              transform: "translateY(-50%)",
              width: 0,
              height: 0,
              borderTop: "6px solid transparent",
              borderBottom: "6px solid transparent",
              borderRight: "6px solid rgba(30,20,50,0.97)",
            }}
          ></div>
        </div>
      )}
    </span>
  );
};

const MenuButton = ({ icon, label, active, onClick, disabled = false }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    title={label}
    style={{
      width: "42px",
      height: "42px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: active
        ? "rgba(139,92,246,0.3)"
        : "rgba(255,255,255,0.1)",
      border: active
        ? "2px solid rgba(139,92,246,0.6)"
        : "1px solid rgba(255,255,255,0.2)",
      borderRadius: "8px",
      color: active
        ? "#c4b5fd"
        : disabled
        ? "rgba(255,255,255,0.3)"
        : "rgba(255,255,255,0.7)",
      cursor: disabled ? "not-allowed" : "pointer",
      opacity: disabled ? 0.5 : 1,
    }}
  >
    {icon}
  </button>
);

/* ── Download-as-Image Button ─────────────────────────────────────────── */

function DownloadButton({ targetRef, formulaCode, sessionName, cardName }: {
  targetRef: React.RefObject<HTMLDivElement | null>;
  formulaCode: string;
  sessionName: string;
  cardName: string;
}) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!targetRef.current || busy) return;
    setBusy(true);
    try {
      const canvas = await html2canvas(targetRef.current, {
        backgroundColor: "#ffffff",
        scale: 3,
        useCORS: true,
        logging: false,
      });
      const url = canvas.toDataURL("image/png");
      const a = document.createElement("a");
      const fc = (formulaCode || "F000").replace(/[^a-zA-Z0-9]/g, "_");
      const sn = (sessionName || "v1").replace(/[^a-zA-Z0-9]/g, "_");
      const cn = cardName.replace(/[^a-zA-Z0-9_]/g, "_");
      a.download = `${fc}_${sn}_${cn}.png`;
      a.href = url;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      onClick={handleClick}
      title={t('common.downloadAsImage')}
      style={{
        position: "absolute", top: 8, right: 8, width: 28, height: 28,
        display: "flex", alignItems: "center", justifyContent: "center",
        background: busy ? "#d0d0d0" : "#f0f0f0", border: "none", borderRadius: 6,
        cursor: busy ? "wait" : "pointer", zIndex: 10, padding: 0,
      }}
      onMouseEnter={(e) => { if (!busy) e.currentTarget.style.background = "#e0e0e0"; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = busy ? "#d0d0d0" : "#f0f0f0"; }}
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <polyline points="7 10 12 15 17 10" />
        <line x1="12" y1="15" x2="12" y2="3" />
      </svg>
    </button>
  );
}

export default function ProteinPal({
  initialTab = "formulation",
}: {
  initialTab?: "formulation" | "packaging";
} = {}) {
  const { t, i18n } = useTranslation();
  /** Display ingredient name with Korean in parentheses when Korean mode is active */
  const ingName = (name: string) => {
    if (i18n.language === 'ko') {
      const koName = dbIngredients?.[name]?.korean_name;
      if (koName) return `${name} (${koName})`;
    }
    return name;
  };
  const claimDisplay: Record<string, string> = {
    "Excellent Source": t('claims.excellentSource'),
    "Good Source": t('claims.goodSource'),
    "No Claim": t('claims.noClaim'),
  };
  const [bgIndex, setBgIndex] = useState(0);
  const bgKeys = Object.keys(BACKGROUNDS);

  // Source mode: 'supabase' when configured, 'demo' as offline fallback
  const [sourceMode, setSourceMode] = useState(() =>
    isSupabaseConfigured() ? "supabase" : "demo",
  );

  // Supabase-backed data state
  const [dbLoading, setDbLoading] = useState(false);
  const [dbError, setDbError] = useState("");
  const [dbIngredients, setDbIngredients] = useState<Record<string, any> | null>(null);
  const [dbIdToName, setDbIdToName] = useState<Record<string, string>>({});
  const [dbFormulas, setDbFormulas] = useState<any[]>([]);
  const [selectedFormula, setSelectedFormula] = useState("");
  const [formulaReloadCounter, setFormulaReloadCounter] = useState(0);

  // Tab state (initialTab from unified nav prop)
  const [activeTab, setActiveTab] = useState(initialTab);

  // When the unified nav routes between #app and #packaging, the same App
  // component stays mounted (same key in Root.tsx). Sync activeTab to the
  // new initialTab so the visible tab follows the URL hash — without
  // wiping the rest of the in-progress App state.
  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  // Packaging tab state
  const [servingsPerContainer, setServingsPerContainer] = useState(6);
  const [packagingFormulationView, setPackagingFormulationView] =
    useState("blended"); // 'components' or 'blended'
  const [cogsExpanded, setCogsExpanded] = useState(false);
  const [packagingCardCollapsed, setPackagingCardCollapsed] = useState({
    servings: false,
    nutrition: false,
    formulation: false,
    claims: false,
    pdcaas: false,
    cogs: false,
    protein: false,
  });

  // Form state
  const [doughRatio, setDoughRatio] = useState(50);
  const [pieceWeight, setPieceWeight] = useState(9.2);
  const [servingSize, setServingSize] = useState(84);
  const [viewMode, setViewMode] = useState("component");
  const [proteinUnit, setProteinUnit] = useState("serving");
  const [nfTableView, setNfTableView] = useState("serving");
  const [showIngredientAccordion, setShowIngredientAccordion] = useState(false);
  const [selectedIngredient, setSelectedIngredient] = useState("");
  const [hoveredPieSection, setHoveredPieSection] = useState(null);
  const [showCalcAudit, setShowCalcAudit] = useState(false);
  const [showFormulationHelper, setShowFormulationHelper] = useState(false);
  const [showIngredientContributions, setShowIngredientContributions] =
    useState(false);

  // Ingredient detail panel
  const [ingredientDetailName, setIngredientDetailName] = useState<string | null>(null);
  const [editingDataSources, setEditingDataSources] = useState<Record<string, string>>({});
  const [dataSourceSaving, setDataSourceSaving] = useState(false);
  const [dataSourceSaveMsg, setDataSourceSaveMsg] = useState("");
  const [showAllSources, setShowAllSources] = useState(false);

  // Session naming
  const [sessionName, setSessionName] = useState("");
  const [stickyPosition, setStickyPosition] = useState({
    x: window.innerWidth / 2 - 100,
    y: window.innerHeight - 60,
  });
  const [stickyOrientation, setStickyOrientation] = useState("horizontal"); // 'horizontal' or 'vertical'
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  // Card refs for download-as-image
  const cardRefNutrition = useRef<HTMLDivElement>(null);
  const cardRefComposition = useRef<HTMLDivElement>(null);
  const cardRefFormulationPct = useRef<HTMLDivElement>(null);
  const cardRefAminoAcid = useRef<HTMLDivElement>(null);
  const cardRefProteinClaim = useRef<HTMLDivElement>(null);
  const cardRefPostProcess = useRef<HTMLDivElement>(null);
  const cardRefFormHelper = useRef<HTMLDivElement>(null);
  const cardRefIngContrib = useRef<HTMLDivElement>(null);
  const cardRefNFLabel = useRef<HTMLDivElement>(null);
  const cardRefCalcValidation = useRef<HTMLDivElement>(null);
  // Packaging card refs
  const cardRefPkgServings = useRef<HTMLDivElement>(null);
  const cardRefPkgNutrition = useRef<HTMLDivElement>(null);
  const cardRefPkgFormulation = useRef<HTMLDivElement>(null);
  const cardRefPkgClaims = useRef<HTMLDivElement>(null);
  const cardRefPkgPDCAAS = useRef<HTMLDivElement>(null);
  const cardRefPkgCOGS = useRef<HTMLDivElement>(null);
  const cardRefPkgProtein = useRef<HTMLDivElement>(null);

  const fillingRatio = 100 - doughRatio;
  const piecesPerServing = fdaRound.piecesPerServing(servingSize / pieceWeight);
  const servingsRounded = fdaRound.servingsPerContainer(servingsPerContainer);

  // Display session name or "no name" if blank
  const displaySessionName = sessionName.trim() || "no name";
  const sessionPrefix =
    sourceMode === "supabase"
      ? `SB:${selectedFormula || "F000"}`
      : "DEMO";

  // Drag handlers for sticky box
  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragOffset({
      x: e.clientX - stickyPosition.x,
      y: e.clientY - stickyPosition.y,
    });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    setStickyPosition({
      x: e.clientX - dragOffset.x,
      y: e.clientY - dragOffset.y,
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, dragOffset]);

  const [doughFormula, setDoughFormula] = useState<Record<string, number>>(DEMO_DOUGH_FORMULA);
  const [fillingFormula, setFillingFormula] = useState<Record<string, number>>(DEMO_FILLING_FORMULA);
  const [showAddDough, setShowAddDough] = useState(false);
  const [showAddFilling, setShowAddFilling] = useState(false);
  const [doughPickerPos, setDoughPickerPos] = useState<{ top: number; left: number } | null>(null);
  const [fillingPickerPos, setFillingPickerPos] = useState<{ top: number; left: number } | null>(null);

  // Post-Process Adjustment State
  const [showPostProcess, setShowPostProcess] = useState(false);
  const [postProcessEnabled, setPostProcessEnabled] = useState(false);
  const [targetDoughMoisture, setTargetDoughMoisture] = useState(23);
  const [targetFillingMoisture, setTargetFillingMoisture] = useState(40);
  const [targetWholeMoisture, setTargetWholeMoisture] = useState(30);
  const [moistureMode, setMoistureMode] = useState("components"); // 'components' or 'whole'
  const [ratioMode, setRatioMode] = useState("as-mixed"); // 'as-mixed' or 'finished'
  const [consumerPrepMethod, setConsumerPrepMethod] = useState("none");
  const [crustFraction, setCrustFraction] = useState(15);
  const [macroView, setMacroView] = useState("mixed"); // 'mixed' or 'postbake'
  const [formulaSettingsSaving, setFormulaSettingsSaving] = useState(false);
  const [formulaSettingsMsg, setFormulaSettingsMsg] = useState("");

  // Maillard degradation factors based on research literature
  const MAILLARD_FACTORS = {
    none: {
      name: "No Adjustment",
      filling: { lysine: 1.0, other: 1.0 },
      doughCore: { lysine: 1.0, other: 1.0 },
      doughCrust: { lysine: 1.0, other: 1.0 },
    },
    steam: {
      name: "Steam Only",
      temp: "100°C",
      filling: { lysine: 0.98, other: 0.99 },
      doughCore: { lysine: 0.97, other: 0.99 },
      doughCrust: { lysine: 0.97, other: 0.99 },
    },
    light_brown: {
      name: "Light Browning",
      temp: "150°C",
      filling: { lysine: 0.98, other: 0.99 },
      doughCore: { lysine: 0.97, other: 0.99 },
      doughCrust: { lysine: 0.85, other: 0.95 },
    },
    dark_brown: {
      name: "Dark Browning",
      temp: "180°C+",
      filling: { lysine: 0.98, other: 0.99 },
      doughCore: { lysine: 0.97, other: 0.99 },
      doughCrust: { lysine: 0.75, other: 0.9 },
    },
  };

  // Display names for the prep methods live in the locale files (same keys as
  // the post-process method buttons), so Korean mode does not fall back to
  // the hardcoded English MAILLARD_FACTORS names.
  const MAILLARD_METHOD_I18N: Record<string, string> = {
    none: "postProcess.noAdjust",
    steam: "postProcess.steam",
    light_brown: "postProcess.lightBrown",
    dark_brown: "postProcess.darkBrown",
  };

  const doughTotal = Object.values(doughFormula).reduce((a, b) => a + b, 0);
  const fillingTotal = Object.values(fillingFormula).reduce((a, b) => a + b, 0);

  // ALL_INGREDIENTS prefers Supabase-loaded data and falls back to the embedded demo set
  const ALL_INGREDIENTS =
    sourceMode === "supabase" && dbIngredients ? dbIngredients : DB_INGREDIENTS;

  // Water rows are keyed by the exact "Water" common_name. When a component has
  // no exact "Water" row, fall back conservatively (name contains "water", or
  // the ingredient is >=99% moisture) so moisture math does not silently drop
  // a renamed/localized water row. An exact "Water" row always wins.
  const isWaterRow = (name: string, formula: Record<string, number>) =>
    name === "Water" ||
    (!("Water" in formula) &&
      (name.toLowerCase().includes("water") ||
        (ALL_INGREDIENTS[name]?.moisture ?? 0) >= 99));

  // ── Golden Ratio, live ──
  // Targets are editable on the card and shared with the full screen through
  // localStorage (engine/goldenRatioTargets.ts), so the two views always show
  // the same goal. Strings, so an input can be emptied mid-edit.
  const [grTargets, setGrTargets] = useState(loadGrTargets);
  useEffect(() => {
    saveGrTargets(grTargets);
  }, [grTargets]);
  const grTargetNum = useMemo(() => grTargetNumbers(grTargets), [grTargets]);
  const grTargetSum = grTargetNum.hydro + grTargetNum.isolate + grTargetNum.pulse;

  // Recomputes whenever the formula or the dough/filling split changes, so the
  // card tracks the sliders. Percentages are normalised within their component
  // and then weighted by the dough/filling ratio, which puts both components on
  // one comparable basis. The ratio itself is scale-free.
  const goldenRatio = useMemo(() => {
    const items: BlendItem[] = [];
    const push = (
      formula: Record<string, number>,
      componentTotal: number,
      componentShare: number
    ) => {
      if (componentTotal <= 0 || componentShare <= 0) return;
      Object.keys(formula).forEach((name) => {
        const pct = formula[name];
        if (!pct) return;
        const ing = (ALL_INGREDIENTS as Record<string, any>)[name];
        items.push({
          id: name,
          name,
          weight: (pct / componentTotal) * componentShare,
          proteinPer100g: ing && ing.protein_per_100g != null ? ing.protein_per_100g : 0,
          tags: ing ? ing.tags : null,
          proteinClass: ing ? ing.protein_class : null,
        });
      });
    };
    push(doughFormula, doughTotal, doughRatio);
    push(fillingFormula, fillingTotal, 100 - doughRatio);
    return summariseBlend(items);
  }, [doughFormula, fillingFormula, doughTotal, fillingTotal, doughRatio, ALL_INGREDIENTS]);

  // Deduplicated ingredient list for the IngredientPicker dropdown — matches
  // the {id, common_name, supplier, fda_name} shape FormulaSheet uses.
  // dbIngredients indexes the same record under both common_name and raw id,
  // so we dedupe by id before passing to the picker.
  const pickerIngredients = useMemo(() => {
    const seen = new Set<string>();
    const list: Array<{ id: string; common_name: string; supplier: string | null; fda_name: string | null }> = [];
    Object.entries(ALL_INGREDIENTS).forEach(([key, data]: [string, any]) => {
      const id: string = data?.id || key;
      if (seen.has(id)) return;
      seen.add(id);
      list.push({
        id,
        common_name: data?.common_name || key,
        supplier: data?.supplier || null,
        fda_name: data?.fdaName || data?.fda_name || null,
      });
    });
    return list;
  }, [ALL_INGREDIENTS]);

  // Map an ingredient id back to the common_name key used by doughFormula /
  // fillingFormula state (so onSelect can call addIngredient with the right key).
  const idToCommonName = useMemo(() => {
    const m: Record<string, string> = {};
    pickerIngredients.forEach((i) => { m[i.id] = i.common_name; });
    return m;
  }, [pickerIngredients]);

  // Load data from Supabase when supabase mode is enabled
  useEffect(() => {
    if (sourceMode !== "supabase") return;

    let cancelled = false;
    const loadSupa = async () => {
      setDbLoading(true);
      setDbError("");
      try {
        const data: SupabaseData = await loadSupabaseData();
        if (cancelled) return;
        setDbIngredients(data.ingredients);
        setDbIdToName(data.idToName);
        setDbFormulas(data.formulas);
        if (data.formulas.length > 0) {
          // Default to the same formula the (numerically sorted) selector
          // would show first — the query itself is unordered.
          const sorted = [...data.formulas].sort(compareFormulaCodes);
          setSelectedFormula(sorted[0].code);
        }
      } catch (e: any) {
        if (cancelled) return;
        // Fail safely back to demo mode
        console.warn("Supabase load failed, falling back to demo:", e);
        setDbError(e?.message || String(e));
        setSourceMode("demo");
      } finally {
        if (!cancelled) setDbLoading(false);
      }
    };

    loadSupa();
    return () => { cancelled = true; };
  }, [sourceMode]);

  // Reset to demo-formula state when Supabase is unavailable
  useEffect(() => {
    if (sourceMode !== "demo") return;
    setDoughFormula(DEMO_DOUGH_FORMULA);
    setFillingFormula(DEMO_FILLING_FORMULA);
    setDoughRatio(50);
    setServingSize(84);
    setPieceWeight(9.2);
  }, [sourceMode]);

  // Load selected formula's components from Supabase
  useEffect(() => {
    if (sourceMode !== "supabase") return;
    if (!selectedFormula || !dbIdToName || Object.keys(dbIdToName).length === 0)
      return;

    const f = dbFormulas.find((x: any) => x.code === selectedFormula);
    if (!f) return;

    let cancelled = false;
    const loadComponents = async () => {
      setDbLoading(true);
      setDbError("");
      try {
        const { dough, filling } = await loadFormulaComponents(
          selectedFormula,
          dbIdToName,
        );
        if (cancelled) return;
        setDoughFormula(dough);
        setFillingFormula(filling);
        if (f.doughRatio != null) setDoughRatio(Math.round(f.doughRatio));
        setPieceWeight(f.pieceWeight || 9.2);
        setServingSize(Math.round(f.servingSize || 84));

        // Auto-set ratio mode from formula
        setRatioMode(f.ratioMode === "finished" ? "finished" : "as-mixed");

        // Auto-enable post-process mode if formula has moisture targets
        if (f.finalMoistureMode) {
          setPostProcessEnabled(true);
          setShowPostProcess(true);
          if (f.finalMoistureMode === "composite") {
            setMoistureMode("whole");
            if (f.finalMoistureWhole != null) setTargetWholeMoisture(f.finalMoistureWhole);
          } else {
            setMoistureMode("components");
            // null = "no change" → use sentinel -1 so postProcessed falls back to as-mixed moisture
            setTargetDoughMoisture(f.finalMoistureDough ?? -1);
            setTargetFillingMoisture(f.finalMoistureFilling ?? -1);
          }
        } else {
          // Formula has no moisture targets — reset post-process state
          setPostProcessEnabled(false);
          setMoistureMode("components");
        }
      } catch (e: any) {
        if (!cancelled) setDbError(e?.message || String(e));
      } finally {
        if (!cancelled) setDbLoading(false);
      }
    };
    loadComponents();
    return () => { cancelled = true; };
  }, [sourceMode, selectedFormula, dbFormulas, dbIdToName, formulaReloadCounter]);

  // Save As: fork the currently loaded formula into a NEW F-code with the
  // post-process tweaks the scientist has been "playing" with. Never overwrites
  // the source formula — formula numbering is append-only per project rules.
  const saveFormulaSettings = async () => {
    if (!selectedFormula || sourceMode !== "supabase") return;
    setFormulaSettingsSaving(true);
    setFormulaSettingsMsg("");
    try {
      const sourceFormula = dbFormulas.find((x: any) => x.code === selectedFormula);
      // The "Name your session" field is the new recipe's name when provided;
      // otherwise keep the source formula's name.
      const modName = sessionName.trim();
      const displayName = modName || sourceFormula?.displayName || "Untitled";

      // Build components array for save_formula_as RPC from current state.
      // Grams are arbitrary scale (use percentages directly) — pct_of_component
      // and pct_of_formula are the source of truth.
      const sectionGrams: Record<string, number> = { DOUGH: 0, FILLING: 0 };
      Object.values(doughFormula).forEach((p) => { sectionGrams.DOUGH += p; });
      Object.values(fillingFormula).forEach((p) => { sectionGrams.FILLING += p; });

      const dr = doughRatio / 100;
      const fr = 1 - dr;

      const components: Array<Record<string, unknown>> = [];
      let order = 1;
      Object.entries(doughFormula).forEach(([name, pct]) => {
        const ing = (ALL_INGREDIENTS as any)[name];
        const sg = sectionGrams.DOUGH || 1;
        const pctComp = pct / sg;
        components.push({
          component: "DOUGH",
          ingredient_id: ing?.id ?? name,
          display_order: order++,
          row_type: "ingredient",
          pct_of_component: pctComp,
          grams: pct,
          pct_of_formula: pctComp * dr,
          supplier: ing?.supplier ?? null,
          lot_code: null,
          moisture_pct: ing?.moisture ?? null,
          cost_per_kg: ing?.cost_per_kg ?? null,
          cost_contribution: null,
          notes: null,
          process_step: null,
          process_instruction: null,
        });
      });
      Object.entries(fillingFormula).forEach(([name, pct]) => {
        const ing = (ALL_INGREDIENTS as any)[name];
        const sg = sectionGrams.FILLING || 1;
        const pctComp = pct / sg;
        components.push({
          component: "FILLING",
          ingredient_id: ing?.id ?? name,
          display_order: order++,
          row_type: "ingredient",
          pct_of_component: pctComp,
          grams: pct,
          pct_of_formula: pctComp * fr,
          supplier: ing?.supplier ?? null,
          lot_code: null,
          moisture_pct: ing?.moisture ?? null,
          cost_per_kg: ing?.cost_per_kg ?? null,
          cost_contribution: null,
          notes: null,
          process_step: null,
          process_instruction: null,
        });
      });

      // Pre-fill notes: "Forked from F___" plus the Mod session name if present.
      const presetNotes = `Forked from ${selectedFormula}${modName ? `\nMod: ${modName}` : ""}`;

      const { data: newCode, error: rpcErr } = await supabase.rpc("save_formula_as", {
        p_display_name: displayName,
        p_piece_weight_g: pieceWeight,
        p_serving_size_g: servingSize,
        p_dough_ratio_pct: doughRatio,
        p_formula_type: "F",
        p_owner_name: null,
        p_notes: presetNotes,
        p_source_formula_code: selectedFormula,
        p_components: components,
      });
      if (rpcErr) throw new Error(rpcErr.message);
      if (!newCode) throw new Error("Save As returned no formula code");

      // Persist ratio_mode and moisture targets onto the new formula row.
      const settingsPatch: Record<string, unknown> = {
        ratio_mode: ratioMode === "finished" ? "finished" : "as_mixed",
      };
      if (postProcessEnabled) {
        if (moistureMode === "whole") {
          settingsPatch.final_moisture_mode = "composite";
          settingsPatch.final_moisture_whole = targetWholeMoisture;
          settingsPatch.final_moisture_dough = null;
          settingsPatch.final_moisture_filling = null;
        } else {
          settingsPatch.final_moisture_mode = "components";
          settingsPatch.final_moisture_dough = targetDoughMoisture < 0 ? null : targetDoughMoisture;
          settingsPatch.final_moisture_filling = targetFillingMoisture < 0 ? null : targetFillingMoisture;
          settingsPatch.final_moisture_whole = null;
        }
      } else {
        settingsPatch.final_moisture_mode = null;
        settingsPatch.final_moisture_whole = null;
        settingsPatch.final_moisture_dough = null;
        settingsPatch.final_moisture_filling = null;
      }
      await updateRowByColumn("formulas", "formula_code", String(newCode), settingsPatch);

      logActivity("formula_save_as", {
        from: selectedFormula,
        to: String(newCode),
        display_name: displayName,
      });

      // Make the fork show up in the selector immediately (no page reload):
      // append it to dbFormulas and switch the selection to it.
      setDbFormulas((prev: any[]) => [
        ...prev,
        {
          code: String(newCode),
          displayName,
          pieceWeight,
          servingSize,
          doughRatio,
          ratioMode,
          finalMoistureMode: postProcessEnabled
            ? moistureMode === "whole"
              ? "composite"
              : "components"
            : null,
          finalMoistureWhole:
            postProcessEnabled && moistureMode === "whole"
              ? targetWholeMoisture
              : null,
          finalMoistureDough:
            postProcessEnabled && moistureMode !== "whole" && targetDoughMoisture >= 0
              ? targetDoughMoisture
              : null,
          finalMoistureFilling:
            postProcessEnabled && moistureMode !== "whole" && targetFillingMoisture >= 0
              ? targetFillingMoisture
              : null,
        },
      ]);
      setSelectedFormula(String(newCode));

      setFormulaSettingsMsg(`Saved as ${newCode}`);
      window.alert(`Save successful. Saved as ${newCode}.`);
    } catch (e: any) {
      setFormulaSettingsMsg("Error: " + (e?.message || String(e)));
    } finally {
      setFormulaSettingsSaving(false);
    }
  };

  const snapTo100 = (section) => {
    const formula = section === "dough" ? doughFormula : fillingFormula;
    const total = section === "dough" ? doughTotal : fillingTotal;
    if (total === 0) return;
    const scale = 100 / total;
    const newFormula: Record<string, number> = {};
    Object.entries(formula).forEach(([name, pct]) => {
      newFormula[name] = Math.round(pct * scale * 10) / 10;
    });
    if (section === "dough") setDoughFormula(newFormula);
    else setFillingFormula(newFormula);
  };

  // Compute effective as-mixed ratios (may differ from user-entered ratio when ratioMode = 'finished')
  const effectiveRatios = useMemo(() => {
    // Compute component moistures from formulas
    let dM = 0, fM = 0;
    Object.entries(doughFormula).forEach(([n, p]) => {
      if (ALL_INGREDIENTS[n]) dM += (p / 100) * ALL_INGREDIENTS[n].moisture;
    });
    Object.entries(fillingFormula).forEach(([n, p]) => {
      if (ALL_INGREDIENTS[n]) fM += (p / 100) * ALL_INGREDIENTS[n].moisture;
    });

    // If post-processing disabled or ratio mode is as-mixed, use entered ratio directly
    if (!postProcessEnabled || ratioMode === 'as-mixed') {
      return { asMixedDough: doughRatio, asMixedFilling: 100 - doughRatio };
    }

    // ratioMode === 'finished' && postProcessEnabled: back-calculate as-mixed ratio
    // Derive effective target moistures (handle whole vs component mode)
    // Sentinel -1 means "no change" → use as-mixed moisture
    let tDM = targetDoughMoisture < 0 ? dM : targetDoughMoisture;
    let tFM = targetFillingMoisture < 0 ? fM : targetFillingMoisture;
    if (moistureMode === 'whole') {
      const compMoist = (dM * doughRatio + fM * (100 - doughRatio)) / 100;
      if (compMoist > 0) {
        const scale = targetWholeMoisture / compMoist;
        tDM = Math.max(0, Math.min(80, dM * scale));
        tFM = Math.max(0, Math.min(80, fM * scale));
      }
    }

    // The back-calculation itself now lives in src/engine/composition.ts, so the
    // report and compare screens can blend on the same ratio this screen does
    // instead of each keeping a private copy. Behaviour is unchanged: the
    // extracted function is this code, and src/engine/__tests__ pins it to the
    // audit this screen prints for F234.
    return computeAsMixedRatio({
      doughRatioPct: doughRatio,
      ratioMode: 'finished',
      postProcessEnabled: true,
      doughMoisturePct: dM,
      fillingMoisturePct: fM,
      moistureMode: 'components',   // whole-mode was already resolved into tDM/tFM above
      targetDoughMoisture: tDM,
      targetFillingMoisture: tFM,
    });
  }, [postProcessEnabled, ratioMode, doughRatio, doughFormula, fillingFormula, ALL_INGREDIENTS,
      moistureMode, targetWholeMoisture, targetDoughMoisture, targetFillingMoisture]);

  const calculated = useMemo(() => {
    // Use effective as-mixed ratios for formula blending
    const amDoughRatio = effectiveRatios.asMixedDough;
    const amFillingRatio = effectiveRatios.asMixedFilling;

    const totalFormula: Record<string, number> = {};
    Object.entries(doughFormula).forEach(([name, pct]) => {
      const totalPct = (pct / 100) * amDoughRatio;
      totalFormula[name] = (totalFormula[name] || 0) + totalPct;
    });
    Object.entries(fillingFormula).forEach(([name, pct]) => {
      const totalPct = (pct / 100) * amFillingRatio;
      totalFormula[name] = (totalFormula[name] || 0) + totalPct;
    });

    let totalProtein = 0,
      totalFat = 0,
      totalFiber = 0,
      totalCarbs = 0,
      totalMoisture = 0,
      totalSugar = 0,
      totalSodium = 0,
      totalSatFat = 0,
      totalTransFat = 0,
      totalCholesterol = 0,
      totalCost = 0;
    let totalVitaminD = 0,
      totalCalcium = 0,
      totalIron = 0,
      totalPotassium = 0;
    let weightedDigestibilitySum = 0;
    const proteinContributions: Record<string, any> = {};
    const ingredientData: any[] = [];
    let idx = 1;

    Object.entries(totalFormula).forEach(([name, pct]) => {
      if (pct > 0 && ALL_INGREDIENTS[name]) {
        const ing = ALL_INGREDIENTS[name];
        const proteinContrib = (pct / 100) * ing.protein_per_100g;
        totalProtein += proteinContrib;
        totalFat += (pct / 100) * ing.fat;
        totalSatFat += (pct / 100) * (ing.satfat || 0);
        totalTransFat += (pct / 100) * (ing.transfat || 0);
        totalCholesterol += (pct / 100) * (ing.cholesterol || 0);
        totalFiber += (pct / 100) * ing.fiber;
        totalCarbs += (pct / 100) * ing.carbs;
        totalMoisture += (pct / 100) * ing.moisture;
        totalSugar += (pct / 100) * ing.sugar;
        totalSodium += (pct / 100) * ing.sodium;
        totalVitaminD += (pct / 100) * (ing.vitaminD || 0);
        totalCalcium += (pct / 100) * (ing.calcium || 0);
        totalIron += (pct / 100) * (ing.iron || 0);
        totalPotassium += (pct / 100) * (ing.potassium || 0);

        const costPerKg = ing.cost_per_kg || 0;
        const costContrib = ((pct / 100) * costPerKg) / 10; // cost per 100g
        totalCost += costContrib;

        ingredientData.push({
          id: idx++,
          name,
          pct,
          proteinContrib,
          fatContrib: (pct / 100) * ing.fat,
          fiberContrib: (pct / 100) * ing.fiber,
          carbsContrib: (pct / 100) * ing.carbs,
          sodiumContrib: (pct / 100) * ing.sodium,
          vitaminDContrib: (pct / 100) * (ing.vitaminD || 0),
          calciumContrib: (pct / 100) * (ing.calcium || 0),
          ironContrib: (pct / 100) * (ing.iron || 0),
          potassiumContrib: (pct / 100) * (ing.potassium || 0),
          costPerKg,
          costContrib,
        });

        if (proteinContrib > 0.01) {
          proteinContributions[name] = {
            protein: proteinContrib,
            digestibility: ing.digestibility,
            amino_acids: ing.amino_acids,
            color: ing.color,
          };
          weightedDigestibilitySum += ing.digestibility * proteinContrib;
        }
      }
    });

    const weightedDigestibility =
      totalProtein > 0 ? weightedDigestibilitySum / totalProtein : 0;
    const calories = totalProtein * 4 + totalCarbs * 4 + totalFat * 9;

    const blendedAA: Record<string, number> = {};
    const aaByIngredient: Record<string, Record<string, number>> = {};
    Object.keys(REFERENCE_PATTERN).forEach((aa) => {
      blendedAA[aa] = 0;
      aaByIngredient[aa] = {};
      Object.entries(proteinContributions).forEach(([name, data]) => {
        if (totalProtein > 0) {
          const fraction = data.protein / totalProtein;
          const contrib = data.amino_acids[aa] * fraction;
          blendedAA[aa] += contrib;
          aaByIngredient[aa][name] = contrib;
        }
      });
    });

    const aaScores: Record<string, number> = {};
    let limitingAA: string | null = null,
      limitingScore = Infinity;
    Object.keys(REFERENCE_PATTERN).forEach((aa) => {
      const score = blendedAA[aa] / REFERENCE_PATTERN[aa];
      aaScores[aa] = score;
      if (score < limitingScore) {
        limitingScore = score;
        limitingAA = aa;
      }
    });

    const aminoAcidScore = Math.min(limitingScore, 1);
    const pdcaas = aminoAcidScore * weightedDigestibility;
    const truncatedPdcaas = Math.min(pdcaas, 1);

    const proteinPerServing = (totalProtein * servingSize) / 100;
    const correctedProteinPerServing = proteinPerServing * truncatedPdcaas;
    const fatPerServing = (totalFat * servingSize) / 100;
    const satfatPerServing = (totalSatFat * servingSize) / 100;
    const transfatPerServing = (totalTransFat * servingSize) / 100;
    const cholesterolPerServing = (totalCholesterol * servingSize) / 100;
    const carbsPerServing = (totalCarbs * servingSize) / 100;
    const fiberPerServing = (totalFiber * servingSize) / 100;
    const sugarPerServing = (totalSugar * servingSize) / 100;
    const sodiumPerServing = (totalSodium * servingSize) / 100;
    const vitaminDPerServing = (totalVitaminD * servingSize) / 100;
    const calciumPerServing = (totalCalcium * servingSize) / 100;
    const ironPerServing = (totalIron * servingSize) / 100;
    const potassiumPerServing = (totalPotassium * servingSize) / 100;
    const caloriesPerServing = (calories * servingSize) / 100;
    const costPerServing = (totalCost * servingSize) / 100;
    const costPerGProtein = totalProtein > 0 ? totalCost / totalProtein : 0;
    const percentDV = (correctedProteinPerServing / 50) * 100;

    let proteinClaim = "No Claim",
      claimColor = "#b91c1c";
    if (percentDV >= 20) {
      proteinClaim = "Excellent Source";
      claimColor = "#15803d";
    } else if (percentDV >= 10) {
      proteinClaim = "Good Source";
      claimColor = "#b45309";
    }

    let fiberClaim = "No Claim";
    if (fiberPerServing >= 5) fiberClaim = "Excellent Source";
    else if (fiberPerServing >= 2.5) fiberClaim = "Good Source";

    let doughMoisture = 0,
      fillingMoisture = 0;
    let doughNonWaterMoisture = 0,
      fillingNonWaterMoisture = 0;
    let doughWaterPct = 0,
      fillingWaterPct = 0;

    Object.entries(doughFormula).forEach(([name, pct]) => {
      if (ALL_INGREDIENTS[name]) {
        const moistureContrib = (pct / 100) * ALL_INGREDIENTS[name].moisture;
        doughMoisture += moistureContrib;
        if (isWaterRow(name, doughFormula)) doughWaterPct += pct;
        else doughNonWaterMoisture += moistureContrib;
      }
    });
    Object.entries(fillingFormula).forEach(([name, pct]) => {
      if (ALL_INGREDIENTS[name]) {
        const moistureContrib = (pct / 100) * ALL_INGREDIENTS[name].moisture;
        fillingMoisture += moistureContrib;
        if (isWaterRow(name, fillingFormula)) fillingWaterPct += pct;
        else fillingNonWaterMoisture += moistureContrib;
      }
    });

    const doughComposition: { name: string; value: number; color: string }[] = [];
    const doughCat = { flour: 0, water: 0, oil: 0, protein: 0, other: 0 };
    Object.entries(doughFormula).forEach(([name, pct]) => {
      if (name.includes("Flour")) doughCat.flour += pct;
      else if (isWaterRow(name, doughFormula)) doughCat.water += pct;
      else if (name.includes("Oil")) doughCat.oil += pct;
      else if (ALL_INGREDIENTS[name]?.protein_per_100g > 15)
        doughCat.protein += pct;
      else doughCat.other += pct;
    });
    if (doughCat.flour > 0)
      doughComposition.push({
        name: "Flour",
        value: doughCat.flour,
        color: "#f59e0b",
      });
    if (doughCat.water > 0)
      doughComposition.push({
        name: "Water",
        value: doughCat.water,
        color: "#60a5fa",
      });
    if (doughCat.oil > 0)
      doughComposition.push({
        name: "Oil",
        value: doughCat.oil,
        color: "#fcd34d",
      });
    if (doughCat.protein > 0)
      doughComposition.push({
        name: "Protein",
        value: doughCat.protein,
        color: "#22c55e",
      });
    if (doughCat.other > 0)
      doughComposition.push({
        name: "Other",
        value: doughCat.other,
        color: "#9ca3af",
      });

    const fillingComposition: { name: string; value: number; color: string }[] = [];
    const fillCat = {
      water: 0,
      chickpea: 0,
      oil: 0,
      fiber: 0,
      protein: 0,
      other: 0,
    };
    Object.entries(fillingFormula).forEach(([name, pct]) => {
      if (isWaterRow(name, fillingFormula)) fillCat.water += pct;
      else if (name.includes("Chickpea")) fillCat.chickpea += pct;
      else if (name.includes("Oil")) fillCat.oil += pct;
      else if (ALL_INGREDIENTS[name]?.fiber > 20) fillCat.fiber += pct;
      else if (ALL_INGREDIENTS[name]?.protein_per_100g > 50)
        fillCat.protein += pct;
      else fillCat.other += pct;
    });
    if (fillCat.chickpea > 0)
      fillingComposition.push({
        name: "Chickpea",
        value: fillCat.chickpea,
        color: "#a855f7",
      });
    if (fillCat.water > 0)
      fillingComposition.push({
        name: "Water",
        value: fillCat.water,
        color: "#60a5fa",
      });
    if (fillCat.protein > 0)
      fillingComposition.push({
        name: "Protein",
        value: fillCat.protein,
        color: "#22c55e",
      });
    if (fillCat.fiber > 0)
      fillingComposition.push({
        name: "Fiber",
        value: fillCat.fiber,
        color: "#d97706",
      });
    if (fillCat.oil > 0)
      fillingComposition.push({
        name: "Oil",
        value: fillCat.oil,
        color: "#fcd34d",
      });
    if (fillCat.other > 0)
      fillingComposition.push({
        name: "Other",
        value: fillCat.other,
        color: "#9ca3af",
      });

    const allAbove100 = Object.values(aaScores).every((s) => s >= 1);

    const chartData = Object.keys(REFERENCE_PATTERN).map((aa) => {
      const dataPoint = {
        name: AA_NAMES[aa],
        fullName: AA_FULL_NAMES[aa],
        reference: REFERENCE_PATTERN[aa],
        total: blendedAA[aa],
        score: aaScores[aa],
        isLimiting: aa === limitingAA,
      };
      Object.entries(proteinContributions).forEach(([name, data]) => {
        dataPoint[name] = aaByIngredient[aa][name] || 0;
      });
      return dataPoint;
    });

    const nutrientDensity =
      calories > 0 ? ((totalProtein + totalFiber) / calories) * 100 : 0;

    const sortedIngredients = Object.entries(totalFormula)
      .filter(([, pct]) => pct > 0)
      .sort((a, b) => b[1] - a[1])
      .map(([name]) => ALL_INGREDIENTS[name]?.fdaName || name);

    return {
      totalFormula,
      totalProtein,
      totalFat,
      totalSatFat,
      totalTransFat,
      totalCholesterol,
      totalFiber,
      totalCarbs,
      totalSugar,
      totalSodium,
      totalMoisture,
      calories,
      totalVitaminD,
      totalCalcium,
      totalIron,
      totalPotassium,
      proteinPerServing,
      correctedProteinPerServing,
      fatPerServing,
      satfatPerServing,
      transfatPerServing,
      cholesterolPerServing,
      carbsPerServing,
      fiberPerServing,
      sugarPerServing,
      sodiumPerServing,
      caloriesPerServing,
      vitaminDPerServing,
      calciumPerServing,
      ironPerServing,
      potassiumPerServing,
      percentDV,
      proteinClaim,
      claimColor,
      fiberClaim,
      weightedDigestibility,
      pdcaas: truncatedPdcaas,
      aminoAcidScore,
      limitingScore,
      blendedAA,
      aaScores,
      limitingAA,
      allAbove100,
      doughMoisture,
      fillingMoisture,
      doughComposition,
      fillingComposition,
      doughNonWaterMoisture,
      fillingNonWaterMoisture,
      doughWaterPct,
      fillingWaterPct,
      chartData,
      proteinContributions,
      nutrientDensity,
      sortedIngredients,
      aaByIngredient,
      ingredientData,
      totalCost,
      costPerServing,
      costPerGProtein,
    };
  }, [
    doughFormula,
    fillingFormula,
    effectiveRatios,
    servingSize,
    ALL_INGREDIENTS,
  ]);

  // Post-process calculations
  const postProcessed = useMemo(() => {
    const defaultResult = {
      enabled: false,
      adjustedPdcaas: calculated.pdcaas,
      adjustedBlendedAA: calculated.blendedAA,
      adjustedAAScores: calculated.aaScores as Record<string, number>,
      adjustedAminoAcidScore: calculated.aminoAcidScore as number,
      adjustedLimitingAA: calculated.limitingAA,
      adjustedLimitingScore: calculated.limitingScore,
      adjustedCorrectedProtein: calculated.correctedProteinPerServing,
      adjustedPercentDV: calculated.percentDV,
      moistureAdjustment: {
        dough: {
          originalMoisture: calculated.doughMoisture,
          finalMoisture: calculated.doughMoisture,
          change: 0,
          isPickup: false,
          originalWaterPct: calculated.doughWaterPct,
          adjustedWaterPct: calculated.doughWaterPct,
          nonWaterMoisture: calculated.doughNonWaterMoisture,
        },
        filling: {
          originalMoisture: calculated.fillingMoisture,
          finalMoisture: calculated.fillingMoisture,
          change: 0,
          isPickup: false,
          originalWaterPct: calculated.fillingWaterPct,
          adjustedWaterPct: calculated.fillingWaterPct,
          nonWaterMoisture: calculated.fillingNonWaterMoisture,
        },
      },
      maillardAdjustment: {
        method: "steam",
        factors: MAILLARD_FACTORS.steam,
        lysineRetention: {
          filling: 1,
          doughCore: 1,
          doughCrust: 1,
          netDough: 1,
        },
        proteinWeights: { dough: 0.5, filling: 0.5 },
      },
      concentrationFactor: { dough: 1, filling: 1, overall: 1 },
      finishedDoughFormula: { ...doughFormula },
      finishedFillingFormula: { ...fillingFormula },
      finishedTotalFormula: { ...calculated.totalFormula },
      weightLossPct: 0,
      asMixedPieceWeight: pieceWeight,
      finishedPieceWeight: pieceWeight,
      finishedServingWeight: servingSize,
      asMixedDoughRatio: doughRatio,
      asMixedFillingRatio: fillingRatio,
      finishedDoughRatio: doughRatio,
      finishedFillingRatio: fillingRatio,
      finishedNutritionals: {
        protein: calculated.proteinPerServing,
        fat: calculated.fatPerServing,
        satfat: calculated.satfatPerServing,
        transfat: calculated.transfatPerServing,
        cholesterol: calculated.cholesterolPerServing,
        carbs: calculated.carbsPerServing,
        fiber: calculated.fiberPerServing,
        sugar: calculated.sugarPerServing,
        sodium: calculated.sodiumPerServing,
        calories: calculated.caloriesPerServing,
        vitaminD: calculated.vitaminDPerServing,
        calcium: calculated.calciumPerServing,
        iron: calculated.ironPerServing,
        potassium: calculated.potassiumPerServing,
      },
      pdcaasChange: 0,
      percentDVChange: 0,
    };

    if (!postProcessEnabled) return defaultResult;

    // Derive effective target moistures based on moisture mode
    let effTargetDoughMoisture = targetDoughMoisture;
    let effTargetFillingMoisture = targetFillingMoisture;
    if (moistureMode === "whole") {
      // Proportionally scale component moistures to achieve the target composite moisture
      const compositeMoisture =
        (calculated.doughMoisture * effectiveRatios.asMixedDough +
         calculated.fillingMoisture * effectiveRatios.asMixedFilling) / 100;
      if (compositeMoisture > 0) {
        const scale = targetWholeMoisture / compositeMoisture;
        effTargetDoughMoisture = Math.max(0, Math.min(80, calculated.doughMoisture * scale));
        effTargetFillingMoisture = Math.max(0, Math.min(80, calculated.fillingMoisture * scale));
      }
    } else {
      // "components" mode: if a target equals the sentinel -1, treat as "no change"
      if (targetDoughMoisture < 0) effTargetDoughMoisture = calculated.doughMoisture;
      if (targetFillingMoisture < 0) effTargetFillingMoisture = calculated.fillingMoisture;
    }

    // Moisture calculations
    const doughMoistureChange = effTargetDoughMoisture - calculated.doughMoisture;
    const fillingMoistureChange =
      effTargetFillingMoisture - calculated.fillingMoisture;

    // Calculate what Water% would achieve the target moisture
    const doughAdjustedWaterPct = Math.max(
      0,
      effTargetDoughMoisture - calculated.doughNonWaterMoisture
    );
    const fillingAdjustedWaterPct = Math.max(
      0,
      effTargetFillingMoisture - calculated.fillingNonWaterMoisture
    );

    // Concentration/dilution factor based on dry matter
    // If moisture decreases from 30% to 10%, dry matter goes from 70% to 90%
    // Concentration factor = 90/70 = 1.286 (nutrients are 28.6% more concentrated per gram)
    // Formula: FINAL dry matter / ORIGINAL dry matter
    // Dry-matter denominators are guarded: a 100%-moisture component would
    // otherwise divide by zero and yield Infinity/NaN.
    const doughDryMatter = 100 - calculated.doughMoisture;
    const doughConcentration =
      Math.abs(doughMoistureChange) > 0.01 && doughDryMatter > 0.01
        ? (100 - effTargetDoughMoisture) / doughDryMatter
        : 1;
    const fillingDryMatter = 100 - calculated.fillingMoisture;
    const fillingConcentration =
      Math.abs(fillingMoistureChange) > 0.01 && fillingDryMatter > 0.01
        ? (100 - effTargetFillingMoisture) / fillingDryMatter
        : 1;

    const amDR = effectiveRatios.asMixedDough;
    const amFR = effectiveRatios.asMixedFilling;

    // Compute finished component weight ratios
    // After moisture loss, dough and filling lose different amounts of water
    // Finished component mass is proportional to asMixedRatio / concentration (mass decreases)
    const finDoughMass = doughConcentration > 0 ? amDR / doughConcentration : amDR;
    const finFillingMass = fillingConcentration > 0 ? amFR / fillingConcentration : amFR;
    const finTotal = finDoughMass + finFillingMass;
    const finishedDoughRatio = finTotal > 0 ? (finDoughMass / finTotal) * 100 : 50;
    const finishedFillingRatio = 100 - finishedDoughRatio;

    // Overall concentration factor.
    //
    // This is a HARMONIC weighted mean, not an arithmetic one, and the difference
    // is not cosmetic. Each component's concentration factor is a mass ratio
    // (as-mixed / finished), so finished mass DIVIDES by it. Averaging the two
    // factors directly assumes mass multiplies, which overstates concentration
    // whenever the components dry by different amounts.
    //
    // finTotal above is already the finished mass per 100 g of as-mixed, so the
    // correct factor is simply 100 / finTotal. The pieces were always here.
    //
    // Previously: (doughConcentration * amDR + fillingConcentration * amFR) / 100
    // On F234 that returned 1.2101 against a true 1.1899 -- every finished
    // nutrient reported 1.70% high, and the as-mixed piece was 169.44 g against
    // a true 166.61 g (matching the isolated per-component runs recorded in
    // F234_Protein_Pal_Audit_Snapshot.json). Protein read 30.00 g against 29.50 g,
    // so a 30 g target appeared to be met exactly when it was in fact missed.
    //
    // The equivalent algebra is done correctly in effectiveRatios above (~line
    // 1997), where the finished-to-as-mixed relation is solved rather than averaged.
    const overallConcentration = finTotal > 0 ? 100 / finTotal : 1;

    // Weight loss percentage: if concentration is 1.25, we lost 20% of weight (1 - 1/1.25 = 0.2)
    const weightLossPct =
      overallConcentration > 1
        ? (1 - 1 / overallConcentration) * 100
        : overallConcentration < 1
        ? (1 / overallConcentration - 1) * -100
        : 0;

    // Calculate finished piece weight based on moisture loss
    // If as-mixed piece would be X grams and we lose Y% weight, finished = X * (1 - Y/100)
    // But user INPUTS the finished piece weight, so we use that directly
    // The as-mixed weight would be: pieceWeight / (1 - weightLossPct/100)
    // Guard the degenerate 100%-weight-loss case (pure-water component dried
    // to nothing) so the UI never renders Infinity/NaN.
    const asMixedPieceWeight =
      weightLossPct !== 0 && Math.abs(1 - weightLossPct / 100) > 0.001
        ? pieceWeight / (1 - weightLossPct / 100)
        : pieceWeight;

    // Create FINISHED formulas (with water removed, recalculated to 100%)
    // For dough: reduce water, then recalculate all percentages
    // Denominators are clamped away from 0: a 100%-moisture component (e.g. a
    // dough of pure water) would otherwise divide by zero and render NaN.
    const finishedDoughFormula: Record<string, number> = {};
    const waterRemoved_dough = calculated.doughWaterPct - doughAdjustedWaterPct;
    const doughTotalAfterWaterRemoval = Math.max(100 - waterRemoved_dough, 0.01);
    Object.entries(doughFormula).forEach(([name, pct]) => {
      if (isWaterRow(name, doughFormula)) {
        finishedDoughFormula[name] =
          (doughAdjustedWaterPct / doughTotalAfterWaterRemoval) * 100;
      } else {
        finishedDoughFormula[name] = (pct / doughTotalAfterWaterRemoval) * 100;
      }
    });

    const finishedFillingFormula: Record<string, number> = {};
    const waterRemoved_filling =
      calculated.fillingWaterPct - fillingAdjustedWaterPct;
    const fillingTotalAfterWaterRemoval = Math.max(100 - waterRemoved_filling, 0.01);
    Object.entries(fillingFormula).forEach(([name, pct]) => {
      if (isWaterRow(name, fillingFormula)) {
        finishedFillingFormula[name] =
          (fillingAdjustedWaterPct / fillingTotalAfterWaterRemoval) * 100;
      } else {
        finishedFillingFormula[name] =
          (pct / fillingTotalAfterWaterRemoval) * 100;
      }
    });

    // Finished total formula (uses finished ratios since components have different mass retention)
    const finishedTotalFormula: Record<string, number> = {};
    Object.entries(finishedDoughFormula).forEach(([name, pct]) => {
      const totalPct = (pct / 100) * finishedDoughRatio;
      finishedTotalFormula[name] = (finishedTotalFormula[name] || 0) + totalPct;
    });
    Object.entries(finishedFillingFormula).forEach(([name, pct]) => {
      const totalPct = (pct / 100) * finishedFillingRatio;
      finishedTotalFormula[name] = (finishedTotalFormula[name] || 0) + totalPct;
    });

    // CONCENTRATED NUTRITIONALS for finished product
    // Per FDA 21 CFR 101.9, nutrition labeling is based on food "as packaged" or "as prepared"
    // For baked goods, this means the finished/baked product
    // Nutrients concentrate when water is removed: nutrient_per_g_finished = nutrient_per_g_asmixed * concentrationFactor
    // But we're calculating per SERVING, and serving size is based on finished product weight
    // If serving = 84g finished, and finished is more concentrated, nutrients per 84g finished > nutrients per 84g as-mixed
    const finishedNutritionals = {
      protein: calculated.proteinPerServing * overallConcentration,
      fat: calculated.fatPerServing * overallConcentration,
      satfat: calculated.satfatPerServing * overallConcentration,
      transfat: calculated.transfatPerServing * overallConcentration,
      cholesterol: calculated.cholesterolPerServing * overallConcentration,
      carbs: calculated.carbsPerServing * overallConcentration,
      fiber: calculated.fiberPerServing * overallConcentration,
      sugar: calculated.sugarPerServing * overallConcentration,
      sodium: calculated.sodiumPerServing * overallConcentration,
      calories: calculated.caloriesPerServing * overallConcentration,
      vitaminD: calculated.vitaminDPerServing * overallConcentration,
      calcium: calculated.calciumPerServing * overallConcentration,
      iron: calculated.ironPerServing * overallConcentration,
      potassium: calculated.potassiumPerServing * overallConcentration,
    };

    // Maillard calculations
    const maillardFactors = MAILLARD_FACTORS[consumerPrepMethod];
    const crustPct = crustFraction / 100;
    const netDoughLysineRetention =
      (1 - crustPct) * maillardFactors.doughCore.lysine +
      crustPct * maillardFactors.doughCrust.lysine;

    // Protein weight distribution
    let doughProteinContrib = 0,
      fillingProteinContrib = 0;
    Object.entries(doughFormula).forEach(([name, pct]) => {
      if (ALL_INGREDIENTS[name])
        doughProteinContrib +=
          (pct / 100) * ALL_INGREDIENTS[name].protein_per_100g;
    });
    Object.entries(fillingFormula).forEach(([name, pct]) => {
      if (ALL_INGREDIENTS[name])
        fillingProteinContrib +=
          (pct / 100) * ALL_INGREDIENTS[name].protein_per_100g;
    });
    const weightedDoughProtein = doughProteinContrib * (amDR / 100);
    const weightedFillingProtein = fillingProteinContrib * (amFR / 100);
    const totalWeightedProtein = weightedDoughProtein + weightedFillingProtein;
    const doughWeight =
      totalWeightedProtein > 0
        ? weightedDoughProtein / totalWeightedProtein
        : 0.5;
    const fillingWeight =
      totalWeightedProtein > 0
        ? weightedFillingProtein / totalWeightedProtein
        : 0.5;

    // Apply lysine degradation
    const lysineRetentionFactor =
      doughWeight * netDoughLysineRetention +
      fillingWeight * maillardFactors.filling.lysine;
    const otherRetentionFactor =
      doughWeight *
        ((1 - crustPct) * maillardFactors.doughCore.other +
          crustPct * maillardFactors.doughCrust.other) +
      fillingWeight * maillardFactors.filling.other;

    const adjustedBlendedAA = { ...calculated.blendedAA };
    adjustedBlendedAA.lysine =
      calculated.blendedAA.lysine * lysineRetentionFactor;
    adjustedBlendedAA.methionine_cysteine =
      calculated.blendedAA.methionine_cysteine * otherRetentionFactor;
    adjustedBlendedAA.tryptophan =
      calculated.blendedAA.tryptophan * otherRetentionFactor;

    // Recalculate scores
    const adjustedAAScores: Record<string, number> = {};
    let adjustedLimitingAA = null,
      adjustedLimitingScore = Infinity;
    Object.keys(REFERENCE_PATTERN).forEach((aa) => {
      const score = adjustedBlendedAA[aa] / REFERENCE_PATTERN[aa];
      adjustedAAScores[aa] = score;
      if (score < adjustedLimitingScore) {
        adjustedLimitingScore = score;
        adjustedLimitingAA = aa;
      }
    });

    // Calculate adjusted PDCAAS (this was missing!)
    const adjustedAminoAcidScore = Math.min(adjustedLimitingScore, 1);
    const adjustedPdcaas = Math.min(
      adjustedAminoAcidScore * calculated.weightedDigestibility,
      1
    );

    // Update corrected protein to use concentrated values
    const finishedProteinPerServing = finishedNutritionals.protein;
    const adjustedCorrectedProteinFinished =
      finishedProteinPerServing * adjustedPdcaas;
    const adjustedPercentDVFinished =
      (adjustedCorrectedProteinFinished / 50) * 100;

    return {
      enabled: true,
      adjustedPdcaas,
      adjustedBlendedAA,
      adjustedAAScores,
      adjustedLimitingAA,
      adjustedLimitingScore,
      adjustedAminoAcidScore,
      adjustedCorrectedProtein: adjustedCorrectedProteinFinished,
      adjustedPercentDV: adjustedPercentDVFinished,
      moistureAdjustment: {
        dough: {
          originalMoisture: calculated.doughMoisture,
          finalMoisture: effTargetDoughMoisture,
          change: doughMoistureChange,
          isPickup: doughMoistureChange > 0,
          originalWaterPct: calculated.doughWaterPct,
          adjustedWaterPct: doughAdjustedWaterPct,
          nonWaterMoisture: calculated.doughNonWaterMoisture,
        },
        filling: {
          originalMoisture: calculated.fillingMoisture,
          finalMoisture: effTargetFillingMoisture,
          change: fillingMoistureChange,
          isPickup: fillingMoistureChange > 0,
          originalWaterPct: calculated.fillingWaterPct,
          adjustedWaterPct: fillingAdjustedWaterPct,
          nonWaterMoisture: calculated.fillingNonWaterMoisture,
        },
      },
      maillardAdjustment: {
        method: consumerPrepMethod,
        factors: maillardFactors,
        crustFraction: crustPct,
        lysineRetention: {
          filling: maillardFactors.filling.lysine,
          doughCore: maillardFactors.doughCore.lysine,
          doughCrust: maillardFactors.doughCrust.lysine,
          netDough: netDoughLysineRetention,
        },
        proteinWeights: { dough: doughWeight, filling: fillingWeight },
      },
      concentrationFactor: {
        dough: doughConcentration,
        filling: fillingConcentration,
        overall: overallConcentration,
      },
      // Finished formulas (water removed, re-normalized to 100%)
      finishedDoughFormula,
      finishedFillingFormula,
      finishedTotalFormula,
      // Weight change info
      weightLossPct,
      asMixedPieceWeight,
      finishedPieceWeight: pieceWeight,
      // Ratio info
      asMixedDoughRatio: amDR,
      asMixedFillingRatio: amFR,
      finishedDoughRatio,
      finishedFillingRatio,
      // Concentrated nutritionals for finished product
      finishedNutritionals,
      pdcaasChange: adjustedPdcaas - calculated.pdcaas,
      percentDVChange: adjustedPercentDVFinished - calculated.percentDV,
    };
  }, [
    postProcessEnabled,
    targetDoughMoisture,
    targetFillingMoisture,
    consumerPrepMethod,
    crustFraction,
    calculated,
    doughFormula,
    fillingFormula,
    effectiveRatios,
    doughRatio,
    fillingRatio,
    pieceWeight,
    servingSize,
    ALL_INGREDIENTS,
    MAILLARD_FACTORS,
    moistureMode,
    targetWholeMoisture,
  ]);

  // A component with a ratio but no rows is still blended at that ratio, so every
  // nutrient comes out proportionally low and nothing says so. Verified live
  // 2026-07-29: F222 declared 19.8626 g protein per 100 g where its filling alone
  // is 39.7252 -- exactly half, because it carries a 50% dough that does not
  // exist. The audit is the validation report; its job is to say so loudly.
  const emptyComponents = useMemo(() => {
    const empties: ("DOUGH" | "FILLING")[] = [];
    if (Object.keys(doughFormula).length === 0 && doughRatio > 0) empties.push("DOUGH");
    if (Object.keys(fillingFormula).length === 0 && 100 - doughRatio > 0) empties.push("FILLING");
    return empties;
  }, [doughFormula, fillingFormula, doughRatio]);

  // Display-ready nutrition values: use post-processed (concentrated) when enabled
  // Ensures Nutrition Overview, Nutrition Facts, and Packaging Dashboard all agree
  const nf = useMemo(() => {
    const useFinished = postProcessEnabled && postProcessed.enabled;
    const cf = postProcessed.concentrationFactor?.overall || 1;
    if (!useFinished) {
      return {
        caloriesPerServing: calculated.caloriesPerServing,
        proteinPerServing: calculated.proteinPerServing,
        fatPerServing: calculated.fatPerServing,
        satfatPerServing: calculated.satfatPerServing,
        transfatPerServing: calculated.transfatPerServing,
        cholesterolPerServing: calculated.cholesterolPerServing,
        carbsPerServing: calculated.carbsPerServing,
        fiberPerServing: calculated.fiberPerServing,
        sugarPerServing: calculated.sugarPerServing,
        sodiumPerServing: calculated.sodiumPerServing,
        vitaminDPerServing: calculated.vitaminDPerServing,
        calciumPerServing: calculated.calciumPerServing,
        ironPerServing: calculated.ironPerServing,
        potassiumPerServing: calculated.potassiumPerServing,
        correctedProteinPerServing: calculated.correctedProteinPerServing,
        percentDV: calculated.percentDV,
        // Per-100g
        calories: calculated.calories,
        totalProtein: calculated.totalProtein,
        totalFat: calculated.totalFat,
        totalSatFat: calculated.totalSatFat,
        totalTransFat: calculated.totalTransFat,
        totalCholesterol: calculated.totalCholesterol,
        totalCarbs: calculated.totalCarbs,
        totalFiber: calculated.totalFiber,
        totalSugar: calculated.totalSugar,
        totalSodium: calculated.totalSodium,
        totalVitaminD: calculated.totalVitaminD,
        totalCalcium: calculated.totalCalcium,
        totalIron: calculated.totalIron,
        totalPotassium: calculated.totalPotassium,
        proteinClaim: calculated.proteinClaim,
        claimColor: calculated.claimColor,
      };
    }
    // Post-processed (concentrated) values
    const pctDV = postProcessed.adjustedPercentDV;
    return {
      caloriesPerServing: postProcessed.finishedNutritionals.calories,
      proteinPerServing: postProcessed.finishedNutritionals.protein,
      fatPerServing: postProcessed.finishedNutritionals.fat,
      satfatPerServing: postProcessed.finishedNutritionals.satfat,
      transfatPerServing: calculated.transfatPerServing * cf,
      cholesterolPerServing: calculated.cholesterolPerServing * cf,
      carbsPerServing: postProcessed.finishedNutritionals.carbs,
      fiberPerServing: postProcessed.finishedNutritionals.fiber,
      sugarPerServing: postProcessed.finishedNutritionals.sugar,
      sodiumPerServing: postProcessed.finishedNutritionals.sodium,
      vitaminDPerServing: postProcessed.finishedNutritionals.vitaminD,
      calciumPerServing: postProcessed.finishedNutritionals.calcium,
      ironPerServing: postProcessed.finishedNutritionals.iron,
      potassiumPerServing: postProcessed.finishedNutritionals.potassium,
      correctedProteinPerServing: postProcessed.adjustedCorrectedProtein,
      percentDV: pctDV,
      // Per-100g (concentrated)
      calories: calculated.calories * cf,
      totalProtein: calculated.totalProtein * cf,
      totalFat: calculated.totalFat * cf,
      totalSatFat: calculated.totalSatFat * cf,
      totalTransFat: calculated.totalTransFat * cf,
      totalCholesterol: calculated.totalCholesterol * cf,
      totalCarbs: calculated.totalCarbs * cf,
      totalFiber: calculated.totalFiber * cf,
      totalSugar: calculated.totalSugar * cf,
      totalSodium: calculated.totalSodium * cf,
      totalVitaminD: calculated.totalVitaminD * cf,
      totalCalcium: calculated.totalCalcium * cf,
      totalIron: calculated.totalIron * cf,
      totalPotassium: calculated.totalPotassium * cf,
      proteinClaim: pctDV >= 20 ? "Excellent Source" : pctDV >= 10 ? "Good Source" : "No Claim",
      claimColor: pctDV >= 20 ? "#15803d" : pctDV >= 10 ? "#b45309" : "#b91c1c",
    };
  }, [postProcessEnabled, postProcessed, calculated]);

  const helperData = useMemo(() => {
    // Only show ingredients that are actually in the current recipe (dough or filling)
    const recipeIngredientNames = new Set([
      ...Object.keys(doughFormula),
      ...Object.keys(fillingFormula),
    ]);
    const ingredients = Object.entries(ALL_INGREDIENTS)
      .map(([name, ing]) => {
        if (!recipeIngredientNames.has(name)) return null;
        if (ing.protein_per_100g < 5) return null;
        const scores: Record<string, number> = {};
        let limitingAA: string | null = null,
          limitingScore = Infinity;
        Object.entries(ing.amino_acids).forEach(([aa, val]: [string, any]) => {
          const score = val / REFERENCE_PATTERN[aa];
          scores[aa] = score;
          if (score < limitingScore) {
            limitingScore = score;
            limitingAA = aa;
          }
        });
        return {
          name,
          color: ing.color,
          protein: ing.protein_per_100g,
          digestibility: ing.digestibility,
          scores,
          limitingAA,
          limitingScore,
          pdcaas: Math.min(limitingScore, 1) * ing.digestibility,
        };
      })
      .filter(Boolean);

    return { ingredients };
  }, [calculated.limitingAA, doughFormula, fillingFormula, ALL_INGREDIENTS]);

  // AI suggestions draw from ALL ingredients (not just recipe) to find improvements
  const aiSuggestions = useMemo(() => {
    const currentLimiting = calculated.limitingAA;
    if (!currentLimiting) return [];
    return Object.entries(ALL_INGREDIENTS)
      .map(([name, ing]) => {
        if (ing.protein_per_100g < 5) return null;
        const scores: Record<string, number> = {};
        let limitingAA: string | null = null, limitingScore = Infinity;
        Object.entries(ing.amino_acids).forEach(([aa, val]: [string, any]) => {
          const score = val / REFERENCE_PATTERN[aa];
          scores[aa] = score;
          if (score < limitingScore) { limitingScore = score; limitingAA = aa; }
        });
        return { name, color: ing.color, protein: ing.protein_per_100g, digestibility: ing.digestibility, scores, limitingAA, limitingScore, pdcaas: Math.min(limitingScore, 1) * ing.digestibility };
      })
      .filter(Boolean)
      .filter((ing) => {
        const inDough = doughFormula[ing.name] !== undefined;
        const inFilling = fillingFormula[ing.name] !== undefined;
        const currentPct = (inDough ? doughFormula[ing.name] : 0) + (inFilling ? fillingFormula[ing.name] : 0);
        return currentPct < 5;
      })
      .filter((ing) => ing.scores[currentLimiting] > 1)
      .sort((a, b) => b.scores[currentLimiting] - a.scores[currentLimiting])
      .slice(0, 3);
  }, [calculated.limitingAA, doughFormula, fillingFormula, ALL_INGREDIENTS]);

  const handleDoughChange = (name, val) =>
    setDoughFormula((prev) => ({
      ...prev,
      [name]: Math.max(0, Math.min(100, parseFloat(val) || 0)),
    }));
  const handleFillingChange = (name, val) =>
    setFillingFormula((prev) => ({
      ...prev,
      [name]: Math.max(0, Math.min(100, parseFloat(val) || 0)),
    }));
  const addIngredient = (section, name) => {
    if (section === "dough") {
      setDoughFormula((prev) => ({ ...prev, [name]: 0 }));
      setShowAddDough(false);
    } else {
      setFillingFormula((prev) => ({ ...prev, [name]: 0 }));
      setShowAddFilling(false);
    }
  };
  const removeIngredient = (section, name) => {
    if (section === "dough")
      setDoughFormula((prev) => {
        const n = { ...prev };
        delete n[name];
        return n;
      });
    else
      setFillingFormula((prev) => {
        const n = { ...prev };
        delete n[name];
        return n;
      });
  };

  const getDisplayPct = (pct, section) => {
    if (viewMode === "component") return pct;
    const ratio = section === "dough" ? doughRatio : fillingRatio;
    return (pct / 100) * ratio;
  };

  // Inverse of getDisplayPct: what the user types in "% of total recipe" mode
  // converts back to the stored %-of-component before it reaches state. Grams
  // and pct_of_component stay the source of truth; the view is only a lens.
  const displayToComponentPct = (val: string, section) => {
    if (viewMode === "component") return val;
    const ratio = section === "dough" ? doughRatio : fillingRatio;
    if (!ratio || ratio <= 0) return val;
    return String(((parseFloat(val) || 0) / ratio) * 100);
  };

  // Per-ingredient % commits once on blur/Enter, never per keystroke — a
  // controlled number input here eats the decimal point as you type ("12.5"
  // becomes 125). Same pattern as the batch-size fix in FormulaSheet.tsx.
  const commitIngredientPct = (
    section: "dough" | "filling",
    name: string,
    el: HTMLInputElement,
  ) => {
    const n = parseFloat(el.value);
    if (!isFinite(n) || n < 0) {
      el.value = el.defaultValue;
      return;
    }
    if (section === "dough")
      handleDoughChange(name, displayToComponentPct(el.value, "dough"));
    else handleFillingChange(name, displayToComponentPct(el.value, "filling"));
  };

  const getAAColor = (score, isLimiting, allAbove) => {
    if (allAbove)
      return `hsl(210, ${60 + Math.min(score, 1.5) * 20}%, ${
        45 + Math.min(score, 1.5) * 10
      }%)`;
    if (isLimiting) return "#ef4444";
    if (score < 1) return "#f87171";
    return "#22c55e";
  };

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0]?.payload;
      const aaKey = Object.keys(AA_NAMES).find(
        (k) => AA_NAMES[k] === data?.name
      );
      const isLysine = aaKey === "lysine";
      const hasDegradation =
        postProcessEnabled &&
        (isLysine || aaKey === "methionine_cysteine" || aaKey === "tryptophan");
      const degradationFactor = isLysine
        ? postProcessed.maillardAdjustment.proteinWeights.dough *
            postProcessed.maillardAdjustment.lysineRetention.netDough +
          postProcessed.maillardAdjustment.proteinWeights.filling *
            postProcessed.maillardAdjustment.lysineRetention.filling
        : 1;

      return (
        <div
          style={{
            backgroundColor: "rgba(30,20,40,0.97)",
            padding: "12px",
            border: "1px solid rgba(139,92,246,0.3)",
            borderRadius: "8px",
            fontSize: "11px",
            maxWidth: "220px",
          }}
        >
          <p style={{ fontWeight: "bold", color: "#fff", marginBottom: "6px" }}>
            {data?.fullName}
          </p>
          <p style={{ color: "#a78bfa" }}>
            Total: {data?.total?.toFixed(1)} mg/g | Ref: {data?.reference}
          </p>
          <p
            style={{
              color: data?.isLimiting
                ? "#f87171"
                : calculated.allAbove100
                ? "#60a5fa"
                : "#4ade80",
              fontWeight: "600",
            }}
          >
            Score: {(data?.score * 100).toFixed(0)}%
            {data?.isLimiting && !calculated.allAbove100 ? " ← LIMITING" : ""}
          </p>

          {hasDegradation && (
            <div
              style={{
                marginTop: "8px",
                padding: "6px",
                backgroundColor: "rgba(251,191,36,0.2)",
                borderRadius: "4px",
                borderLeft: "2px solid #fbbf24",
              }}
            >
              <p
                style={{ color: "#fbbf24", fontSize: "9px", fontWeight: "600" }}
              >
                {t('formulation.postProcessDegradation')}
              </p>
              {isLysine && (
                <>
                  <p style={{ color: "#fcd34d", fontSize: "9px" }}>
                    {t('formulation.maillardFactor')}: {(degradationFactor * 100).toFixed(1)}%
                  </p>
                  <p style={{ color: "#fcd34d", fontSize: "9px" }}>
                    {t('formulation.adjusted')}: {(data?.total * degradationFactor).toFixed(1)}{" "}
                    mg/g
                  </p>
                  <p
                    style={{
                      color: "#9ca3af",
                      fontSize: "8px",
                      marginTop: "4px",
                    }}
                  >
                    {consumerPrepMethod === "none"
                      ? t('formulation.noThermalAdjustment')
                      : consumerPrepMethod === "steam"
                      ? t('formulation.steamOnlyMinimalLoss')
                      : `${t('formulation.browningAt')} ${
                          consumerPrepMethod === "light_brown"
                            ? "150°C"
                            : "180°C+"
                        }: ${t('formulation.crustLysineReducedTo')} ${(
                          postProcessed.maillardAdjustment.lysineRetention
                            .doughCrust * 100
                        ).toFixed(0)}%`}
                  </p>
                </>
              )}
            </div>
          )}

          <div
            style={{
              marginTop: "8px",
              borderTop: "1px solid rgba(139,92,246,0.2)",
              paddingTop: "8px",
            }}
          >
            {payload
              .filter((p) => p.value > 0 && p.dataKey !== "reference")
              .map((entry, i) => (
                <p key={i} style={{ color: entry.fill, fontSize: "10px" }}>
                  {entry.dataKey}: {entry.value.toFixed(1)}
                </p>
              ))}
          </div>
        </div>
      );
    }
    return null;
  };

  const getProteinDisplay = (type) => {
    if (proteinUnit === "serving") {
      return type === "uncorrected"
        ? calculated.proteinPerServing
        : calculated.correctedProteinPerServing;
    }
    return type === "uncorrected"
      ? calculated.totalProtein
      : calculated.totalProtein * calculated.pdcaas;
  };

  const generateCalcAudit = () => {
    const isKo = i18n.language === 'ko';
    const emptyComponentWarning = emptyComponents.length === 0 ? "" : (isKo
      ? `*** 경고: ${emptyComponents.map(c => c === "DOUGH" ? '도우' : '필링').join(', ')} 구성요소에 원료가 없으나 비율이 배정되어 있습니다.\n    모든 영양 성분이 그 비율만큼 낮게 계산됩니다. 아래 값을 사용하지 마십시오. ***\n\n`
      : `*** WARNING: ${emptyComponents.join(', ')} has a ratio but no ingredients.\n    Every nutrient below is understated by that share. Do not use these values. ***\n\n`);
    const fi =
      sourceMode === "supabase"
        ? dbFormulas.find((f) => f.code === selectedFormula)
        : null;

    return (
      `PROTEIN PAL - ${isKo ? '계산 감사 (Calculation Audit)' : 'Calculation Audit'}
${isKo ? '생성일' : 'Generated'}: ${new Date().toISOString()}
${isKo ? '모드' : 'Mode'}: ${sourceMode.toUpperCase()}${
        sourceMode === "supabase"
          ? ` | ${isKo ? '배합' : 'Formula'}: ` +
            selectedFormula +
            " (" +
            (fi?.displayName || "") +
            ")"
          : ` | ${isKo ? '데모 데이터' : 'Demo Data'}`
      }
=====================================

1. ${isKo ? '배합 조성 (FORMULA COMPOSITION)' : 'FORMULA COMPOSITION'}
----------------------
${emptyComponentWarning}${isKo ? '도우 비율' : 'Dough Ratio'}: ${doughRatio}% | ${isKo ? '필링 비율' : 'Filling Ratio'}: ${fillingRatio}% (${ratioMode === 'finished' ? (isKo ? '완제품' : 'FINISHED') : (isKo ? '혼합 기준' : 'AS-MIXED')} ${isKo ? '기준' : 'basis'})
${postProcessEnabled && ratioMode === 'finished' ? `${isKo ? '혼합 기준 비율' : 'As-Mixed Ratio'}: ${isKo ? '도우' : 'Dough'} ${postProcessed.asMixedDoughRatio.toFixed(1)}% | ${isKo ? '필링' : 'Filling'} ${postProcessed.asMixedFillingRatio.toFixed(1)}%\n` : ''}${postProcessEnabled && ratioMode === 'as-mixed' ? `${isKo ? '완제품 비율' : 'Finished Ratio'}: ${isKo ? '도우' : 'Dough'} ${postProcessed.finishedDoughRatio.toFixed(1)}% | ${isKo ? '필링' : 'Filling'} ${postProcessed.finishedFillingRatio.toFixed(1)}%\n` : ''}${isKo ? '1회 제공량' : 'Serving Size'}: ${servingSize}g | ${isKo ? '개당 무게' : 'Piece Weight'}: ${pieceWeight}g (${isKo ? '완제품' : 'finished'})${postProcessEnabled ? `\n${isKo ? '혼합 기준 개당 무게' : 'As-Mixed Piece Weight'}: ${postProcessed.asMixedPieceWeight.toFixed(2)}g` : ''}

${isKo ? '도우 배합 (도우 기준 %)' : 'Dough Formula (% of dough)'}:
${Object.entries(doughFormula)
  .map(([n, p]) => "  " + n + ": " + p + "%")
  .join("\n")}
  ${isKo ? '합계' : 'TOTAL'}: ${doughTotal.toFixed(2)}%

${isKo ? '필링 배합 (필링 기준 %)' : 'Filling Formula (% of filling)'}:
${Object.entries(fillingFormula)
  .map(([n, p]) => "  " + n + ": " + p + "%")
  .join("\n")}
  ${isKo ? '합계' : 'TOTAL'}: ${fillingTotal.toFixed(2)}%

2. ${isKo ? '전체 배합 (제품 기준 %)' : 'TOTAL FORMULA (% of product)'}
-------------------------------
${Object.entries(calculated.totalFormula)
  .filter(([, p]) => p > 0)
  .map(([n, p]) => n + ": " + p.toFixed(2) + "%")
  .join("\n")}

3. ${isKo ? '영양 계산 (100g당)' : 'NUTRITION CALCULATIONS (per 100g)'}
------------------------------------
${isKo ? '각 원재료: 기여량 = (배합% / 100) × 100g당 영양소' : 'For each ingredient: contribution = (formula% / 100) × nutrient_per_100g'}

${isKo ? '총 단백질' : 'Total Protein'}: ${calculated.totalProtein.toFixed(4)}g
${Object.entries(calculated.proteinContributions)
  .map(
    ([n, d]) =>
      "  " +
      n +
      ": (" +
      (calculated.totalFormula[n]?.toFixed(2) || 0) +
      "% / 100) × " +
      (ALL_INGREDIENTS[n]?.protein_per_100g || 0) +
      "g = " +
      d.protein.toFixed(4) +
      "g"
  )
  .join("\n")}

${isKo ? '총 지방' : 'Total Fat'}: ${calculated.totalFat.toFixed(4)}g
${isKo ? '총 탄수화물' : 'Total Carbs'}: ${calculated.totalCarbs.toFixed(4)}g
${isKo ? '총 식이섬유' : 'Total Fiber'}: ${calculated.totalFiber.toFixed(4)}g
${isKo ? '칼로리' : 'Calories'}: (${calculated.totalProtein.toFixed(
        2
      )} × 4) + (${calculated.totalCarbs.toFixed(
        2
      )} × 4) + (${calculated.totalFat.toFixed(
        2
      )} × 9) = ${calculated.calories.toFixed(2)} kcal

4. ${isKo ? '가중 소화율 (WEIGHTED DIGESTIBILITY)' : 'WEIGHTED DIGESTIBILITY'}
-------------------------
${isKo ? '공식' : 'Formula'}: Σ(digestibility_i × protein_contribution_i) / total_protein

${Object.entries(calculated.proteinContributions)
  .map(
    ([n, d]) =>
      n +
      ": " +
      d.digestibility +
      " × " +
      d.protein.toFixed(4) +
      " = " +
      (d.digestibility * d.protein).toFixed(4)
  )
  .join("\n")}
${isKo ? '합계' : 'Sum'}: ${Object.values(calculated.proteinContributions)
        .reduce((s, d) => s + d.digestibility * d.protein, 0)
        .toFixed(4)}
${isKo ? '가중 소화율' : 'Weighted Digestibility'}: ${Object.values(calculated.proteinContributions)
        .reduce((s, d) => s + d.digestibility * d.protein, 0)
        .toFixed(4)} / ${calculated.totalProtein.toFixed(
        4
      )} = ${calculated.weightedDigestibility.toFixed(4)} (${(
        calculated.weightedDigestibility * 100
      ).toFixed(1)}%)

5. ${isKo ? '아미노산 프로파일 (AMINO ACID PROFILE)' : 'AMINO ACID PROFILE'}
---------------------
${isKo ? '혼합 AA' : 'Blended AA'} = Σ(AA_i × protein_fraction_i)

${Object.entries(calculated.blendedAA)
  .map(([aa, val]) => {
    const ref = REFERENCE_PATTERN[aa];
    const score = calculated.aaScores[aa];
    return (
      AA_FULL_NAMES[aa] +
      " (" +
      AA_NAMES[aa] +
      "):\n  " + (isKo ? "혼합" : "Blended") + ": " +
      val.toFixed(2) +
      " mg/g | " + (isKo ? "기준" : "Reference") + ": " +
      ref +
      " mg/g\n  " + (isKo ? "점수" : "Score") + ": " +
      val.toFixed(2) +
      " / " +
      ref +
      " = " +
      score.toFixed(4) +
      " (" +
      (score * 100).toFixed(1) +
      "%)" +
      (aa === calculated.limitingAA ? ` ← ${isKo ? '제한 (LIMITING)' : 'LIMITING'}` : "")
    );
  })
  .join("\n\n")}

6. ${isKo ? 'PDCAAS 계산' : 'PDCAAS CALCULATION'}
---------------------
${isKo ? '아미노산 점수' : 'Amino Acid Score'} (AAS): min(${isKo ? '모든 AA 점수' : 'all AA scores'}, 1) = min(${calculated.limitingScore.toFixed(
        4
      )}, 1) = ${calculated.aminoAcidScore.toFixed(4)}
PDCAAS: AAS × ${isKo ? '가중 소화율' : 'Weighted Digestibility'} = ${calculated.aminoAcidScore.toFixed(
        4
      )} × ${calculated.weightedDigestibility.toFixed(
        4
      )} = ${calculated.pdcaas.toFixed(4)}
${isKo ? '절단 PDCAAS' : 'Truncated PDCAAS'}: min(${calculated.pdcaas.toFixed(
        4
      )}, 1) = ${calculated.pdcaas.toFixed(4)} (${(
        calculated.pdcaas * 100
      ).toFixed(1)}%)

7. ${isKo ? '1회 제공량 기준 (PER SERVING VALUES)' : 'PER SERVING VALUES'} (${servingSize}g)${postProcessEnabled ? (isKo ? ' - 혼합 기준 (완제품 값은 9번 참조)' : ' - AS-MIXED BASIS (see section 9 for finished values)') : ''}
---------------------------------------
${isKo ? '미보정 단백질' : 'Uncorrected Protein'}: ${calculated.totalProtein.toFixed(
        4
      )} × (${servingSize}/100) = ${calculated.proteinPerServing.toFixed(4)}g
${isKo ? '보정 단백질' : 'Corrected Protein'}: ${calculated.proteinPerServing.toFixed(
        4
      )} × ${calculated.pdcaas.toFixed(
        4
      )} = ${calculated.correctedProteinPerServing.toFixed(4)}g
${isKo ? '% 일일 섭취량' : '% Daily Value'}: (${calculated.correctedProteinPerServing.toFixed(
        4
      )} / 50) × 100 = ${calculated.percentDV.toFixed(1)}%

${isKo ? '단백질 클레임' : 'Protein Claim'}: ${calculated.proteinClaim} (${
        calculated.percentDV >= 20
          ? "≥20% DV"
          : calculated.percentDV >= 10
          ? "≥10% DV"
          : "<10% DV"
      })

8. ${isKo ? '원가 분석 (COST ANALYSIS)' : 'COST ANALYSIS'}
----------------
${isKo ? '100g당 총 원가' : 'Total Cost per 100g'}: €${calculated.totalCost.toFixed(4)}
${isKo ? '1회분당 원가' : 'Cost per Serving'}: €${calculated.costPerServing.toFixed(4)}
${isKo ? '단백질 g당 원가' : 'Cost per g Protein'}: €${calculated.costPerGProtein.toFixed(4)}

${calculated.ingredientData
  .map(
    (i) =>
      i.name +
      ": " +
      i.pct.toFixed(2) +
      "% × €" +
      i.costPerKg +
      "/kg = €" +
      i.costContrib.toFixed(4) +
      "/100g"
  )
  .join("\n")}

9. ${isKo ? 'FDA 반올림 규칙 적용' : 'FDA ROUNDING RULES APPLIED'} (21 CFR 101.9)${postProcessEnabled ? (isKo ? ' - 완제품 값' : ' - FINISHED PRODUCT VALUES') : ''}
--------------------------------------------
${isKo ? '각 영양소에는 FDA 규정에 따른 특정 반올림 규칙이 있습니다:' : 'Each nutrient has specific rounding rules per FDA regulations:'}${postProcessEnabled ? (isKo ? '\n참고: 아래 값은 완제품의 후공정(농축) 영양 값을 사용합니다.' : '\nNote: Values below use post-processed (concentrated) nutritionals for the finished product.') : ''}

${isKo ? '칼로리' : 'CALORIES'} [21 CFR 101.9(c)(1)]:
  ${isKo ? '규칙' : 'Rule'}: <5 cal→0, ≤50 cal→${isKo ? '5 단위' : 'nearest 5'}, >50 cal→${isKo ? '10 단위' : 'nearest 10'}
  ${isKo ? '실제' : 'Actual'}: ${calculated.caloriesPerServing.toFixed(
    2
  )} kcal → ${isKo ? '표시' : 'Declared'}: ${fdaRound.calories(calculated.caloriesPerServing)} kcal

${isKo ? '총 지방' : 'TOTAL FAT'} [21 CFR 101.9(c)(2)]:
  ${isKo ? '규칙' : 'Rule'}: <0.5g→0, <5g→${isKo ? '0.5g 단위' : 'nearest 0.5g'}, ≥5g→${isKo ? '1g 단위' : 'nearest 1g'}
  ${isKo ? '실제' : 'Actual'}: ${calculated.fatPerServing.toFixed(2)}g → ${isKo ? '표시' : 'Declared'}: ${fdaRound.fat(
        calculated.fatPerServing
      )}g
  %DV: ${calculated.fatPerServing.toFixed(2)}g / 78g × 100 = ${(
        (calculated.fatPerServing / 78) *
        100
      ).toFixed(1)}% → ${Math.round((calculated.fatPerServing / 78) * 100)}%

${isKo ? '포화지방' : 'SATURATED FAT'} [21 CFR 101.9(c)(2)(i)]:
  ${isKo ? '규칙' : 'Rule'}: <0.5g→0, <5g→${isKo ? '0.5g 단위' : 'nearest 0.5g'}, ≥5g→${isKo ? '1g 단위' : 'nearest 1g'}
  ${isKo ? '실제' : 'Actual'}: ${calculated.satfatPerServing.toFixed(
    2
  )}g → ${isKo ? '표시' : 'Declared'}: ${fdaRound.satfat(calculated.satfatPerServing)}g
  %DV: ${calculated.satfatPerServing.toFixed(2)}g / 20g × 100 = ${(
        (calculated.satfatPerServing / 20) *
        100
      ).toFixed(1)}% → ${Math.round((calculated.satfatPerServing / 20) * 100)}%

${isKo ? '트랜스 지방' : 'TRANS FAT'} [21 CFR 101.9(c)(2)(ii)]:
  ${isKo ? '규칙' : 'Rule'}: <0.5g→0, <5g→${isKo ? '0.5g 단위' : 'nearest 0.5g'}, ≥5g→${isKo ? '1g 단위' : 'nearest 1g'}
  ${isKo ? '실제' : 'Actual'}: ${calculated.transfatPerServing.toFixed(
    2
  )}g → ${isKo ? '표시' : 'Declared'}: ${fdaRound.transfat(calculated.transfatPerServing)}g
  ${isKo ? '참고: %DV 설정 없음; 섭취 최소화' : 'Note: No %DV established; minimize intake'}

${isKo ? '콜레스테롤' : 'CHOLESTEROL'} [21 CFR 101.9(c)(3)]:
  ${isKo ? '규칙' : 'Rule'}: <2mg→0, 2-5mg→"<5mg", >5mg→${isKo ? '5mg 단위' : 'nearest 5mg'}
  ${isKo ? '실제' : 'Actual'}: ${calculated.cholesterolPerServing.toFixed(
    2
  )}mg → ${isKo ? '표시' : 'Declared'}: ${fdaRound.cholesterol(calculated.cholesterolPerServing)}mg
  %DV: ${calculated.cholesterolPerServing.toFixed(2)}mg / 300mg × 100 = ${(
        (calculated.cholesterolPerServing / 300) *
        100
      ).toFixed(1)}% → ${Math.round(
        (calculated.cholesterolPerServing / 300) * 100
      )}%

${isKo ? '나트륨' : 'SODIUM'} [21 CFR 101.9(c)(4)]:
  ${isKo ? '규칙' : 'Rule'}: <5mg→0, ≤140mg→${isKo ? '5mg 단위' : 'nearest 5mg'}, >140mg→${isKo ? '10mg 단위' : 'nearest 10mg'}
  ${isKo ? '실제' : 'Actual'}: ${calculated.sodiumPerServing.toFixed(
    2
  )}mg → ${isKo ? '표시' : 'Declared'}: ${fdaRound.sodium(calculated.sodiumPerServing)}mg
  %DV: ${calculated.sodiumPerServing.toFixed(2)}mg / 2300mg × 100 = ${(
        (calculated.sodiumPerServing / 2300) *
        100
      ).toFixed(1)}% → ${Math.round(
        (calculated.sodiumPerServing / 2300) * 100
      )}%

${isKo ? '총 탄수화물' : 'TOTAL CARBOHYDRATE'} [21 CFR 101.9(c)(6)]:
  ${isKo ? '규칙' : 'Rule'}: <0.5g→0, ≥0.5g→${isKo ? '1g 단위' : 'nearest 1g'}
  ${isKo ? '실제' : 'Actual'}: ${calculated.carbsPerServing.toFixed(
    2
  )}g → ${isKo ? '표시' : 'Declared'}: ${fdaRound.carbs(calculated.carbsPerServing)}g
  %DV: ${calculated.carbsPerServing.toFixed(2)}g / 275g × 100 = ${(
        (calculated.carbsPerServing / 275) *
        100
      ).toFixed(1)}% → ${Math.round((calculated.carbsPerServing / 275) * 100)}%

${isKo ? '식이섬유' : 'DIETARY FIBER'} [21 CFR 101.9(c)(6)(i)]:
  ${isKo ? '규칙' : 'Rule'}: <0.5g→0, ≥0.5g→${isKo ? '1g 단위' : 'nearest 1g'}
  ${isKo ? '실제' : 'Actual'}: ${calculated.fiberPerServing.toFixed(
    2
  )}g → ${isKo ? '표시' : 'Declared'}: ${fdaRound.fiber(calculated.fiberPerServing)}g
  %DV: ${calculated.fiberPerServing.toFixed(2)}g / 28g × 100 = ${(
        (calculated.fiberPerServing / 28) *
        100
      ).toFixed(1)}% → ${Math.round((calculated.fiberPerServing / 28) * 100)}%

${isKo ? '총 당류' : 'TOTAL SUGARS'} [21 CFR 101.9(c)(6)(ii)]:
  ${isKo ? '규칙' : 'Rule'}: <0.5g→0, ≥0.5g→${isKo ? '1g 단위' : 'nearest 1g'}
  ${isKo ? '실제' : 'Actual'}: ${calculated.sugarPerServing.toFixed(
    2
  )}g → ${isKo ? '표시' : 'Declared'}: ${fdaRound.sugar(calculated.sugarPerServing)}g
  ${isKo ? '참고: 총 당류에 대한 %DV 없음 (첨가당 DRV = 50g)' : 'Note: No %DV for total sugars (Added Sugars DRV = 50g)'}

${isKo ? '단백질' : 'PROTEIN'} [21 CFR 101.9(c)(7)]:
  ${isKo ? '규칙' : 'Rule'}: <0.5g→0, ≥0.5g→${isKo ? '1g 단위' : 'nearest 1g'}
  ${isKo ? '중요: 라벨에는 미보정 단백질 표시, %DV는 PDCAAS 보정 단백질 사용' : 'CRITICAL: Label shows UNCORRECTED protein, %DV uses PDCAAS-CORRECTED protein'}

  ${isKo ? '라벨 표시용' : 'For Label Declaration'}:
    ${isKo ? '실제' : 'Actual'}: ${calculated.proteinPerServing.toFixed(
      2
    )}g → ${isKo ? '표시' : 'Declared'}: ${fdaRound.protein(calculated.proteinPerServing)}g

  ${isKo ? '%DV 계산용' : 'For %DV Calculation'}:
    ${isKo ? '보정 단백질' : 'Corrected Protein'}: ${calculated.correctedProteinPerServing.toFixed(2)}g
    %DV: ${calculated.correctedProteinPerServing.toFixed(
      2
    )}g / 50g × 100 = ${calculated.percentDV.toFixed(1)}% → ${Math.round(
        calculated.percentDV
      )}%

═══════════════════════════════════════════════════════════════════════════════
${isKo ? '필수 미량영양소' : 'MANDATORY MICRONUTRIENTS'} [21 CFR 101.9(c)(8)(ii)]
═══════════════════════════════════════════════════════════════════════════════
${isKo ? '2016년 FDA 영양성분 표시 업데이트에 따라, 이 4가지 미량영양소는 필수입니다:' : 'Per 2016 FDA Nutrition Facts Label update, these 4 micronutrients are MANDATORY:'}

${isKo ? '비타민 D' : 'VITAMIN D'} [21 CFR 101.9(c)(8)(ii)]:
  ${isKo ? '규칙' : 'Rule'}: <0.1mcg→0, <10mcg→${isKo ? '0.1mcg 단위' : 'nearest 0.1mcg'}, ≥10mcg→${isKo ? '1mcg 단위' : 'nearest 1mcg'}
  ${isKo ? '원재료별 계산' : 'Calculation per ingredient'}:
${calculated.ingredientData
  .map(
    (i) =>
      "    " +
      i.name +
      ": (" +
      i.pct.toFixed(2) +
      "/100) × " +
      (ALL_INGREDIENTS[i.name]?.vitaminD || 0) +
      "mcg = " +
      i.vitaminDContrib.toFixed(4) +
      "mcg"
  )
  .join("\n")}
  ${isKo ? '100g당 합계' : 'Total per 100g'}: ${calculated.totalVitaminD.toFixed(4)}mcg
  ${isKo ? '1회분' : 'Per Serving'} (${servingSize}g): ${calculated.vitaminDPerServing.toFixed(
        4
      )}mcg → ${isKo ? '표시' : 'Declared'}: ${fdaRound.vitaminD(calculated.vitaminDPerServing)}mcg
  %DV: ${calculated.vitaminDPerServing.toFixed(4)}mcg / 20mcg × 100 = ${(
        (calculated.vitaminDPerServing / 20) *
        100
      ).toFixed(1)}% → ${Math.round(
        (calculated.vitaminDPerServing / 20) * 100
      )}%
  ${isKo ? '참고: 대부분의 식물성 식품은 강화하지 않으면 0mcg (UV에 노출된 버섯은 예외)' : 'Note: Most plant-based foods contain 0mcg unless fortified (mushrooms exposed to UV are an exception)'}

${isKo ? '칼슘' : 'CALCIUM'} [21 CFR 101.9(c)(8)(ii)]:
  ${isKo ? '규칙' : 'Rule'}: <10mg→0, 10-100mg→${isKo ? '10mg 단위' : 'nearest 10mg'}, 100-1000mg→${isKo ? '50mg 단위' : 'nearest 50mg'}, >1000mg→${isKo ? '100mg 단위' : 'nearest 100mg'}
  ${isKo ? '원재료별 계산' : 'Calculation per ingredient'}:
${calculated.ingredientData
  .map(
    (i) =>
      "    " +
      i.name +
      ": (" +
      i.pct.toFixed(2) +
      "/100) × " +
      (ALL_INGREDIENTS[i.name]?.calcium || 0) +
      "mg = " +
      i.calciumContrib.toFixed(4) +
      "mg"
  )
  .join("\n")}
  ${isKo ? '100g당 합계' : 'Total per 100g'}: ${calculated.totalCalcium.toFixed(4)}mg
  ${isKo ? '1회분' : 'Per Serving'} (${servingSize}g): ${calculated.calciumPerServing.toFixed(
        4
      )}mg → ${isKo ? '표시' : 'Declared'}: ${fdaRound.calcium(calculated.calciumPerServing)}mg
  %DV: ${calculated.calciumPerServing.toFixed(4)}mg / 1300mg × 100 = ${(
        (calculated.calciumPerServing / 1300) *
        100
      ).toFixed(1)}% → ${Math.round(
        (calculated.calciumPerServing / 1300) * 100
      )}%
  ${isKo ? '참고: 콩류는 적당한 급원; 클레임을 위해 강화 제품이나 칼슘 첨가가 필요할 수 있음' : 'Note: Legumes are moderate sources; fortified products or added calcium may be needed for claims'}

${isKo ? '철분' : 'IRON'} [21 CFR 101.9(c)(8)(ii)]:
  ${isKo ? '규칙' : 'Rule'}: <0.5mg→0, 0.5-2mg→${isKo ? '0.1mg 단위' : 'nearest 0.1mg'}, >2mg→${isKo ? '0.5mg 단위' : 'nearest 0.5mg'}
  ${isKo ? '원재료별 계산' : 'Calculation per ingredient'}:
${calculated.ingredientData
  .map(
    (i) =>
      "    " +
      i.name +
      ": (" +
      i.pct.toFixed(2) +
      "/100) × " +
      (ALL_INGREDIENTS[i.name]?.iron || 0) +
      "mg = " +
      i.ironContrib.toFixed(4) +
      "mg"
  )
  .join("\n")}
  ${isKo ? '100g당 합계' : 'Total per 100g'}: ${calculated.totalIron.toFixed(4)}mg
  ${isKo ? '1회분' : 'Per Serving'} (${servingSize}g): ${calculated.ironPerServing.toFixed(
        4
      )}mg → ${isKo ? '표시' : 'Declared'}: ${fdaRound.iron(calculated.ironPerServing)}mg
  %DV: ${calculated.ironPerServing.toFixed(4)}mg / 18mg × 100 = ${(
        (calculated.ironPerServing / 18) *
        100
      ).toFixed(1)}% → ${Math.round((calculated.ironPerServing / 18) * 100)}%
  ${isKo ? '참고: 식물성 철분(비헴)은 헴 철분(~15-35%)보다 생체이용률이 낮음(~5-12%)' : 'Note: Plant iron (non-heme) has lower bioavailability (~5-12%) than heme iron (~15-35%)'}

${isKo ? '칼륨' : 'POTASSIUM'} [21 CFR 101.9(c)(8)(ii)]:
  ${isKo ? '규칙' : 'Rule'}: <5mg→0, ≤140mg→${isKo ? '5mg 단위' : 'nearest 5mg'}, >140mg→${isKo ? '10mg 단위' : 'nearest 10mg'} (${isKo ? '나트륨과 동일' : 'same as sodium'})
  ${isKo ? '원재료별 계산' : 'Calculation per ingredient'}:
${calculated.ingredientData
  .map(
    (i) =>
      "    " +
      i.name +
      ": (" +
      i.pct.toFixed(2) +
      "/100) × " +
      (ALL_INGREDIENTS[i.name]?.potassium || 0) +
      "mg = " +
      i.potassiumContrib.toFixed(4) +
      "mg"
  )
  .join("\n")}
  ${isKo ? '100g당 합계' : 'Total per 100g'}: ${calculated.totalPotassium.toFixed(4)}mg
  ${isKo ? '1회분' : 'Per Serving'} (${servingSize}g): ${calculated.potassiumPerServing.toFixed(
        4
      )}mg → ${isKo ? '표시' : 'Declared'}: ${fdaRound.potassium(calculated.potassiumPerServing)}mg
  %DV: ${calculated.potassiumPerServing.toFixed(4)}mg / 4700mg × 100 = ${(
        (calculated.potassiumPerServing / 4700) *
        100
      ).toFixed(1)}% → ${Math.round(
        (calculated.potassiumPerServing / 4700) * 100
      )}%
  ${isKo ? '참고: 2020년 1월 1일부터 의무 표시; 콩류와 통곡물이 좋은 급원' : 'Note: Mandatory as of Jan 1, 2020; legumes and whole grains are good sources'}

10. ${isKo ? '영양성분 표시 - 최종 표시 값' : 'NUTRITION FACTS PANEL - FINAL DECLARATION VALUES'}${postProcessEnabled ? (isKo ? ' (후공정)' : ' (POST-PROCESSED)') : ''}
----------------------------------------------------
${isKo ? '21 CFR 101.9에 따라 - 라벨에 표시되는 값:' : 'Per 21 CFR 101.9 - Values as they appear on label:'}${postProcessEnabled ? (isKo ? '\n참고: 값은 ' + postProcessed.weightLossPct.toFixed(1) + '% 수분 손실 후 농축된 완제품을 반영합니다' : '\nNote: Values reflect concentrated finished product after ' + postProcessed.weightLossPct.toFixed(1) + '% moisture loss') : ''}

┌─────────────────────────────────────┐
│ Nutrition Facts                     │
├─────────────────────────────────────┤
│ ${Math.round(
        servingSize / pieceWeight
      )} pieces (${servingSize}g)                      │
├─────────────────────────────────────┤
│ Calories                ${fdaRound
        .calories(nf.caloriesPerServing)
        .toString()
        .padStart(10)}  │
├─────────────────────────────────────┤
│ Total Fat ${fdaRound.fat(nf.fatPerServing)}g           ${Math.round(
        (nf.fatPerServing / 78) * 100
      )
        .toString()
        .padStart(10)}% │
│   Saturated Fat ${fdaRound.satfat(
        nf.satfatPerServing
      )}g     ${Math.round((nf.satfatPerServing / 20) * 100)
        .toString()
        .padStart(10)}% │
│   Trans Fat ${fdaRound.transfat(
        nf.transfatPerServing
      )}g                       │
│ Cholesterol ${fdaRound.cholesterol(
        nf.cholesterolPerServing
      )}mg          ${Math.round((nf.cholesterolPerServing / 300) * 100)
        .toString()
        .padStart(10)}% │
│ Sodium ${fdaRound.sodium(
        nf.sodiumPerServing
      )}mg              ${Math.round((nf.sodiumPerServing / 2300) * 100)
        .toString()
        .padStart(10)}% │
│ Total Carb. ${fdaRound.carbs(
        nf.carbsPerServing
      )}g          ${Math.round((nf.carbsPerServing / 275) * 100)
        .toString()
        .padStart(10)}% │
│   Dietary Fiber ${fdaRound.fiber(
        nf.fiberPerServing
      )}g       ${Math.round((nf.fiberPerServing / 28) * 100)
        .toString()
        .padStart(10)}% │
│   Total Sugars ${fdaRound.sugar(
        nf.sugarPerServing
      )}g                    │
│ Protein ${fdaRound.protein(
        nf.proteinPerServing
      )}g              ${Math.round(nf.percentDV)
        .toString()
        .padStart(10)}% │
├─────────────────────────────────────┤
│ Vitamin D ${fdaRound.vitaminD(
        nf.vitaminDPerServing
      )}mcg          ${Math.round((nf.vitaminDPerServing / 20) * 100)
        .toString()
        .padStart(10)}% │
│ Calcium ${fdaRound.calcium(
        nf.calciumPerServing
      )}mg             ${Math.round((nf.calciumPerServing / 1300) * 100)
        .toString()
        .padStart(10)}% │
│ Iron ${fdaRound.iron(
        nf.ironPerServing
      )}mg                 ${Math.round((nf.ironPerServing / 18) * 100)
        .toString()
        .padStart(10)}% │
│ Potassium ${fdaRound.potassium(
        nf.potassiumPerServing
      )}mg          ${Math.round((nf.potassiumPerServing / 4700) * 100)
        .toString()
        .padStart(10)}% │
└─────────────────────────────────────┘
${
  calculated.pdcaas < 0.2
    ? (isKo
        ? '\n필수 문구: "Not a significant source of protein" - 단백질 표시 옆에 병기. 근거: 21 CFR 101.9(c)(7)(i), PDCAAS ' + (calculated.pdcaas * 100).toFixed(1) + '% < 20%.'
        : '\nREQUIRED STATEMENT: "Not a significant source of protein" adjacent to the protein declaration. Basis: 21 CFR 101.9(c)(7)(i), PDCAAS ' + (calculated.pdcaas * 100).toFixed(1) + '% < 20%.')
    : ''
}
11. ${isKo ? '원재료 표시 (INGREDIENT STATEMENT)' : 'INGREDIENT STATEMENT'} (21 CFR 101.4)
---------------------------------------
${isKo ? 'FDA 규정에 따라 중량 기준 내림차순으로 원재료를 나열합니다.' : 'Per FDA regulations, ingredients listed in descending order of predominance by weight.'}

${isKo ? '전체 배합 % 기준 정렬' : 'Sorted by % of Total Formula'}:
${[...calculated.ingredientData]
  .sort((a, b) => b.pct - a.pct)
  .map(
    (i, idx) =>
      "  " +
      (idx + 1) +
      ". " +
      (ALL_INGREDIENTS[i.name]?.fdaName || i.name) +
      " (" +
      i.pct.toFixed(1) +
      "%)"
  )
  .join("\n")}

${isKo ? '원재료 표시 (라벨에 인쇄)' : 'INGREDIENT STATEMENT (as printed on label)'}:
"${calculated.sortedIngredients.join(", ")}."

${isKo ? '알레르겐 표시' : 'ALLERGEN DECLARATION'} [21 CFR 101.4(d)]:
${isKo ? '포함: 밀, 대두' : 'Contains: Wheat, Soy'}
${isKo ? '참고: 배합에 9대 주요 알레르겐이 포함된 경우 반드시 표시해야 합니다:' : 'Note: Must declare if formula contains any of the 9 major allergens:'}
      ${isKo ? '우유, 계란, 생선, 갑각류, 견과류, 땅콩, 밀, 대두, 참깨' : 'Milk, Eggs, Fish, Shellfish, Tree Nuts, Peanuts, Wheat, Soybeans, Sesame'}

12. ${isKo ? '영양성분 클레임 요약' : 'NUTRIENT CONTENT CLAIMS SUMMARY'} (21 CFR 101.54)
---------------------------------------------------
${isKo ? '클레임은 RACC(통상적 소비 기준량)당 %DV에 기반합니다' : 'Claims are based on %DV per RACC (Reference Amount Customarily Consumed)'}

═══════════════════════════════════════════════════════════════════════════════
${isKo ? '단백질 클레임' : 'PROTEIN CLAIMS'}
═══════════════════════════════════════════════════════════════════════════════
${isKo ? '1회분당 보정 단백질' : 'Corrected Protein per Serving'}: ${calculated.correctedProteinPerServing.toFixed(
        2
      )}g
${isKo ? '단백질 %DV' : 'Protein %DV'}: ${calculated.percentDV.toFixed(1)}%
${isKo ? '단백질 DRV' : 'DRV for Protein'}: 50g

${isKo ? '클레임 자격' : 'Claim Qualification'} [21 CFR 101.54(c)]:
${
  calculated.percentDV >= 20
    ? `  ✓ ${isKo ? '우수한 단백질 급원 (EXCELLENT SOURCE OF PROTEIN)' : 'EXCELLENT SOURCE OF PROTEIN'} (≥20% DV)`
    : calculated.percentDV >= 10
    ? `  ✓ ${isKo ? '단백질 급원 (GOOD SOURCE OF PROTEIN)' : 'GOOD SOURCE OF PROTEIN'} (≥10% DV)`
    : `  ✗ ${isKo ? '단백질 클레임 불가 (No protein claim allowed)' : 'No protein claim allowed'} (<10% DV)`
}
${
  calculated.percentDV >= 20
    ? `  ✓ ${isKo ? '고단백 (HIGH PROTEIN)' : 'HIGH PROTEIN'} (≥20% DV) - ${isKo ? '대체 문구 허용' : 'alternate wording allowed'}`
    : ""
}
${
  calculated.percentDV >= 10
    ? `  ✓ ${isKo ? '단백질 함유 (CONTAINS PROTEIN)' : 'CONTAINS PROTEIN'} (≥10% DV) - ${isKo ? '대체 문구 허용' : 'alternate wording allowed'}`
    : ""
}

${isKo ? '전면 표시 단백질 선언' : 'Front-of-Pack Protein Declaration'}:
  ${isKo ? '미보정 (그램 표시용)' : 'Uncorrected (for grams callout)'}: ${fdaRound.protein(
    calculated.proteinPerServing
  )}g ${isKo ? '1회 제공량당 단백질' : 'protein per serving'}
  ${isKo ? '표시 가능' : 'May state'}: "${fdaRound.protein(
    calculated.proteinPerServing
  )}g PROTEIN" ${isKo ? '전면 표시' : 'on front of pack'}

═══════════════════════════════════════════════════════════════════════════════
${isKo ? '식이섬유 클레임' : 'FIBER CLAIMS'}
═══════════════════════════════════════════════════════════════════════════════
${isKo ? '1회분당 식이섬유' : 'Fiber per Serving'}: ${calculated.fiberPerServing.toFixed(2)}g
${isKo ? '식이섬유 %DV' : 'Fiber %DV'}: ${((calculated.fiberPerServing / 28) * 100).toFixed(1)}%
${isKo ? '식이섬유 DRV' : 'DRV for Fiber'}: 28g

${isKo ? '클레임 자격' : 'Claim Qualification'} [21 CFR 101.54(d)]:
${
  calculated.fiberPerServing >= 5
    ? `  ✓ ${isKo ? '우수한 식이섬유 급원 (EXCELLENT SOURCE OF FIBER)' : 'EXCELLENT SOURCE OF FIBER'} (≥5g ${isKo ? '1회분당' : 'per serving'})`
    : calculated.fiberPerServing >= 2.5
    ? `  ✓ ${isKo ? '식이섬유 급원 (GOOD SOURCE OF FIBER)' : 'GOOD SOURCE OF FIBER'} (≥2.5g ${isKo ? '1회분당' : 'per serving'})`
    : `  ✗ ${isKo ? '식이섬유 클레임 불가' : 'No fiber claim allowed'} (<2.5g ${isKo ? '1회분당' : 'per serving'})`
}
${
  calculated.fiberPerServing >= 5
    ? `  ✓ ${isKo ? '고식이섬유 (HIGH FIBER)' : 'HIGH FIBER'} (≥5g ${isKo ? '1회분당' : 'per serving'}) - ${isKo ? '대체 문구 허용' : 'alternate wording allowed'}`
    : ""
}
${
  calculated.fiberPerServing >= 2.5
    ? `  ✓ ${isKo ? '식이섬유 함유 (CONTAINS FIBER)' : 'CONTAINS FIBER'} (≥2.5g ${isKo ? '1회분당' : 'per serving'}) - ${isKo ? '대체 문구 허용' : 'alternate wording allowed'}`
    : ""
}

${isKo ? '전면 표시 식이섬유 선언' : 'Front-of-Pack Fiber Declaration'}:
  ${isKo ? '표시 가능' : 'May state'}: "${fdaRound.fiber(
    calculated.fiberPerServing
  )}g FIBER" ${isKo ? '전면 표시' : 'on front of pack'}

═══════════════════════════════════════════════════════════════════════════════
${isKo ? '철분 클레임' : 'IRON CLAIMS'}
═══════════════════════════════════════════════════════════════════════════════
${isKo ? '1회분당 철분' : 'Iron per Serving'}: ${calculated.ironPerServing.toFixed(2)}mg
${isKo ? '철분 %DV' : 'Iron %DV'}: ${((calculated.ironPerServing / 18) * 100).toFixed(1)}%
${isKo ? '철분 DRV' : 'DRV for Iron'}: 18mg

${isKo ? '클레임 자격' : 'Claim Qualification'} [21 CFR 101.54]:
${
  (calculated.ironPerServing / 18) * 100 >= 20
    ? `  ✓ ${isKo ? '우수한 철분 급원 (EXCELLENT SOURCE OF IRON)' : 'EXCELLENT SOURCE OF IRON'} (≥20% DV)`
    : (calculated.ironPerServing / 18) * 100 >= 10
    ? `  ✓ ${isKo ? '철분 급원 (GOOD SOURCE OF IRON)' : 'GOOD SOURCE OF IRON'} (≥10% DV)`
    : `  ✗ ${isKo ? '철분 클레임 불가' : 'No iron claim allowed'} (<10% DV)`
}
${
  (calculated.ironPerServing / 18) * 100 >= 20
    ? `  ✓ ${isKo ? '고철분 (HIGH IN IRON)' : 'HIGH IN IRON'} (≥20% DV) - ${isKo ? '대체 문구 허용' : 'alternate wording allowed'}`
    : ""
}

${isKo ? '참고: 식물성(비헴) 철분은 생체이용률이 낮습니다. 비타민 C와 함께 섭취를 고려하세요.' : 'Note: Plant-based (non-heme) iron has lower bioavailability. Consider pairing with Vitamin C.'}

═══════════════════════════════════════════════════════════════════════════════
${isKo ? '칼슘 클레임' : 'CALCIUM CLAIMS'}
═══════════════════════════════════════════════════════════════════════════════
${isKo ? '1회분당 칼슘' : 'Calcium per Serving'}: ${calculated.calciumPerServing.toFixed(2)}mg
${isKo ? '칼슘 %DV' : 'Calcium %DV'}: ${((calculated.calciumPerServing / 1300) * 100).toFixed(1)}%
${isKo ? '칼슘 DRV' : 'DRV for Calcium'}: 1300mg

${isKo ? '클레임 자격' : 'Claim Qualification'} [21 CFR 101.54]:
${
  (calculated.calciumPerServing / 1300) * 100 >= 20
    ? `  ✓ ${isKo ? '우수한 칼슘 급원 (EXCELLENT SOURCE OF CALCIUM)' : 'EXCELLENT SOURCE OF CALCIUM'} (≥20% DV)`
    : (calculated.calciumPerServing / 1300) * 100 >= 10
    ? `  ✓ ${isKo ? '칼슘 급원 (GOOD SOURCE OF CALCIUM)' : 'GOOD SOURCE OF CALCIUM'} (≥10% DV)`
    : `  ✗ ${isKo ? '칼슘 클레임 불가' : 'No calcium claim allowed'} (<10% DV)`
}
${
  (calculated.calciumPerServing / 1300) * 100 >= 20
    ? `  ✓ ${isKo ? '고칼슘 (HIGH IN CALCIUM)' : 'HIGH IN CALCIUM'} (≥20% DV) - ${isKo ? '대체 문구 허용' : 'alternate wording allowed'}`
    : ""
}

═══════════════════════════════════════════════════════════════════════════════
${isKo ? '칼륨 클레임' : 'POTASSIUM CLAIMS'}
═══════════════════════════════════════════════════════════════════════════════
${isKo ? '1회분당 칼륨' : 'Potassium per Serving'}: ${calculated.potassiumPerServing.toFixed(2)}mg
${isKo ? '칼륨 %DV' : 'Potassium %DV'}: ${((calculated.potassiumPerServing / 4700) * 100).toFixed(1)}%
${isKo ? '칼륨 DRV' : 'DRV for Potassium'}: 4700mg

${isKo ? '클레임 자격' : 'Claim Qualification'} [21 CFR 101.54]:
${
  (calculated.potassiumPerServing / 4700) * 100 >= 20
    ? `  ✓ ${isKo ? '우수한 칼륨 급원 (EXCELLENT SOURCE OF POTASSIUM)' : 'EXCELLENT SOURCE OF POTASSIUM'} (≥20% DV)`
    : (calculated.potassiumPerServing / 4700) * 100 >= 10
    ? `  ✓ ${isKo ? '칼륨 급원 (GOOD SOURCE OF POTASSIUM)' : 'GOOD SOURCE OF POTASSIUM'} (≥10% DV)`
    : `  ✗ ${isKo ? '칼륨 클레임 불가' : 'No potassium claim allowed'} (<10% DV)`
}
${
  (calculated.potassiumPerServing / 4700) * 100 >= 20
    ? `  ✓ ${isKo ? '고칼륨 (HIGH IN POTASSIUM)' : 'HIGH IN POTASSIUM'} (≥20% DV) - ${isKo ? '대체 문구 허용' : 'alternate wording allowed'}`
    : ""
}

${isKo ? '참고: 2020년부터 의무 표시. 콩류가 우수한 천연 급원입니다.' : 'Note: Mandatory declaration since 2020. Legumes are excellent natural sources.'}

═══════════════════════════════════════════════════════════════════════════════
${isKo ? '전면 표시 요약' : 'FRONT-OF-PACK SUMMARY'}
═══════════════════════════════════════════════════════════════════════════════
${isKo ? '허용 가능한 전면 표시 클레임' : 'Allowable Front-of-Pack Claims'}:

${isKo ? '단백질' : 'PROTEIN'}:
  ${
    calculated.percentDV >= 20
      ? `• "${isKo ? '우수한 단백질 급원' : 'EXCELLENT SOURCE OF PROTEIN'}"`
      : calculated.percentDV >= 10
      ? `• "${isKo ? '단백질 급원' : 'GOOD SOURCE OF PROTEIN'}"`
      : `• ${isKo ? '단백질 클레임 불가' : 'No protein claim allowed'}`
  }
  • "${fdaRound.protein(calculated.proteinPerServing)}g ${isKo ? '1회분당 단백질' : 'PROTEIN PER SERVING'}"
  ${calculated.percentDV >= 20 ? `• "${isKo ? '고단백' : 'HIGH IN PROTEIN'}"` : ""}

${isKo ? '식이섬유' : 'FIBER'}:
  ${
    calculated.fiberPerServing >= 5
      ? `• "${isKo ? '우수한 식이섬유 급원' : 'EXCELLENT SOURCE OF FIBER'}"`
      : calculated.fiberPerServing >= 2.5
      ? `• "${isKo ? '식이섬유 급원' : 'GOOD SOURCE OF FIBER'}"`
      : `• ${isKo ? '식이섬유 클레임 불가' : 'No fiber claim allowed'}`
  }
  ${
    calculated.fiberPerServing >= 2.5
      ? '• "' +
        fdaRound.fiber(calculated.fiberPerServing) +
        'g ' + (isKo ? '1회분당 식이섬유' : 'FIBER PER SERVING') + '"'
      : ""
  }
  ${calculated.fiberPerServing >= 5 ? `• "${isKo ? '고식이섬유' : 'HIGH IN FIBER'}"` : ""}

${isKo ? '철분' : 'IRON'}:
  ${
    (calculated.ironPerServing / 18) * 100 >= 20
      ? `• "${isKo ? '우수한 철분 급원' : 'EXCELLENT SOURCE OF IRON'}"`
      : (calculated.ironPerServing / 18) * 100 >= 10
      ? `• "${isKo ? '철분 급원' : 'GOOD SOURCE OF IRON'}"`
      : `• ${isKo ? '철분 클레임 불가' : 'No iron claim allowed'}`
  }
  ${(calculated.ironPerServing / 18) * 100 >= 20 ? `• "${isKo ? '고철분' : 'HIGH IN IRON'}"` : ""}

${isKo ? '칼슘' : 'CALCIUM'}:
  ${
    (calculated.calciumPerServing / 1300) * 100 >= 20
      ? `• "${isKo ? '우수한 칼슘 급원' : 'EXCELLENT SOURCE OF CALCIUM'}"`
      : (calculated.calciumPerServing / 1300) * 100 >= 10
      ? `• "${isKo ? '칼슘 급원' : 'GOOD SOURCE OF CALCIUM'}"`
      : `• ${isKo ? '칼슘 클레임 불가' : 'No calcium claim allowed'}`
  }
  ${
    (calculated.calciumPerServing / 1300) * 100 >= 20
      ? `• "${isKo ? '고칼슘' : 'HIGH IN CALCIUM'}"`
      : ""
  }

${isKo ? '칼륨' : 'POTASSIUM'}:
  ${
    (calculated.potassiumPerServing / 4700) * 100 >= 20
      ? `• "${isKo ? '우수한 칼륨 급원' : 'EXCELLENT SOURCE OF POTASSIUM'}"`
      : (calculated.potassiumPerServing / 4700) * 100 >= 10
      ? `• "${isKo ? '칼륨 급원' : 'GOOD SOURCE OF POTASSIUM'}"`
      : `• ${isKo ? '칼륨 클레임 불가' : 'No potassium claim allowed'}`
  }
  ${
    (calculated.potassiumPerServing / 4700) * 100 >= 20
      ? `• "${isKo ? '고칼륨' : 'HIGH IN POTASSIUM'}"`
      : ""
  }

${isKo ? '통합 표시 제안' : 'COMBINED CALLOUT SUGGESTION'}:
  "${fdaRound.protein(calculated.proteinPerServing)}g ${isKo ? '단백질' : 'Protein'}${
        calculated.fiberPerServing >= 2.5
          ? " • " + fdaRound.fiber(calculated.fiberPerServing) + "g " + (isKo ? '식이섬유' : 'Fiber')
          : ""
      }${
        (calculated.ironPerServing / 18) * 100 >= 10
          ? " • " + (isKo ? '철분 급원' : 'Good Source of Iron')
          : ""
      } ${isKo ? '1회분당' : 'per Serving'}"

═══════════════════════════════════════════════════════════════════════════════
${isKo ? '규제 참고 문헌' : 'REGULATORY REFERENCES'}
═══════════════════════════════════════════════════════════════════════════════
${isKo ? '영양 표시' : 'NUTRITION LABELING'}:
• 21 CFR 101.9 - Nutrition labeling of food (rounding rules, mandatory nutrients)
• 21 CFR 101.9(c)(1) - Calories declaration and rounding
• 21 CFR 101.9(c)(2)-(7) - Macronutrient declaration and rounding
• 21 CFR 101.9(c)(8)(ii) - Micronutrient declaration and rounding

${isKo ? '원재료 표시' : 'INGREDIENT LABELING'}:
• 21 CFR 101.4 - Food; designation of ingredients
• 21 CFR 101.4(d) - Major allergen declaration (FALCPA)

${isKo ? '영양성분 함량 클레임' : 'NUTRIENT CONTENT CLAIMS'}:
• 21 CFR 101.54 - Nutrient content claims for "good source," "high," etc.
• 21 CFR 101.54(b) - "High" claims (≥20% DV per RACC)
• 21 CFR 101.54(c) - "Good Source" claims (10-19% DV per RACC)
• 21 CFR 101.13 - Nutrient content claims - general principles

${isKo ? '단백질 품질' : 'PROTEIN QUALITY'}:
• 21 CFR 101.9(c)(7)(ii) - PDCAAS for protein %DV calculation
• FAO/WHO 1991 - PDCAAS methodology and reference patterns
• 2-5 year old reference pattern (WHO/FAO/UNU 2007)

${isKo ? '일일 기준값' : 'DAILY REFERENCE VALUES'} (DRVs):
• Protein: 50g | Fat: 78g | Sat Fat: 20g | Carbs: 275g | Fiber: 28g
• Sodium: 2300mg | Cholesterol: 300mg
• Vitamin D: 20mcg | Calcium: 1300mg | Iron: 18mg | Potassium: 4700mg
` +
      (postProcessEnabled
        ? `
===============================================================================
${isKo ? '후공정 조정 (POST-PROCESS ADJUSTMENTS)' : 'POST-PROCESS ADJUSTMENTS'} (${isKo ? '고급 모드' : 'ADVANCED MODE'})
===============================================================================
${isKo ? '열처리가 단백질 품질에 미치는 영향을 모델링합니다.' : 'Models the impact of thermal processing on protein quality.'}
${isKo ? '모든 값: 혼합 기준 --> 조정 --> 최종' : 'All values shown as: AS-MIXED --> ADJUSTMENT --> FINAL'}

-------------------------------------------------------------------------------
${isKo ? '수분 조정' : 'MOISTURE ADJUSTMENT'}
-------------------------------------------------------------------------------
${isKo ? '도우 단계' : 'DOUGH PHASE'}:
  ${isKo ? '총 수분' : 'Total Moisture'}: ${calculated.doughMoisture.toFixed(1)}% --> ${
            postProcessed.moistureAdjustment.dough.isPickup ? "+" : "-"
          }${Math.abs(postProcessed.moistureAdjustment.dough.change).toFixed(
            1
          )}% ${
            postProcessed.moistureAdjustment.dough.isPickup
              ? (isKo ? "(흡수)" : "(pickup)")
              : (isKo ? "(손실)" : "(loss)")
          } --> ${targetDoughMoisture.toFixed(1)}%

  ${isKo ? '수분 내역 (혼합 기준)' : 'Moisture Breakdown (as-mixed)'}:
    ${isKo ? '물 원재료' : 'Water ingredient'}: ${postProcessed.moistureAdjustment.dough.originalWaterPct.toFixed(
      1
    )}% of formula x 100% moisture = ${postProcessed.moistureAdjustment.dough.originalWaterPct.toFixed(
            1
          )}%
    ${isKo ? '기타 원재료' : 'Other ingredients'}: ${postProcessed.moistureAdjustment.dough.nonWaterMoisture.toFixed(
      1
    )}% ${isKo ? '기여' : 'contribution'}
    ${isKo ? '합계' : 'Total'}: ${postProcessed.moistureAdjustment.dough.originalWaterPct.toFixed(
      1
    )}% + ${postProcessed.moistureAdjustment.dough.nonWaterMoisture.toFixed(
            1
          )}% = ${calculated.doughMoisture.toFixed(1)}%
  
  ${isKo ? '수분 조정' : 'Water Adjustment'}:
    ${isKo ? '목표 총 수분 달성' : 'To achieve'} ${targetDoughMoisture.toFixed(1)}% ${isKo ? '' : 'total moisture'}:
    ${isKo ? '필요 수분' : 'Required Water'} = ${isKo ? '목표 - 비수분 수분' : 'target - non-water moisture'} = ${targetDoughMoisture.toFixed(
      1
    )}% - ${postProcessed.moistureAdjustment.dough.nonWaterMoisture.toFixed(
            1
          )}% = ${postProcessed.moistureAdjustment.dough.adjustedWaterPct.toFixed(
            1
          )}%
    Water: ${postProcessed.moistureAdjustment.dough.originalWaterPct.toFixed(
      1
    )}% --> ${postProcessed.moistureAdjustment.dough.adjustedWaterPct.toFixed(
            1
          )}% (${
            postProcessed.moistureAdjustment.dough.adjustedWaterPct >
            postProcessed.moistureAdjustment.dough.originalWaterPct
              ? "+"
              : ""
          }${(
            postProcessed.moistureAdjustment.dough.adjustedWaterPct -
            postProcessed.moistureAdjustment.dough.originalWaterPct
          ).toFixed(1)}%)

${isKo ? '필링 단계' : 'FILLING PHASE'}:
  ${isKo ? '총 수분' : 'Total Moisture'}: ${calculated.fillingMoisture.toFixed(1)}% --> ${
            postProcessed.moistureAdjustment.filling.isPickup ? "+" : "-"
          }${Math.abs(postProcessed.moistureAdjustment.filling.change).toFixed(
            1
          )}% ${
            postProcessed.moistureAdjustment.filling.isPickup
              ? (isKo ? "(흡수)" : "(pickup)")
              : (isKo ? "(손실)" : "(loss)")
          } --> ${targetFillingMoisture.toFixed(1)}%

  ${isKo ? '수분 내역 (혼합 기준)' : 'Moisture Breakdown (as-mixed)'}:
    ${isKo ? '물 원재료' : 'Water ingredient'}: ${postProcessed.moistureAdjustment.filling.originalWaterPct.toFixed(
      1
    )}% of formula x 100% moisture = ${postProcessed.moistureAdjustment.filling.originalWaterPct.toFixed(
            1
          )}%
    ${isKo ? '기타 원재료' : 'Other ingredients'}: ${postProcessed.moistureAdjustment.filling.nonWaterMoisture.toFixed(
      1
    )}% ${isKo ? '기여' : 'contribution'}
    ${isKo ? '합계' : 'Total'}: ${postProcessed.moistureAdjustment.filling.originalWaterPct.toFixed(
      1
    )}% + ${postProcessed.moistureAdjustment.filling.nonWaterMoisture.toFixed(
            1
          )}% = ${calculated.fillingMoisture.toFixed(1)}%
  
  ${isKo ? '수분 조정' : 'Water Adjustment'}:
    ${isKo ? '목표 총 수분 달성' : 'To achieve'} ${targetFillingMoisture.toFixed(1)}% ${isKo ? '' : 'total moisture'}:
    ${isKo ? '필요 수분' : 'Required Water'} = ${targetFillingMoisture.toFixed(
      1
    )}% - ${postProcessed.moistureAdjustment.filling.nonWaterMoisture.toFixed(
            1
          )}% = ${postProcessed.moistureAdjustment.filling.adjustedWaterPct.toFixed(
            1
          )}%
    Water: ${postProcessed.moistureAdjustment.filling.originalWaterPct.toFixed(
      1
    )}% --> ${postProcessed.moistureAdjustment.filling.adjustedWaterPct.toFixed(
            1
          )}% (${
            postProcessed.moistureAdjustment.filling.adjustedWaterPct >
            postProcessed.moistureAdjustment.filling.originalWaterPct
              ? "+"
              : ""
          }${(
            postProcessed.moistureAdjustment.filling.adjustedWaterPct -
            postProcessed.moistureAdjustment.filling.originalWaterPct
          ).toFixed(1)}%)

-------------------------------------------------------------------------------
${isKo ? '마이야르 분해 (MAILLARD DEGRADATION)' : 'MAILLARD DEGRADATION'}
-------------------------------------------------------------------------------
${isKo ? '소비자 조리 방법' : 'Consumer Prep Method'}: ${t(MAILLARD_METHOD_I18N[consumerPrepMethod] || "postProcess.noAdjust")}
${isKo ? '크러스트 비율' : 'Crust Fraction'}: ${crustFraction}% ${isKo ? '도우 질량 대비' : 'of dough mass'}

${isKo ? '층별 라이신 보존율' : 'Lysine Retention by Layer'}:
  ${isKo ? '필링 (내부 ~75C, 보호됨)' : 'Filling (internal ~75C, protected)'}:
    ${isKo ? '보존율' : 'Retention'}: ${(
      postProcessed.maillardAdjustment.lysineRetention.filling * 100
    ).toFixed(1)}%

  ${isKo ? '도우 코어 (스팀 ~100C)' : 'Dough Core (steam ~100C)'}:
    ${isKo ? '보존율' : 'Retention'}: ${(
      postProcessed.maillardAdjustment.lysineRetention.doughCore * 100
    ).toFixed(1)}%

  ${isKo ? '도우 크러스트' : 'Dough Crust'} (${
    consumerPrepMethod === "steam"
      ? (isKo ? "갈변 없음" : "no browning")
      : consumerPrepMethod === "light_brown"
      ? (isKo ? "~150C 갈변" : "~150C browning")
      : (isKo ? "~180C+ 갈변" : "~180C+ browning")
  }):
    ${isKo ? '보존율' : 'Retention'}: ${(
      postProcessed.maillardAdjustment.lysineRetention.doughCrust * 100
    ).toFixed(1)}%

${isKo ? '도우 순 라이신 보존율' : 'Net Dough Lysine Retention'}:
  = (1 - crust_fraction) x core_retention + crust_fraction x crust_retention
  = (1 - ${(crustFraction / 100).toFixed(
    2
  )}) x ${postProcessed.maillardAdjustment.lysineRetention.doughCore.toFixed(
            3
          )} + ${(crustFraction / 100).toFixed(
            2
          )} x ${postProcessed.maillardAdjustment.lysineRetention.doughCrust.toFixed(
            3
          )}
  = ${(
    (1 - crustFraction / 100) *
    postProcessed.maillardAdjustment.lysineRetention.doughCore
  ).toFixed(4)} + ${(
            (crustFraction / 100) *
            postProcessed.maillardAdjustment.lysineRetention.doughCrust
          ).toFixed(4)}
  = ${postProcessed.maillardAdjustment.lysineRetention.netDough.toFixed(4)} (${(
            postProcessed.maillardAdjustment.lysineRetention.netDough * 100
          ).toFixed(1)}%)

-------------------------------------------------------------------------------
${isKo ? '아미노산 조정 (AMINO ACID ADJUSTMENTS)' : 'AMINO ACID ADJUSTMENTS'}
-------------------------------------------------------------------------------
${isKo ? '단백질 중량 분포' : 'Protein Weight Distribution'}:
  ${isKo ? '도우 기여' : 'Dough contributes'}: ${(
    postProcessed.maillardAdjustment.proteinWeights.dough * 100
  ).toFixed(1)}% ${isKo ? '총 단백질 대비' : 'of total protein'}
  ${isKo ? '필링 기여' : 'Filling contributes'}: ${(
    postProcessed.maillardAdjustment.proteinWeights.filling * 100
  ).toFixed(1)}% ${isKo ? '총 단백질 대비' : 'of total protein'}

${isKo ? '라이신 (주요 영향 AA)' : 'Lysine (primary affected AA)'}:
  ${isKo ? '혼합 기준' : 'As-Mixed'}: ${calculated.blendedAA.lysine.toFixed(2)} mg/g ${isKo ? '단백질' : 'protein'}
  --> ${isKo ? '마이야르 분해 계수' : 'Maillard degradation factor'}: ${(
    postProcessed.maillardAdjustment.proteinWeights.dough *
      postProcessed.maillardAdjustment.lysineRetention.netDough +
    postProcessed.maillardAdjustment.proteinWeights.filling *
      postProcessed.maillardAdjustment.lysineRetention.filling
  ).toFixed(4)}
  --> ${isKo ? '조정됨' : 'Adjusted'}: ${postProcessed.adjustedBlendedAA.lysine.toFixed(
    2
  )} mg/g ${isKo ? '단백질' : 'protein'}

Met+Cys: ${calculated.blendedAA.methionine_cysteine.toFixed(
            2
          )} --> ${postProcessed.adjustedBlendedAA.methionine_cysteine.toFixed(
            2
          )} mg/g
Tryptophan: ${calculated.blendedAA.tryptophan.toFixed(
            2
          )} --> ${postProcessed.adjustedBlendedAA.tryptophan.toFixed(2)} mg/g

-------------------------------------------------------------------------------
${isKo ? 'PDCAAS 비교: 혼합 기준 vs 후공정' : 'PDCAAS COMPARISON: AS-MIXED vs POST-PROCESSED'}
-------------------------------------------------------------------------------
                          ${isKo ? '혼합 기준' : 'AS-MIXED'}        ${isKo ? '후공정' : 'POST-PROCESSED'}     ${isKo ? '변화' : 'CHANGE'}
${isKo ? '제한 AA' : 'Limiting AA'}:              ${calculated.limitingAA.padEnd(
            15
          )} ${postProcessed.adjustedLimitingAA.padEnd(18)} ${
            calculated.limitingAA === postProcessed.adjustedLimitingAA
              ? (isKo ? "(동일)" : "(same)")
              : (isKo ? "(변경!)" : "(changed!)")
          }
${isKo ? '제한 점수' : 'Limiting Score'}:           ${calculated.limitingScore
            .toFixed(4)
            .padEnd(15)} ${postProcessed.adjustedLimitingScore
            .toFixed(4)
            .padEnd(18)} ${
            (postProcessed.adjustedLimitingScore - calculated.limitingScore >= 0
              ? "+"
              : "") +
            (
              postProcessed.adjustedLimitingScore - calculated.limitingScore
            ).toFixed(4)
          }
${isKo ? '아미노산 점수' : 'Amino Acid Score'}:         ${calculated.aminoAcidScore
            .toFixed(4)
            .padEnd(15)} ${postProcessed.adjustedAminoAcidScore
            .toFixed(4)
            .padEnd(18)} ${
            (postProcessed.adjustedAminoAcidScore - calculated.aminoAcidScore >=
            0
              ? "+"
              : "") +
            (
              postProcessed.adjustedAminoAcidScore - calculated.aminoAcidScore
            ).toFixed(4)
          }
PDCAAS:                   ${(calculated.pdcaas * 100).toFixed(2)}%${" ".repeat(
            11
          )} ${(postProcessed.adjustedPdcaas * 100).toFixed(2)}%${" ".repeat(
            14
          )} ${postProcessed.pdcaasChange >= 0 ? "+" : ""}${(
            postProcessed.pdcaasChange * 100
          ).toFixed(2)}%
${isKo ? '보정 단백질' : 'Corrected Protein'}:        ${calculated.correctedProteinPerServing.toFixed(
            2
          )}g${" ".repeat(11)} ${postProcessed.adjustedCorrectedProtein.toFixed(
            2
          )}g${" ".repeat(14)} ${
            (postProcessed.adjustedCorrectedProtein -
              calculated.correctedProteinPerServing >=
            0
              ? "+"
              : "") +
            (
              postProcessed.adjustedCorrectedProtein -
              calculated.correctedProteinPerServing
            ).toFixed(2)
          }g
%DV:                      ${calculated.percentDV.toFixed(1)}%${" ".repeat(
            12
          )} ${postProcessed.adjustedPercentDV.toFixed(1)}%${" ".repeat(15)} ${
            postProcessed.percentDVChange >= 0 ? "+" : ""
          }${postProcessed.percentDVChange.toFixed(1)}%

-------------------------------------------------------------------------------
${isKo ? '참고 문헌' : 'LITERATURE REFERENCES'}
-------------------------------------------------------------------------------
  Palamidis 1980: Bread crust 75% lysine availability vs 90% in crumb
  Rosenberg 1951: 15% avg lysine loss in baking (range 9.5-23.8%)
  Martins 2001: Maillard minimal below 120C, rate 3-5x per 10C above

-------------------------------------------------------------------------------
${isKo ? '완제품 영양소' : 'FINISHED PRODUCT NUTRITIONALS'} (${isKo ? '1회분' : 'per'} ${servingSize}g ${isKo ? '' : 'serving'})
-------------------------------------------------------------------------------
${isKo ? '아래 값은 수분 손실 후 농축된 완제품을 반영합니다.' : 'Values below reflect the CONCENTRATED finished product after moisture loss.'}
${isKo ? '이 값들이 영양성분 표시에 나타나는 값입니다.' : 'These are the values that appear on the Nutrition Facts label.'}

${isKo ? '농축 계수' : 'Concentration Factor'}: ${postProcessed.concentrationFactor.overall.toFixed(4)}x
${isKo ? '중량 감소' : 'Weight Loss'}: ${postProcessed.weightLossPct.toFixed(1)}%
${isKo ? '혼합 기준 개당 무게' : 'As-Mixed Piece Weight'}: ${postProcessed.asMixedPieceWeight.toFixed(2)}g → ${isKo ? '완제품' : 'Finished'}: ${pieceWeight}g

${isKo ? '비율 모드' : 'Ratio Mode'}: ${ratioMode === 'finished' ? (isKo ? '완제품' : 'FINISHED') : (isKo ? '혼합 기준' : 'AS-MIXED')}
${isKo ? '혼합 기준 비율' : 'As-Mixed Ratio'}: ${isKo ? '도우' : 'Dough'} ${postProcessed.asMixedDoughRatio.toFixed(1)}% | ${isKo ? '필링' : 'Filling'} ${postProcessed.asMixedFillingRatio.toFixed(1)}%
${isKo ? '완제품 비율' : 'Finished Ratio'}: ${isKo ? '도우' : 'Dough'} ${postProcessed.finishedDoughRatio.toFixed(1)}% | ${isKo ? '필링' : 'Filling'} ${postProcessed.finishedFillingRatio.toFixed(1)}%

                          ${isKo ? '혼합 기준 (1회분)' : 'AS-MIXED (per srv)'}   ${isKo ? '완제품 (1회분)' : 'FINISHED (per srv)'}   ${isKo ? '변화' : 'CHANGE'}
${isKo ? '칼로리' : 'Calories'}:                 ${calculated.caloriesPerServing.toFixed(2).padEnd(20)} ${nf.caloriesPerServing.toFixed(2).padEnd(20)} ${(nf.caloriesPerServing - calculated.caloriesPerServing >= 0 ? '+' : '') + (nf.caloriesPerServing - calculated.caloriesPerServing).toFixed(2)}
${isKo ? '총 지방' : 'Total Fat'}:                ${calculated.fatPerServing.toFixed(2).padEnd(20)} ${nf.fatPerServing.toFixed(2).padEnd(20)} ${(nf.fatPerServing - calculated.fatPerServing >= 0 ? '+' : '') + (nf.fatPerServing - calculated.fatPerServing).toFixed(2)}g
${isKo ? '포화지방' : 'Saturated Fat'}:            ${calculated.satfatPerServing.toFixed(2).padEnd(20)} ${nf.satfatPerServing.toFixed(2).padEnd(20)} ${(nf.satfatPerServing - calculated.satfatPerServing >= 0 ? '+' : '') + (nf.satfatPerServing - calculated.satfatPerServing).toFixed(2)}g
${isKo ? '총 탄수화물' : 'Total Carbs'}:              ${calculated.carbsPerServing.toFixed(2).padEnd(20)} ${nf.carbsPerServing.toFixed(2).padEnd(20)} ${(nf.carbsPerServing - calculated.carbsPerServing >= 0 ? '+' : '') + (nf.carbsPerServing - calculated.carbsPerServing).toFixed(2)}g
${isKo ? '식이섬유' : 'Dietary Fiber'}:            ${calculated.fiberPerServing.toFixed(2).padEnd(20)} ${nf.fiberPerServing.toFixed(2).padEnd(20)} ${(nf.fiberPerServing - calculated.fiberPerServing >= 0 ? '+' : '') + (nf.fiberPerServing - calculated.fiberPerServing).toFixed(2)}g
${isKo ? '총 당류' : 'Total Sugars'}:             ${calculated.sugarPerServing.toFixed(2).padEnd(20)} ${nf.sugarPerServing.toFixed(2).padEnd(20)} ${(nf.sugarPerServing - calculated.sugarPerServing >= 0 ? '+' : '') + (nf.sugarPerServing - calculated.sugarPerServing).toFixed(2)}g
${isKo ? '나트륨' : 'Sodium'}:                   ${calculated.sodiumPerServing.toFixed(2).padEnd(20)} ${nf.sodiumPerServing.toFixed(2).padEnd(20)} ${(nf.sodiumPerServing - calculated.sodiumPerServing >= 0 ? '+' : '') + (nf.sodiumPerServing - calculated.sodiumPerServing).toFixed(2)}mg
${isKo ? '단백질' : 'Protein'}:                  ${calculated.proteinPerServing.toFixed(2).padEnd(20)} ${nf.proteinPerServing.toFixed(2).padEnd(20)} ${(nf.proteinPerServing - calculated.proteinPerServing >= 0 ? '+' : '') + (nf.proteinPerServing - calculated.proteinPerServing).toFixed(2)}g
${isKo ? '보정 단백질' : 'Corrected Protein'}:        ${calculated.correctedProteinPerServing.toFixed(2).padEnd(20)} ${nf.correctedProteinPerServing.toFixed(2).padEnd(20)} ${(nf.correctedProteinPerServing - calculated.correctedProteinPerServing >= 0 ? '+' : '') + (nf.correctedProteinPerServing - calculated.correctedProteinPerServing).toFixed(2)}g
${isKo ? '%DV 단백질' : '%DV Protein'}:              ${calculated.percentDV.toFixed(1).padEnd(20)} ${nf.percentDV.toFixed(1).padEnd(20)} ${(nf.percentDV - calculated.percentDV >= 0 ? '+' : '') + (nf.percentDV - calculated.percentDV).toFixed(1)}%
${isKo ? '비타민 D' : 'Vitamin D'}:                ${calculated.vitaminDPerServing.toFixed(4).padEnd(20)} ${nf.vitaminDPerServing.toFixed(4).padEnd(20)} mcg
${isKo ? '칼슘' : 'Calcium'}:                  ${calculated.calciumPerServing.toFixed(2).padEnd(20)} ${nf.calciumPerServing.toFixed(2).padEnd(20)} mg
${isKo ? '철분' : 'Iron'}:                     ${calculated.ironPerServing.toFixed(2).padEnd(20)} ${nf.ironPerServing.toFixed(2).padEnd(20)} mg
${isKo ? '칼륨' : 'Potassium'}:                ${calculated.potassiumPerServing.toFixed(2).padEnd(20)} ${nf.potassiumPerServing.toFixed(2).padEnd(20)} mg

${isKo ? '교차 검증' : 'CROSS-VALIDATION'}:
  ${isKo ? '영양성분 표시 칼로리' : 'Nutrition Facts Label Calories'}: ${fdaRound.calories(nf.caloriesPerServing)} kcal
  ${isKo ? '영양 개요 칼로리' : 'Nutrition Overview Calories'}:   ${fdaRound.calories(nf.caloriesPerServing)} kcal
  ${isKo ? '상태: 일치 ✓' : 'Status: MATCH ✓'}
  ${
    isKo
      ? '칼로리 방식 확정 (2026-08-10): Atwater 4/4/9, 저장된 대량영양소 기준, 섬유질 차감 없음 - 메인 계산기와 동일한 수식.'
      : 'Calorie method settled 2026-08-10: Atwater 4/4/9 on stored macros, no fiber deduction - the same math as the main calculator.'
  }
  ${
    isKo
      ? '섬유질 에너지 보정(21 CFR 101.9(c)(1)(i))은 가용성/불용성 분획 데이터가 신뢰 가능해진 뒤 재검토(#17).'
      : 'Fiber-energy adjustment under 21 CFR 101.9(c)(1)(i) deferred until soluble/insoluble fraction data is reliable (#17).'
  }
`
        : "")
    );
  };

  const currentFormulaInfo =
    sourceMode === "supabase"
      ? dbFormulas.find((f) => f.code === selectedFormula)
      : null;

  // Chronological sort of formulas (F001, F002, ... F222, F223, ...) for the selector
  const sortedDbFormulas = [...dbFormulas].sort(compareFormulaCodes);

  return (
    <div
      style={{
        fontFamily: "Arial, sans-serif",
        minHeight: "calc(100vh - 96px)",
        padding: "16px",
        background: "transparent",
        color: "#1a1a2e",
      }}
    >
      <style>{`
        @keyframes shimmer { 0% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } 100% { background-position: 0% 50%; } }
        .card { background: rgba(255,255,255,0.97); color: #1a1a2e; border-radius: 12px; padding: 16px; box-shadow: 0 4px 20px rgba(0,0,0,0.15); margin-bottom: 20px; }
        input[type="range"] { -webkit-appearance: none; height: 5px; border-radius: 3px; outline: none; cursor: pointer; }
        input[type="range"]::-webkit-slider-thumb { -webkit-appearance: none; width: 14px; height: 14px; border-radius: 50%; background: white; border: 2px solid currentColor; cursor: pointer; box-shadow: 0 1px 4px rgba(0,0,0,0.3); }
        .nf-panel { font-family: Helvetica, 'Helvetica Neue', Arial, sans-serif; border: 0.5pt solid black; padding: 3pt; width: 260px; background: white; color: black; }
        .nf-title { font-size: 32px; font-weight: 900; margin: 0; line-height: 1; }
        .nf-line { border-top: 0.5px solid black; margin: 1px 0; }
        .nf-line-thick { border-top: 7pt solid black; margin: 0; }
        .nf-line-medium { border-top: 3pt solid black; margin: 0; }
        .nf-row { display: flex; justify-content: space-between; font-size: 14px; padding: 1px 0; position: relative; }
        .nf-indent { padding-left: 16px; }
        .nf-indent2 { padding-left: 32px; }
        .nf-bold { font-weight: 900; }
        .pie-tooltip { position: absolute; background: rgba(30,20,40,0.95); border: 1px solid rgba(139,92,246,0.4); padding: 8px 12px; border-radius: 6px; font-size: 10px; color: white; pointer-events: none; z-index: 50; white-space: nowrap; }
        .audit-text { font-family: monospace; font-size: 10px; white-space: pre-wrap; background: #1e1b4b; color: #e0e7ff; padding: 16px; border-radius: 8px; max-height: 400px; overflow-y: auto; }
      `}</style>

      {/* SUPABASE FORMULA PANEL */}
      {sourceMode === "supabase" && (
        <div
          style={{
            maxWidth: "1500px",
            margin: "0 auto 16px",
            padding: "16px",
            backgroundColor: "rgba(30,20,50,0.9)",
            borderRadius: "12px",
            border: "1px solid rgba(59,130,246,0.4)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "16px",
              flexWrap: "wrap",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <span
                style={{
                  width: "10px",
                  height: "10px",
                  borderRadius: "50%",
                  backgroundColor:
                    dbError || !dbIngredients ? "#ef4444" : "#22c55e",
                  boxShadow:
                    dbError || !dbIngredients
                      ? "0 0 8px rgba(239,68,68,0.7)"
                      : "0 0 8px rgba(34,197,94,0.7)",
                  display: "inline-block",
                }}
              />
              <span
                style={{
                  fontSize: "11px",
                  color: "rgba(255,255,255,0.75)",
                  letterSpacing: "0.5px",
                }}
              >
                {dbError || !dbIngredients
                  ? t('databasePanel.disconnected')
                  : t('databasePanel.connected')}
              </span>
            </div>

            <div
              style={{ display: "flex", alignItems: "center", gap: "8px" }}
            >
              <span
                style={{ fontSize: "10px", color: "rgba(255,255,255,0.6)" }}
              >
                {t('databasePanel.formula')}
              </span>
              <select
                value={selectedFormula}
                onChange={(e) => setSelectedFormula(e.target.value)}
                disabled={!dbIngredients || sortedDbFormulas.length === 0}
                style={{
                  padding: "6px 10px",
                  backgroundColor: "rgba(139,92,246,0.2)",
                  border: "1px solid rgba(139,92,246,0.4)",
                  borderRadius: "6px",
                  color: "#ede9fe",
                  fontSize: "11px",
                  fontWeight: "600",
                  minWidth: "260px",
                  cursor: (!dbIngredients || sortedDbFormulas.length === 0) ? "not-allowed" : "pointer",
                  opacity: (!dbIngredients || sortedDbFormulas.length === 0) ? 0.6 : 1,
                }}
              >
                {!dbIngredients ? (
                  <option>{t('databasePanel.loadingFormulas')}</option>
                ) : sortedDbFormulas.length === 0 ? (
                  <option>{t('databasePanel.noFormulasYet')}</option>
                ) : (
                  sortedDbFormulas.map((f) => (
                    <option key={f.code} value={f.code}>
                      {f.code} - {f.displayName || "Untitled"}
                    </option>
                  ))
                )}
              </select>
                <button
                  type="button"
                  onClick={() => {
                    if (!selectedFormula || dbLoading) return;
                    if (!window.confirm(t('databasePanel.confirmRefresh'))) return;
                    setFormulaReloadCounter((c) => c + 1);
                  }}
                  disabled={!selectedFormula || dbLoading}
                  title={t('databasePanel.refreshTooltip')}
                  style={{
                    padding: "6px 10px",
                    backgroundColor: "rgba(139,92,246,0.2)",
                    border: "1px solid rgba(139,92,246,0.4)",
                    borderRadius: "6px",
                    color: "#ede9fe",
                    fontSize: "11px",
                    fontWeight: 600,
                    cursor: (!selectedFormula || dbLoading) ? "not-allowed" : "pointer",
                    opacity: (!selectedFormula || dbLoading) ? 0.5 : 1,
                  }}
                >
                  {dbLoading ? t('databasePanel.refreshing') : t('databasePanel.refresh')}
                </button>
              </div>
          </div>
          {dbIngredients && selectedFormula && (
            <div
              style={{
                marginTop: "10px",
                padding: "8px 12px",
                backgroundColor: "rgba(139,92,246,0.15)",
                borderRadius: "6px",
                fontSize: "10px",
                color: "rgba(255,255,255,0.7)",
              }}
            >
              {(() => {
                const f = sortedDbFormulas.find((x) => x.code === selectedFormula);
                return f ? (
                  <>
                    <strong>{t('databasePanel.formula')}</strong> {f.code} | <strong>{t('databasePanel.name')}</strong>{" "}
                    {f.displayName || "—"}
                  </>
                ) : null;
              })()}
            </div>
          )}
        </div>
      )}

      {/* Header */}
      <div style={{ textAlign: "center", marginBottom: "20px" }}>
        <h1
          style={{
            fontSize: "44px",
            fontWeight: "900",
            background:
              "linear-gradient(135deg, #c4b5fd 0%, #f0abfc 25%, #fcd34d 50%, #a78bfa 75%, #c4b5fd 100%)",
            backgroundSize: "300% 300%",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            animation: "shimmer 4s ease infinite",
            letterSpacing: "-2px",
            marginBottom: "4px",
          }}
        >
          PROTEIN PAL
        </h1>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "10px",
            marginTop: "8px",
          }}
        >
          <label
            style={{
              color: "rgba(255,255,255,0.6)",
              fontSize: "11px",
              letterSpacing: "1px",
              textTransform: "uppercase",
            }}
          >
            {t('formulation.nameYourSession')}
          </label>
          <input
            type="text"
            value={sessionName}
            onChange={(e) => setSessionName(e.target.value)}
            placeholder={t('formulation.leaveBlankAutoName')}
            style={{
              padding: "6px 12px",
              fontSize: "12px",
              borderRadius: "6px",
              border: "2px solid rgba(139,92,246,0.4)",
              backgroundColor: "rgba(255,255,255,0.1)",
              color: "rgba(255,255,255,0.9)",
              width: "250px",
              outline: "none",
            }}
          />
          {/* Same fork-to-new-F-code save as the post-process card button;
              surfaced here so it is reachable without scrolling. */}
          <button
            onClick={saveFormulaSettings}
            disabled={formulaSettingsSaving}
            style={{
              padding: "6px 16px",
              fontSize: "11px",
              fontWeight: 700,
              backgroundColor: formulaSettingsSaving ? "#93c5fd" : "#2563eb",
              color: "white",
              border: "none",
              borderRadius: "6px",
              cursor: formulaSettingsSaving ? "wait" : "pointer",
              whiteSpace: "nowrap",
            }}
          >
            {formulaSettingsSaving ? t('postProcess.saving') : t('postProcess.saveAsNew')}
          </button>
          {formulaSettingsMsg && (
            <span
              style={{
                fontSize: "11px",
                fontWeight: 600,
                color: formulaSettingsMsg.startsWith("Error") ? "#fca5a5" : "#86efac",
              }}
            >
              {formulaSettingsMsg}
            </span>
          )}
        </div>
      </div>

      {/* FORMULATION TAB */}
      {activeTab === "formulation" && (
        <>
          {/* Top Row */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "260px 340px 1fr",
              gap: "12px",
              maxWidth: "1500px",
              margin: "0 auto",
            }}
          >
            {/* Nutrition Overview */}
            <div className="card" ref={cardRefNutrition} style={{ position: "relative" }}>
              <DownloadButton targetRef={cardRefNutrition} formulaCode={sessionPrefix} sessionName={displaySessionName} cardName="Nutrition_Overview" />
              <h3
                style={{
                  fontSize: "12px",
                  fontWeight: "700",
                  color: "#1e1b4b",
                  marginBottom: "10px",
                  borderBottom: "2px solid #e5e7eb",
                  paddingBottom: "6px",
                }}
              >
                {t('formulation.nutritionOverview')}
                {postProcessEnabled && (
                  <span
                    style={{
                      marginLeft: "8px",
                      fontSize: "8px",
                      backgroundColor: "#fef3c7",
                      color: "#92400e",
                      padding: "2px 6px",
                      borderRadius: "3px",
                    }}
                  >
                    {t('formulation.postBakeBadge')}
                  </span>
                )}
              </h3>
              <div
                style={{
                  fontSize: "10px",
                  color: "#6b7280",
                  marginBottom: "8px",
                }}
              >
                {t('formulation.serving')}{" "}
                <input
                  lang="en"
                  type="text"
                  inputMode="decimal"
                  key={`serving-${servingSize}`}
                  defaultValue={String(servingSize)}
                  onBlur={(e) => {
                    const n = parseFloat(e.currentTarget.value);
                    if (!isFinite(n) || n <= 0) {
                      // Restore the formula's own value — never inject 84 over
                      // a cleared field (that default belongs to no formula).
                      e.currentTarget.value = String(servingSize);
                      return;
                    }
                    setServingSize(Math.min(2000, Math.max(1, n)));
                  }}
                  onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                  style={{
                    width: "45px",
                    padding: "2px 4px",
                    border: "1px solid #e5e7eb",
                    borderRadius: "4px",
                    fontSize: "10px",
                    marginLeft: "4px",
                  }}
                />
                g
                {postProcessEnabled && (
                  <span
                    style={{
                      marginLeft: "6px",
                      fontSize: "8px",
                      color: "#059669",
                    }}
                  >
                    {t('formulation.finished')}
                  </span>
                )}
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "6px",
                  marginBottom: "10px",
                }}
              >
                {(() => {
                  // Use the module-level nf which already handles post-process switching
                  return [
                    {
                      label: t('formulation.calories'),
                      value: fdaRound.calories(nf.caloriesPerServing),
                      color: "#7c3aed",
                    },
                    {
                      label: t('formulation.fat'),
                      value: `${fdaRound.fat(nf.fatPerServing)}g`,
                      color: "#f59e0b",
                    },
                    {
                      label: t('formulation.carbs'),
                      value: `${fdaRound.carbs(nf.carbsPerServing)}g`,
                      color: "#3b82f6",
                    },
                    {
                      label: t('formulation.fiber'),
                      value: `${fdaRound.fiber(nf.fiberPerServing)}g`,
                      color: "#22c55e",
                    },
                    {
                      label: t('formulation.protein'),
                      value: `${fdaRound.protein(nf.proteinPerServing)}g`,
                      color: "#ec4899",
                    },
                  ].map((n, i) => (
                    <div
                      key={i}
                      style={{
                        padding: "6px",
                        backgroundColor: "#f8fafc",
                        borderRadius: "6px",
                        borderLeft: `3px solid ${n.color}`,
                      }}
                    >
                      <div
                        style={{
                          fontSize: "8px",
                          color: "#6b7280",
                          textTransform: "uppercase",
                        }}
                      >
                        {n.label}
                      </div>
                      <div
                        style={{
                          fontSize: "16px",
                          fontWeight: "700",
                          color: "#1e1b4b",
                        }}
                      >
                        {n.value}
                      </div>
                    </div>
                  ));
                })()}
              </div>

              <div
                style={{
                  padding: "6px",
                  backgroundColor: "#eff6ff",
                  borderRadius: "6px",
                  marginBottom: "8px",
                  border: "1px solid #bfdbfe",
                }}
              >
                <div style={{ fontSize: "8px", color: "#1d4ed8" }}>
                  {t('formulation.nutrientDensity')}
                </div>
                <div
                  style={{
                    fontSize: "16px",
                    fontWeight: "700",
                    color: "#1d4ed8",
                  }}
                >
                  {(() => {
                    const density =
                      nf.caloriesPerServing > 0 ? ((nf.proteinPerServing + nf.fiberPerServing) / nf.caloriesPerServing) * 100 : 0;
                    return density.toFixed(1);
                  })()}
                </div>
                <div style={{ fontSize: "8px", color: "#2563eb" }}>
                  {t('formulation.proteinFiberPer100kcal')}
                </div>
              </div>

              <div
                style={{
                  padding: "8px",
                  background: (() => {
                    const percentDV = postProcessEnabled
                      ? postProcessed.adjustedPercentDV
                      : calculated.percentDV;
                    const proteinClaim =
                      percentDV >= 20
                        ? "Excellent Source"
                        : percentDV >= 10
                        ? "Good Source"
                        : "No Claim";
                    return proteinClaim === "Excellent Source"
                      ? "linear-gradient(135deg, #dcfce7, #bbf7d0)"
                      : proteinClaim === "Good Source"
                      ? "linear-gradient(135deg, #fef3c7, #fde68a)"
                      : "#f1f5f9";
                  })(),
                  borderRadius: "8px",
                  marginBottom: "6px",
                }}
              >
                <div style={{ fontSize: "8px", color: "#6b7280" }}>
                  {t('claims.proteinClaim')}
                </div>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "4px" }}
                >
                  {(() => {
                    const percentDV = postProcessEnabled
                      ? postProcessed.adjustedPercentDV
                      : calculated.percentDV;
                    const proteinClaim =
                      percentDV >= 20
                        ? "Excellent Source"
                        : percentDV >= 10
                        ? "Good Source"
                        : "No Claim";
                    const claimColor =
                      percentDV >= 20
                        ? "#15803d"
                        : percentDV >= 10
                        ? "#b45309"
                        : "#b91c1c";
                    const correctedProtein = postProcessEnabled
                      ? postProcessed.adjustedCorrectedProtein
                      : calculated.correctedProteinPerServing;
                    return (
                      <>
                        {proteinClaim === "Excellent Source"
                          ? Icons.check
                          : proteinClaim === "Good Source"
                          ? Icons.zap
                          : Icons.alert}
                        <span
                          style={{
                            fontSize: "12px",
                            fontWeight: "700",
                            color: claimColor,
                          }}
                        >
                          {t('claims.' + (proteinClaim === "Excellent Source" ? 'excellentSource' : proteinClaim === "Good Source" ? 'goodSource' : 'noClaim'))}
                        </span>
                      </>
                    );
                  })()}
                </div>
                <div style={{ fontSize: "10px", color: "#374151" }}>
                  <strong>
                    {fdaRound.protein(
                      postProcessEnabled
                        ? postProcessed.adjustedCorrectedProtein
                        : calculated.correctedProteinPerServing
                    )}
                    g
                  </strong>{" "}
                  {t('formulation.corrected')} •{" "}
                  {Math.round(
                    postProcessEnabled
                      ? postProcessed.adjustedPercentDV
                      : calculated.percentDV
                  )}
                  {t('formulation.pctDV')}
                </div>
              </div>

              <div
                style={{
                  padding: "8px",
                  background: (() => {
                    const fiberClaim =
                      nf.fiberPerServing >= 5
                        ? "Excellent Source"
                        : nf.fiberPerServing >= 2.5
                        ? "Good Source"
                        : "No Claim";
                    return fiberClaim === "Excellent Source"
                      ? "linear-gradient(135deg, #d1fae5, #a7f3d0)"
                      : fiberClaim === "Good Source"
                      ? "linear-gradient(135deg, #fef9c3, #fef08a)"
                      : "#f1f5f9";
                  })(),
                  borderRadius: "8px",
                }}
              >
                <div style={{ fontSize: "8px", color: "#6b7280" }}>
                  {t('claims.fiberClaim')}
                </div>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "4px" }}
                >
                  {(() => {
                    const fiberClaim =
                      nf.fiberPerServing >= 5
                        ? "Excellent Source"
                        : nf.fiberPerServing >= 2.5
                        ? "Good Source"
                        : "No Claim";
                    return (
                      <>
                        {fiberClaim === "Excellent Source"
                          ? Icons.check
                          : fiberClaim === "Good Source"
                          ? Icons.zap
                          : Icons.alert}
                        <span
                          style={{
                            fontSize: "12px",
                            fontWeight: "700",
                            color: fiberClaim.includes("Excellent")
                              ? "#059669"
                              : fiberClaim.includes("Good")
                              ? "#ca8a04"
                              : "#6b7280",
                          }}
                        >
                          {t('claims.' + (fiberClaim === "Excellent Source" ? 'excellentSource' : fiberClaim === "Good Source" ? 'goodSource' : 'noClaim'))}
                        </span>
                      </>
                    );
                  })()}
                </div>
                <div style={{ fontSize: "10px", color: "#374151" }}>
                  <strong>
                    {fdaRound.fiber(
                      postProcessEnabled
                        ? postProcessed.finishedNutritionals.fiber
                        : calculated.fiberPerServing
                    )}
                    g
                  </strong>{" "}
                  {t('formulation.perServing')}
                </div>
              </div>
            </div>

            {/* Composition Overview */}
            <div className="card" ref={cardRefComposition} style={{ position: "relative" }}>
              <DownloadButton targetRef={cardRefComposition} formulaCode={sessionPrefix} sessionName={displaySessionName} cardName="Composition_Overview" />
              <h3
                style={{
                  fontSize: "12px",
                  fontWeight: "700",
                  color: "#1e1b4b",
                  marginBottom: "10px",
                  borderBottom: "2px solid #e5e7eb",
                  paddingBottom: "6px",
                }}
              >
                {t('formulation.compositionOverview')}
              </h3>
              <div style={{ marginBottom: "12px" }}>
                {/* Ratio mode toggle - always visible */}
                <div style={{ display: "flex", justifyContent: "center", marginBottom: "6px", gap: "4px" }}>
                  <button
                    onClick={() => setRatioMode("as-mixed")}
                    style={{
                      fontSize: "8px",
                      padding: "2px 8px",
                      borderRadius: "4px 0 0 4px",
                      border: "1px solid #8b5cf6",
                      backgroundColor: ratioMode === "as-mixed" ? "#8b5cf6" : "white",
                      color: ratioMode === "as-mixed" ? "white" : "#8b5cf6",
                      cursor: "pointer",
                      fontWeight: "600",
                    }}
                  >
                    {t('formulation.asMixedRatio')}
                  </button>
                  <button
                    onClick={() => {
                      setRatioMode("finished");
                      // Auto-enable post-process when switching to finished ratio
                      if (!postProcessEnabled) {
                        setPostProcessEnabled(true);
                        setShowPostProcess(true);
                      }
                    }}
                    style={{
                      fontSize: "8px",
                      padding: "2px 8px",
                      borderRadius: "0 4px 4px 0",
                      border: "1px solid #0d9488",
                      backgroundColor: ratioMode === "finished" ? "#0d9488" : "white",
                      color: ratioMode === "finished" ? "white" : "#0d9488",
                      cursor: "pointer",
                      fontWeight: "600",
                    }}
                  >
                    {t('formulation.finishedRatio')}
                  </button>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: "10px",
                    marginBottom: "3px",
                  }}
                >
                  <span>
                    {t('formulation.doughLabel')} <strong>{doughRatio}%</strong>
                    {postProcessEnabled && ratioMode === "as-mixed" && (
                      <span style={{ fontSize: "8px", color: "#0d9488", marginLeft: "4px" }}>
                        (fin: {postProcessed.finishedDoughRatio.toFixed(1)}%)
                      </span>
                    )}
                  </span>
                  <span>
                    {t('formulation.fillingLabel')} <strong>{fillingRatio}%</strong>
                    {postProcessEnabled && ratioMode === "as-mixed" && (
                      <span style={{ fontSize: "8px", color: "#0d9488", marginLeft: "4px" }}>
                        (fin: {postProcessed.finishedFillingRatio.toFixed(1)}%)
                      </span>
                    )}
                  </span>
                </div>
                {postProcessEnabled && ratioMode === "finished" && (
                  <div style={{ fontSize: "8px", color: "#6b7280", marginBottom: "3px", textAlign: "center" }}>
                    {t('formulation.asMixed')}: {t('formulation.doughLabel')} {postProcessed.asMixedDoughRatio.toFixed(1)}% / {t('formulation.fillingLabel')} {postProcessed.asMixedFillingRatio.toFixed(1)}%
                  </div>
                )}
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={doughRatio}
                  onChange={(e) => setDoughRatio(parseInt(e.target.value))}
                  style={{
                    width: "100%",
                    color: "#8b5cf6",
                    background: `linear-gradient(to right, #8b5cf6 ${doughRatio}%, #ec4899 ${doughRatio}%)`,
                  }}
                />
              </div>

              {/* Piece Weight Section */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "8px",
                  marginBottom: "10px",
                }}
              >
                <div>
                  <div style={{ fontSize: "9px", color: "#6b7280" }}>
                    {t('formulation.pieceWeight')} (g)
                  </div>
                  <input
                    type="number"
                    min="0.1"
                    max="2000"
                    key={`piece-${pieceWeight}`}
                    defaultValue={pieceWeight}
                    step="0.1"
                    onBlur={(e) => {
                      const n = parseFloat(e.currentTarget.value);
                      if (!isFinite(n) || n <= 0) {
                        // Restore the formula's own value — clearing the field
                        // must not inject the unrelated 9.2 g default.
                        e.currentTarget.value = String(pieceWeight);
                        return;
                      }
                      setPieceWeight(Math.min(2000, Math.max(0.1, n)));
                    }}
                    onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                    style={{
                      width: "100%",
                      padding: "4px 6px",
                      border: "1px solid #e5e7eb",
                      borderRadius: "4px",
                      fontSize: "12px",
                    }}
                  />
                  {postProcessEnabled && (
                    <div
                      style={{
                        marginTop: "4px",
                        padding: "4px 6px",
                        backgroundColor: "#f0fdfa",
                        borderRadius: "4px",
                        border: "1px solid #5eead4",
                      }}
                    >
                      <div style={{ fontSize: "8px", color: "#0d9488" }}>
                        {t('formulation.asMixed')} (g)
                      </div>
                      <div
                        style={{
                          fontSize: "12px",
                          fontWeight: "700",
                          color: "#0f766e",
                        }}
                      >
                        {postProcessed.asMixedPieceWeight.toFixed(2)}
                      </div>
                    </div>
                  )}
                </div>
                <div
                  style={{
                    padding: "6px",
                    backgroundColor: "#f3e8ff",
                    borderRadius: "6px",
                  }}
                >
                  <div style={{ fontSize: "9px", color: "#7c3aed" }}>
                    {t('formulation.piecesPerServing')}
                  </div>
                  <div
                    style={{
                      fontSize: "16px",
                      fontWeight: "700",
                      color: "#5b21b6",
                    }}
                  >
                    {piecesPerServing}
                  </div>
                </div>
              </div>

              {/* Dough/Filling with Post-Process columns */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "6px",
                  marginBottom: "10px",
                }}
              >
                {/* DOUGH */}
                <div
                  style={{
                    padding: "6px",
                    backgroundColor: "#faf5ff",
                    borderRadius: "6px",
                    borderLeft: "3px solid #8b5cf6",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "4px",
                    }}
                  >
                    <div style={{ fontSize: "9px", color: "#6b7280" }}>
                      {t('formulation.dough')}
                    </div>
                    {postProcessEnabled && (
                      <div
                        style={{
                          fontSize: "7px",
                          color: "#0d9488",
                          fontWeight: "600",
                        }}
                      >
                        POST
                      </div>
                    )}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "baseline",
                    }}
                  >
                    <div style={{ fontSize: "13px", fontWeight: "700" }}>
                      {((pieceWeight * postProcessed.finishedDoughRatio) / 100).toFixed(1)}g/pc
                    </div>
                    {postProcessEnabled && (
                      <div
                        style={{
                          fontSize: "11px",
                          fontWeight: "700",
                          color: "#0f766e",
                        }}
                      >
                        {((postProcessed.asMixedPieceWeight * postProcessed.asMixedDoughRatio) / 100).toFixed(1)}
                        g mix
                      </div>
                    )}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "baseline",
                    }}
                  >
                    <div style={{ fontSize: "9px", color: "#8b5cf6" }}>
                      M: {calculated.doughMoisture.toFixed(0)}%
                    </div>
                    {postProcessEnabled && (
                      <div style={{ fontSize: "8px", color: "#0d9488" }}>
                        {postProcessed.moistureAdjustment.dough.finalMoisture.toFixed(0)}%
                      </div>
                    )}
                  </div>
                </div>
                {/* FILLING */}
                <div
                  style={{
                    padding: "6px",
                    backgroundColor: "#fdf2f8",
                    borderRadius: "6px",
                    borderLeft: "3px solid #ec4899",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "4px",
                    }}
                  >
                    <div style={{ fontSize: "9px", color: "#6b7280" }}>
                      {t('formulation.filling')}
                    </div>
                    {postProcessEnabled && (
                      <div
                        style={{
                          fontSize: "7px",
                          color: "#0d9488",
                          fontWeight: "600",
                        }}
                      >
                        POST
                      </div>
                    )}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "baseline",
                    }}
                  >
                    <div style={{ fontSize: "13px", fontWeight: "700" }}>
                      {((pieceWeight * postProcessed.finishedFillingRatio) / 100).toFixed(1)}g/pc
                    </div>
                    {postProcessEnabled && (
                      <div
                        style={{
                          fontSize: "11px",
                          fontWeight: "700",
                          color: "#0f766e",
                        }}
                      >
                        {((postProcessed.asMixedPieceWeight * postProcessed.asMixedFillingRatio) / 100).toFixed(1)}
                        g mix
                      </div>
                    )}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "baseline",
                    }}
                  >
                    <div style={{ fontSize: "9px", color: "#ec4899" }}>
                      M: {calculated.fillingMoisture.toFixed(0)}%
                    </div>
                    {postProcessEnabled && (
                      <div style={{ fontSize: "8px", color: "#0d9488" }}>
                        {postProcessed.moistureAdjustment.filling.finalMoisture.toFixed(0)}%
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Macronutrient Breakdown - 3 donuts */}
              <div style={{ marginBottom: "8px" }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "6px",
                  }}
                >
                  <span
                    style={{
                      fontSize: "9px",
                      fontWeight: "600",
                      color: "#6b7280",
                    }}
                  >
                    {t('formulation.macroBreakdown')}
                  </span>
                  {postProcessEnabled && (
                    <select
                      value={macroView}
                      onChange={(e) => setMacroView(e.target.value)}
                      style={{
                        fontSize: "8px",
                        padding: "2px 4px",
                        border: "1px solid #e5e7eb",
                        borderRadius: "3px",
                      }}
                    >
                      <option value="mixed">{t('formulation.asMixed')}</option>
                      <option value="postbake">{t('formulation.postBake')}</option>
                    </select>
                  )}
                </div>
                <ChartErrorBoundary fallbackHeight="70px">
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1fr",
                    gap: "4px",
                  }}
                >
                  {/* Whole Product */}
                  <div style={{ height: "70px", position: "relative" }}>
                    <div
                      style={{
                        fontSize: "7px",
                        textAlign: "center",
                        color: "#6b7280",
                      }}
                    >
                      {t('formulation.whole')}
                    </div>
                    <ResponsiveContainer width="100%" height="85%">
                      <PieChart>
                        <Pie
                          data={(() => {
                            const usePost = macroView === "postbake" && postProcessEnabled;
                            const fDR = postProcessed.finishedDoughRatio;
                            const fFR = postProcessed.finishedFillingRatio;
                            const finDoughM = postProcessed.moistureAdjustment.dough.finalMoisture;
                            const finFillM = postProcessed.moistureAdjustment.filling.finalMoisture;
                            const cf = postProcessed.concentrationFactor?.overall || 1;
                            return [
                              {
                                name: t('formulation.water'),
                                value: usePost
                                  ? (finDoughM * fDR) / 100 + (finFillM * fFR) / 100
                                  : calculated.totalMoisture,
                                color: "#60a5fa",
                              },
                              {
                                name: t('formulation.protein'),
                                value: usePost ? calculated.totalProtein * cf : calculated.totalProtein,
                                color: "#22c55e",
                              },
                              {
                                name: t('formulation.fat'),
                                value: usePost ? calculated.totalFat * cf : calculated.totalFat,
                                color: "#fcd34d",
                              },
                              {
                                name: t('formulation.carbs'),
                                value: usePost ? calculated.totalCarbs * cf : calculated.totalCarbs,
                                color: "#f59e0b",
                              },
                            ];
                          })()}
                          cx="50%"
                          cy="50%"
                          innerRadius={10}
                          outerRadius={22}
                          dataKey="value"
                        >
                          {[
                            { color: "#60a5fa" },
                            { color: "#22c55e" },
                            { color: "#fcd34d" },
                            { color: "#f59e0b" },
                          ].map((e, i) => (
                            <Cell key={i} fill={e.color} />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(value, name) => [
                            `${Number(value).toFixed(1)}g/100g`,
                            String(name),
                          ]}
                          contentStyle={{
                            fontSize: "9px",
                            padding: "4px 8px",
                            borderRadius: "4px",
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  {/* Dough */}
                  <div style={{ height: "70px", position: "relative" }}>
                    <div
                      style={{
                        fontSize: "7px",
                        textAlign: "center",
                        color: "#8b5cf6",
                      }}
                    >
                      {t('formulation.doughLabel')}
                    </div>
                    <ResponsiveContainer width="100%" height="85%">
                      <PieChart>
                        <Pie
                          data={(() => {
                            const usePost = macroView === "postbake" && postProcessEnabled;
                            const m = usePost
                              ? postProcessed.moistureAdjustment.dough.finalMoisture
                              : calculated.doughMoisture;
                            return [
                              {
                                name: t('formulation.water'),
                                value: m,
                                color: "#60a5fa",
                              },
                              {
                                name: t('formulation.dryMatter'),
                                value: 100 - m,
                                color: "#ede9fe",
                              },
                            ];
                          })()}
                          cx="50%"
                          cy="50%"
                          innerRadius={10}
                          outerRadius={22}
                          dataKey="value"
                        >
                          {[{ color: "#60a5fa" }, { color: "#c4b5fd" }].map(
                            (e, i) => (
                              <Cell key={i} fill={e.color} />
                            )
                          )}
                        </Pie>
                        <Tooltip
                          formatter={(value, name) => [
                            `${Number(value).toFixed(1)}%`,
                            String(name),
                          ]}
                          contentStyle={{
                            fontSize: "9px",
                            padding: "4px 8px",
                            borderRadius: "4px",
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  {/* Filling */}
                  <div style={{ height: "70px", position: "relative" }}>
                    <div
                      style={{
                        fontSize: "7px",
                        textAlign: "center",
                        color: "#ec4899",
                      }}
                    >
                      {t('formulation.fillingLabel')}
                    </div>
                    <ResponsiveContainer width="100%" height="85%">
                      <PieChart>
                        <Pie
                          data={(() => {
                            const usePost = macroView === "postbake" && postProcessEnabled;
                            const m = usePost
                              ? postProcessed.moistureAdjustment.filling.finalMoisture
                              : calculated.fillingMoisture;
                            return [
                              {
                                name: t('formulation.water'),
                                value: m,
                                color: "#60a5fa",
                              },
                              {
                                name: t('formulation.dryMatter'),
                                value: 100 - m,
                                color: "#f9a8d4",
                              },
                            ];
                          })()}
                          cx="50%"
                          cy="50%"
                          innerRadius={10}
                          outerRadius={22}
                          dataKey="value"
                        >
                          {[{ color: "#60a5fa" }, { color: "#f9a8d4" }].map(
                            (e, i) => (
                              <Cell key={i} fill={e.color} />
                            )
                          )}
                        </Pie>
                        <Tooltip
                          formatter={(value, name) => [
                            `${Number(value).toFixed(1)}%`,
                            String(name),
                          ]}
                          contentStyle={{
                            fontSize: "9px",
                            padding: "4px 8px",
                            borderRadius: "4px",
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                </ChartErrorBoundary>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: "8px",
                    fontSize: "7px",
                    marginTop: "2px",
                  }}
                >
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "2px",
                    }}
                  >
                    <span
                      style={{
                        width: "6px",
                        height: "6px",
                        backgroundColor: "#60a5fa",
                        borderRadius: "1px",
                      }}
                    ></span>
                    {t('formulation.water')}
                  </span>
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "2px",
                    }}
                  >
                    <span
                      style={{
                        width: "6px",
                        height: "6px",
                        backgroundColor: "#22c55e",
                        borderRadius: "1px",
                      }}
                    ></span>
                    {t('formulation.protein')}
                  </span>
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "2px",
                    }}
                  >
                    <span
                      style={{
                        width: "6px",
                        height: "6px",
                        backgroundColor: "#fcd34d",
                        borderRadius: "1px",
                      }}
                    ></span>
                    {t('formulation.fat')}
                  </span>
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "2px",
                    }}
                  >
                    <span
                      style={{
                        width: "6px",
                        height: "6px",
                        backgroundColor: "#f59e0b",
                        borderRadius: "1px",
                      }}
                    ></span>
                    {t('formulation.carbs')}
                  </span>
                </div>
              </div>

              {/* Mini AA Chart */}
              <ChartErrorBoundary fallbackHeight="60px">
              <div style={{ height: "60px", marginBottom: "4px" }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={calculated.chartData}
                    margin={{ top: 2, right: 2, left: 2, bottom: 12 }}
                  >
                    <XAxis
                      dataKey="name"
                      tick={{ fontSize: 7 }}
                      tickLine={false}
                      axisLine={false}
                    />
                    <Bar dataKey="total" radius={[2, 2, 0, 0]}>
                      {calculated.chartData.map((entry, i) => (
                        <Cell
                          key={i}
                          fill={getAAColor(
                            entry.score,
                            entry.isLimiting,
                            calculated.allAbove100
                          )}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              </ChartErrorBoundary>
              {(() => {
                // Match the AA chart card below: when post-processing is on,
                // banner reflects the adjusted (finished) scores, not as-mixed.
                const bannerLimitingAA = postProcessEnabled
                  ? postProcessed.adjustedLimitingAA
                  : calculated.limitingAA;
                const bannerLimitingScore = postProcessEnabled
                  ? postProcessed.adjustedLimitingScore
                  : calculated.limitingScore;
                const bannerAllAbove = postProcessEnabled
                  ? postProcessed.adjustedLimitingScore >= 0.999
                  : calculated.allAbove100;
                return (
              <div
                style={{
                  textAlign: "center",
                  padding: "4px 10px",
                  backgroundColor: bannerAllAbove
                    ? "#dbeafe"
                    : "#fef2f2",
                  borderRadius: "10px",
                  fontSize: "10px",
                  color: bannerAllAbove ? "#1d4ed8" : "#dc2626",
                  fontWeight: "600",
                  marginBottom: "10px",
                }}
              >
                {bannerAllAbove
                  ? `✓ ${t('formulation.allAaMet')}`
                  : `${t('formulation.limitingAA', { aa: AA_NAMES[bannerLimitingAA] })} (${(
                      bannerLimitingScore * 100
                    ).toFixed(0)}%)`}
              </div>
                );
              })()}

              {/* Protein Summary */}
              <div
                style={{
                  padding: "8px",
                  backgroundColor: "#f8fafc",
                  borderRadius: "8px",
                  border: "1px solid #e5e7eb",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "6px",
                  }}
                >
                  <span
                    style={{
                      fontSize: "9px",
                      fontWeight: "600",
                      color: "#6b7280",
                    }}
                  >
                    {t('formulation.proteinSummary')}
                  </span>
                  <select
                    value={proteinUnit}
                    onChange={(e) => setProteinUnit(e.target.value)}
                    style={{
                      fontSize: "8px",
                      padding: "2px 4px",
                      border: "1px solid #e5e7eb",
                      borderRadius: "3px",
                    }}
                  >
                    <option value="serving">{t('formulation.gPerServing')}</option>
                    <option value="100g">{t('formulation.gPer100g')}</option>
                  </select>
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1fr",
                    gap: "6px",
                    fontSize: "9px",
                  }}
                >
                  {(() => {
                    const useFinished = postProcessEnabled;
                    const uncorrected =
                      proteinUnit === "serving"
                        ? useFinished
                          ? postProcessed.finishedNutritionals.protein
                          : calculated.proteinPerServing
                        : useFinished
                        ? (postProcessed.finishedNutritionals.protein * 100) /
                          servingSize
                        : calculated.totalProtein;
                    const corrected =
                      proteinUnit === "serving"
                        ? useFinished
                          ? postProcessed.adjustedCorrectedProtein
                          : calculated.correctedProteinPerServing
                        : useFinished
                        ? (postProcessed.adjustedCorrectedProtein * 100) /
                          servingSize
                        : calculated.totalProtein * calculated.pdcaas;
                    const pdcaas = useFinished
                      ? postProcessed.adjustedPdcaas
                      : calculated.pdcaas;

                    return (
                      <>
                        <div>
                          <div style={{ color: "#6b7280" }}>{t('formulation.uncorrected')}</div>
                          <div style={{ fontWeight: "700", color: "#ec4899" }}>
                            {uncorrected.toFixed(2)}g
                          </div>
                        </div>
                        <div>
                          <div style={{ color: "#6b7280" }}>{t('formulation.corrected')}</div>
                          <div style={{ fontWeight: "700", color: "#7c3aed" }}>
                            {corrected.toFixed(2)}g
                          </div>
                        </div>
                        <div>
                          <div style={{ color: "#6b7280" }}>PDCAAS</div>
                          <div style={{ fontWeight: "700", color: "#059669" }}>
                            {(pdcaas * 100).toFixed(1)}%
                          </div>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>

            {/* Formulation */}
            <div
              className="card"
              ref={cardRefFormulationPct}
              style={{
                maxHeight: "900px",
                overflowY: "auto",
                overflowX: "visible",
                paddingBottom: "160px",
                position: "relative",
              }}
            >
              <DownloadButton targetRef={cardRefFormulationPct} formulaCode={sessionPrefix} sessionName={displaySessionName} cardName="Formulation_Pct" />
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "12px",
                  borderBottom: "2px solid #e5e7eb",
                  paddingBottom: "8px",
                }}
              >
                <h3
                  style={{
                    fontSize: "13px",
                    fontWeight: "700",
                    color: "#1e1b4b",
                    margin: 0,
                  }}
                >
                  {t('formulation.formulationPctRecipe')}
                </h3>
                <select
                  value={viewMode}
                  onChange={(e) => setViewMode(e.target.value)}
                  style={{
                    fontSize: "10px",
                    padding: "3px 8px",
                    border: "1px solid #e5e7eb",
                    borderRadius: "4px",
                  }}
                >
                  <option value="component">{t('formulation.pctOfComponent')}</option>
                  <option value="total">{t('formulation.pctOfTotalRecipe')}</option>
                </select>
              </div>

              {/* DOUGH */}
              <div style={{ marginBottom: "18px" }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "8px",
                  }}
                >
                  <span
                    style={{
                      fontSize: "11px",
                      fontWeight: "700",
                      color: "#8b5cf6",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    {t('formulation.doughLabel')} ({doughRatio}%)
                  </span>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "10px",
                        color:
                          Math.abs(doughTotal - 100) < 0.5
                            ? "#16a34a"
                            : "#dc2626",
                        fontWeight: "600",
                      }}
                    >
                      {t('common.total')}: {getDisplayPct(doughTotal, "dough").toFixed(1)}%{viewMode === "total" ? ` / ${doughRatio}%` : ""}
                    </span>
                    <button
                      onClick={() => snapTo100("dough")}
                      style={{
                        fontSize: "9px",
                        padding: "3px 8px",
                        backgroundColor: "#f3e8ff",
                        border: "1px solid #c4b5fd",
                        borderRadius: "4px",
                        color: "#7c3aed",
                        cursor: "pointer",
                      }}
                    >
                      {t('formulation.snap100')}
                    </button>
                  </div>
                </div>
                {Object.entries(doughFormula).map(([name, pct]) => (
                  <div
                    key={name}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      marginBottom: "8px",
                    }}
                  >
                    <span
                      style={{
                        width: "10px",
                        height: "10px",
                        backgroundColor: ALL_INGREDIENTS[name]?.color || "#ccc",
                        borderRadius: "2px",
                        flexShrink: 0,
                      }}
                    ></span>
                    <span
                      onClick={() => {
                        const ingData = ALL_INGREDIENTS[name];
                        setIngredientDetailName(name);
                        setEditingDataSources(ingData?.data_sources ? { ...ingData.data_sources } : {});
                        setDataSourceSaveMsg("");
                        setShowAllSources(false);
                      }}
                      style={{
                        fontSize: "11px",
                        fontWeight: "500",
                        flex: 1,
                        minWidth: "90px",
                        cursor: "pointer",
                        textDecoration: "underline",
                        textDecorationColor: "#d1d5db",
                        textUnderlineOffset: "2px",
                      }}
                      title={t('formulation.clickToViewIngredient')}
                    >
                      {ingName(name)}
                    </span>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      step="0.5"
                      value={getDisplayPct(pct, "dough")}
                      disabled={viewMode === "total" && doughRatio <= 0}
                      onChange={(e) => handleDoughChange(name, displayToComponentPct(e.target.value, "dough"))}
                      style={{
                        flex: 2,
                        color: ALL_INGREDIENTS[name]?.color || "#8b5cf6",
                        background: `linear-gradient(to right, ${
                          ALL_INGREDIENTS[name]?.color || "#8b5cf6"
                        } ${getDisplayPct(pct, "dough")}%, #e5e7eb ${getDisplayPct(pct, "dough")}%)`,
                      }}
                    />
                    <input
                      lang="en"
                      type="text"
                      inputMode="decimal"
                      key={`dough-pct-${name}-${Number(getDisplayPct(pct, "dough").toFixed(2))}`}
                      defaultValue={String(Number(getDisplayPct(pct, "dough").toFixed(2)))}
                      disabled={viewMode === "total" && doughRatio <= 0}
                      onBlur={(e) => commitIngredientPct("dough", name, e.currentTarget)}
                      onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                      style={{
                        width: "52px",
                        padding: "3px 6px",
                        fontSize: "11px",
                        fontWeight: "700",
                        textAlign: "right",
                        border: "1px solid #e5e7eb",
                        borderRadius: "4px",
                        background: "#f8fafc",
                      }}
                    />
                    <span style={{ fontSize: "10px", color: "#6b7280" }}>
                      %
                    </span>
                    <button
                      onClick={() => removeIngredient("dough", name)}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: "#dc2626",
                        opacity: 0.5,
                        padding: "2px",
                      }}
                    >
                      {Icons.trash}
                    </button>
                  </div>
                ))}
                {/* Water Post Process - only shown when post-process enabled */}
                {postProcessEnabled && doughFormula["Water"] !== undefined && (
                  <div
                    title={t('formulation.waterPostProcessHint')}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      marginBottom: "6px",
                      padding: "4px 0",
                      borderTop: "1px dashed #14b8a6",
                      marginTop: "4px",
                    }}
                  >
                    <span
                      style={{
                        width: "8px",
                        height: "8px",
                        backgroundColor: "#14b8a6",
                        borderRadius: "2px",
                        flexShrink: 0,
                      }}
                    ></span>
                    <span
                      style={{
                        fontSize: "10px",
                        fontWeight: "500",
                        flex: 1,
                        minWidth: "90px",
                        color: "#0d9488",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      {t('formulation.waterPostProcess')} {Icons.lock}
                    </span>
                    <div
                      style={{
                        flex: 2,
                        height: "5px",
                        borderRadius: "3px",
                        background: `linear-gradient(to right, #14b8a6 ${Math.max(
                          0,
                          postProcessed.moistureAdjustment.dough
                            .adjustedWaterPct
                        )}%, #e5e7eb ${Math.max(
                          0,
                          postProcessed.moistureAdjustment.dough
                            .adjustedWaterPct
                        )}%)`,
                      }}
                    ></div>
                    <span
                      style={{
                        fontSize: "10px",
                        fontWeight: "600",
                        width: "50px",
                        textAlign: "right",
                        color:
                          postProcessed.moistureAdjustment.dough
                            .adjustedWaterPct < 0
                            ? "#dc2626"
                            : "#0d9488",
                      }}
                    >
                      {postProcessed.moistureAdjustment.dough.adjustedWaterPct.toFixed(
                        1
                      )}
                      %
                    </span>
                    <span style={{ width: "18px" }}></span>
                  </div>
                )}
                <div style={{ position: "relative" }}>
                  <button
                    onClick={(e) => {
                      if (showAddDough) { setShowAddDough(false); return; }
                      const r = e.currentTarget.getBoundingClientRect();
                      setDoughPickerPos({ top: r.bottom + 4, left: r.left });
                      setShowAddDough(true);
                    }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "3px",
                      padding: "3px 8px",
                      backgroundColor: "#f3e8ff",
                      border: "1px dashed #c4b5fd",
                      borderRadius: "4px",
                      color: "#7c3aed",
                      fontSize: "9px",
                      cursor: "pointer",
                    }}
                  >
                    {Icons.plus} {t('formulation.add')}
                  </button>
                  {showAddDough && doughPickerPos && (
                    <IngredientPicker
                      ingredients={pickerIngredients.filter((i) => !doughFormula.hasOwnProperty(i.common_name))}
                      position={doughPickerPos}
                      currentValue={null}
                      onSelect={(id) => {
                        const name = idToCommonName[id] || id;
                        addIngredient("dough", name);
                        setShowAddDough(false);
                      }}
                      onClose={() => setShowAddDough(false)}
                    />
                  )}
                </div>
              </div>

              {/* FILLING */}
              <div style={{ marginBottom: "24px" }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "8px",
                  }}
                >
                  <span
                    style={{
                      fontSize: "11px",
                      fontWeight: "700",
                      color: "#ec4899",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    {t('formulation.fillingLabel')} ({fillingRatio}%)
                  </span>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "10px",
                        color:
                          Math.abs(fillingTotal - 100) < 0.5
                            ? "#16a34a"
                            : "#dc2626",
                        fontWeight: "600",
                      }}
                    >
                      {t('common.total')}: {getDisplayPct(fillingTotal, "filling").toFixed(1)}%{viewMode === "total" ? ` / ${fillingRatio}%` : ""}
                    </span>
                    <button
                      onClick={() => snapTo100("filling")}
                      style={{
                        fontSize: "9px",
                        padding: "3px 8px",
                        backgroundColor: "#fdf2f8",
                        border: "1px solid #f9a8d4",
                        borderRadius: "4px",
                        color: "#db2777",
                        cursor: "pointer",
                      }}
                    >
                      {t('formulation.snap100')}
                    </button>
                  </div>
                </div>
                {Object.entries(fillingFormula).map(([name, pct]) => (
                  <div
                    key={name}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      marginBottom: "8px",
                    }}
                  >
                    <span
                      style={{
                        width: "10px",
                        height: "10px",
                        backgroundColor: ALL_INGREDIENTS[name]?.color || "#ccc",
                        borderRadius: "2px",
                        flexShrink: 0,
                      }}
                    ></span>
                    <span
                      onClick={() => {
                        const ingData = ALL_INGREDIENTS[name];
                        setIngredientDetailName(name);
                        setEditingDataSources(ingData?.data_sources ? { ...ingData.data_sources } : {});
                        setDataSourceSaveMsg("");
                        setShowAllSources(false);
                      }}
                      style={{
                        fontSize: "11px",
                        fontWeight: "500",
                        flex: 1,
                        minWidth: "90px",
                        cursor: "pointer",
                        textDecoration: "underline",
                        textDecorationColor: "#d1d5db",
                        textUnderlineOffset: "2px",
                      }}
                      title={t('formulation.clickToViewIngredient')}
                    >
                      {ingName(name)}
                    </span>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      step="0.5"
                      value={getDisplayPct(pct, "filling")}
                      disabled={viewMode === "total" && fillingRatio <= 0}
                      onChange={(e) =>
                        handleFillingChange(name, displayToComponentPct(e.target.value, "filling"))
                      }
                      style={{
                        flex: 2,
                        color: ALL_INGREDIENTS[name]?.color || "#ec4899",
                        background: `linear-gradient(to right, ${
                          ALL_INGREDIENTS[name]?.color || "#ec4899"
                        } ${getDisplayPct(pct, "filling")}%, #e5e7eb ${getDisplayPct(pct, "filling")}%)`,
                      }}
                    />
                    <input
                      lang="en"
                      type="text"
                      inputMode="decimal"
                      key={`filling-pct-${name}-${Number(getDisplayPct(pct, "filling").toFixed(2))}`}
                      defaultValue={String(Number(getDisplayPct(pct, "filling").toFixed(2)))}
                      disabled={viewMode === "total" && fillingRatio <= 0}
                      onBlur={(e) => commitIngredientPct("filling", name, e.currentTarget)}
                      onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                      style={{
                        width: "52px",
                        padding: "3px 6px",
                        fontSize: "11px",
                        fontWeight: "700",
                        textAlign: "right",
                        border: "1px solid #e5e7eb",
                        borderRadius: "4px",
                        background: "#f8fafc",
                      }}
                    />
                    <span style={{ fontSize: "10px", color: "#6b7280" }}>
                      %
                    </span>
                    <button
                      onClick={() => removeIngredient("filling", name)}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: "#dc2626",
                        opacity: 0.5,
                        padding: "2px",
                      }}
                    >
                      {Icons.trash}
                    </button>
                  </div>
                ))}
                {/* Water Post Process - only shown when post-process enabled */}
                {postProcessEnabled &&
                  fillingFormula["Water"] !== undefined && (
                    <div
                      title={t('formulation.waterPostProcessHint')}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        marginBottom: "6px",
                        padding: "4px 0",
                        borderTop: "1px dashed #14b8a6",
                        marginTop: "4px",
                      }}
                    >
                      <span
                        style={{
                          width: "8px",
                          height: "8px",
                          backgroundColor: "#14b8a6",
                          borderRadius: "2px",
                          flexShrink: 0,
                        }}
                      ></span>
                      <span
                        style={{
                          fontSize: "10px",
                          fontWeight: "500",
                          flex: 1,
                          minWidth: "90px",
                          color: "#0d9488",
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                      >
                        {t('formulation.waterPostProcess')} {Icons.lock}
                      </span>
                      <div
                        style={{
                          flex: 2,
                          height: "5px",
                          borderRadius: "3px",
                          background: `linear-gradient(to right, #14b8a6 ${Math.max(
                            0,
                            postProcessed.moistureAdjustment.filling
                              .adjustedWaterPct
                          )}%, #e5e7eb ${Math.max(
                            0,
                            postProcessed.moistureAdjustment.filling
                              .adjustedWaterPct
                          )}%)`,
                        }}
                      ></div>
                      <span
                        style={{
                          fontSize: "10px",
                          fontWeight: "600",
                          width: "50px",
                          textAlign: "right",
                          color:
                            postProcessed.moistureAdjustment.filling
                              .adjustedWaterPct < 0
                              ? "#dc2626"
                              : "#0d9488",
                        }}
                      >
                        {postProcessed.moistureAdjustment.filling.adjustedWaterPct.toFixed(
                          1
                        )}
                        %
                      </span>
                      <span style={{ width: "18px" }}></span>
                    </div>
                  )}
                <div style={{ position: "relative" }}>
                  <button
                    onClick={(e) => {
                      if (showAddFilling) { setShowAddFilling(false); return; }
                      const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
                      setFillingPickerPos({ top: r.bottom + 4, left: r.left });
                      setShowAddFilling(true);
                    }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "3px",
                      padding: "3px 8px",
                      backgroundColor: "#fdf2f8",
                      border: "1px dashed #f9a8d4",
                      borderRadius: "4px",
                      color: "#db2777",
                      fontSize: "9px",
                      cursor: "pointer",
                    }}
                  >
                    {Icons.plus} {t('formulation.add')}
                  </button>
                  {showAddFilling && fillingPickerPos && (
                    <IngredientPicker
                      ingredients={pickerIngredients.filter((i) => !fillingFormula.hasOwnProperty(i.common_name))}
                      position={fillingPickerPos}
                      currentValue={null}
                      onSelect={(id) => {
                        const name = idToCommonName[id] || id;
                        addIngredient("filling", name);
                        setShowAddFilling(false);
                      }}
                      onClose={() => setShowAddFilling(false)}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Essential Amino Acid Profile - FULL STACKED CHART */}
          <div
            className="card"
            ref={cardRefAminoAcid}
            style={{ maxWidth: "1500px", margin: "20px auto", position: "relative" }}
          >
            <DownloadButton targetRef={cardRefAminoAcid} formulaCode={sessionPrefix} sessionName={displaySessionName} cardName="Amino_Acid_Chart" />
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "10px",
              }}
            >
              <h3
                style={{
                  fontSize: "12px",
                  fontWeight: "700",
                  color: "#1e1b4b",
                  margin: 0,
                }}
              >
                {t('formulation.essentialAminoAcidProfile')}
              </h3>
              {(() => {
                const limitingAA = postProcessEnabled
                  ? postProcessed.adjustedLimitingAA
                  : calculated.limitingAA;
                const limitingScore = postProcessEnabled
                  ? postProcessed.adjustedLimitingScore
                  : calculated.limitingScore;
                const allAbove = postProcessEnabled
                  ? postProcessed.adjustedLimitingScore >= 0.999
                  : calculated.allAbove100;
                return (
                  <div
                    style={{
                      padding: "4px 12px",
                      backgroundColor: allAbove ? "#dbeafe" : "#fef2f2",
                      borderRadius: "12px",
                      fontSize: "10px",
                      color: allAbove ? "#1d4ed8" : "#dc2626",
                      fontWeight: "600",
                    }}
                  >
                    {allAbove
                      ? `✓ ${t('formulation.allAaMet')}`
                      : `${t('formulation.limitingAA', { aa: AA_FULL_NAMES[limitingAA] })} (${(
                          limitingScore * 100
                        ).toFixed(0)}%)`}
                    {postProcessEnabled && (
                      <span
                        style={{
                          marginLeft: "6px",
                          fontSize: "8px",
                          backgroundColor: "rgba(251,191,36,0.3)",
                          padding: "2px 4px",
                          borderRadius: "3px",
                        }}
                      >
                        {t('formulation.postBakeBadge')}
                      </span>
                    )}
                  </div>
                );
              })()}
            </div>
            <ChartErrorBoundary fallbackHeight="280px">
            <div style={{ height: "280px", position: "relative" }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={calculated.chartData}
                  margin={{ top: 15, right: 20, left: 15, bottom: 35 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 10, fill: "#6b7280" }}
                  />
                  <YAxis
                    tick={{ fontSize: 9, fill: "#6b7280" }}
                    label={{
                      value: t('formulation.mgPerGProtein'),
                      angle: -90,
                      position: "insideLeft",
                      fontSize: 9,
                      fill: "#6b7280",
                    }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: "10px" }} formatter={(value) => <span style={{ color: "#374151" }}>{value}</span>} />
                  {/* Stacked ingredient bars */}
                  {Object.entries(calculated.proteinContributions).map(
                    ([name, data]) => (
                      <Bar
                        key={name}
                        dataKey={name}
                        stackId="a"
                        fill={data.color}
                        name={ingName(name)}
                      />
                    )
                  )}
                </BarChart>
              </ResponsiveContainer>
              {/* Star overlays for degraded amino acids */}
              {postProcessEnabled &&
                (() => {
                  const affectedAAs = [
                    "lysine",
                    "methionine_cysteine",
                    "tryptophan",
                  ];
                  const chartWidth = 1500 - 40; // approximate chart width
                  const totalAAs = calculated.chartData.length;
                  const barWidth = chartWidth / totalAAs / 1.5; // approximate bar spacing

                  return calculated.chartData.map((dataPoint, idx) => {
                    const aaKey = Object.keys(AA_NAMES).find(
                      (k) => AA_NAMES[k] === dataPoint.name
                    );
                    if (!affectedAAs.includes(aaKey)) return null;

                    const xPosition = 35 + (idx + 0.5) * (chartWidth / totalAAs);

                    return (
                      <div
                        key={aaKey}
                        style={{
                          position: "absolute",
                          left: `${xPosition}px`,
                          top: "8px",
                          fontSize: "16px",
                          pointerEvents: "none",
                          zIndex: 10,
                          textShadow: "0 0 3px white, 0 0 5px white",
                        }}
                      >
                        ⭐
                      </div>
                    );
                  });
                })()}
            </div>
            </ChartErrorBoundary>
            <div
              style={{
                marginTop: "8px",
                display: "flex",
                flexWrap: "wrap",
                gap: "6px",
                justifyContent: "center",
                fontSize: "9px",
                color: "#6b7280",
              }}
            >
              <span style={{ fontWeight: "600" }}>{t('formulation.reference')}</span>
              {(() => {
                const limitingAA = postProcessEnabled
                  ? postProcessed.adjustedLimitingAA
                  : calculated.limitingAA;
                const allAbove = postProcessEnabled
                  ? postProcessed.adjustedLimitingScore >= 0.999
                  : calculated.allAbove100;
                return Object.entries(REFERENCE_PATTERN).map(([aa, val]) => (
                  <span
                    key={aa}
                    style={{
                      padding: "2px 5px",
                      backgroundColor:
                        limitingAA === aa && !allAbove
                          ? "#fef2f2"
                          : allAbove
                          ? "#dbeafe"
                          : "#f8fafc",
                      borderRadius: "3px",
                      color:
                        limitingAA === aa && !allAbove
                          ? "#dc2626"
                          : allAbove
                          ? "#1d4ed8"
                          : "#6b7280",
                      fontWeight: limitingAA === aa ? "700" : "400",
                    }}
                  >
                    {AA_NAMES[aa]}: {val}
                  </span>
                ));
              })()}
            </div>
            {postProcessEnabled && (
              <div
                style={{
                  marginTop: "8px",
                  padding: "6px 10px",
                  backgroundColor: "#fef3c7",
                  borderRadius: "6px",
                  fontSize: "9px",
                  color: "#92400e",
                  textAlign: "center",
                }}
              >
                ⭐ {t('formulation.maillardNote')} (
                {t(MAILLARD_METHOD_I18N[consumerPrepMethod] || "postProcess.noAdjust")})
              </div>
            )}
          </div>

          {/* Protein Claim Analysis */}
          <div
            className="card"
            ref={cardRefProteinClaim}
            style={{ maxWidth: "1500px", margin: "20px auto", position: "relative" }}
          >
            <DownloadButton targetRef={cardRefProteinClaim} formulaCode={sessionPrefix} sessionName={displaySessionName} cardName="Protein_Claim" />
            <h3
              style={{
                fontSize: "12px",
                fontWeight: "700",
                color: "#1e1b4b",
                marginBottom: "12px",
              }}
            >
              {t('formulation.proteinClaimAnalysis')}
              {postProcessEnabled && (
                <span
                  style={{
                    marginLeft: "8px",
                    fontSize: "9px",
                    backgroundColor: "#fef3c7",
                    color: "#92400e",
                    padding: "2px 6px",
                    borderRadius: "3px",
                  }}
                >
                  {t('formulation.postBakeBadge')}
                </span>
              )}
            </h3>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(6, 1fr)",
                gap: "10px",
              }}
            >
              {(() => {
                const proteinPerServing = postProcessEnabled
                  ? postProcessed.finishedNutritionals.protein
                  : calculated.proteinPerServing;
                const correctedProtein = postProcessEnabled
                  ? postProcessed.adjustedCorrectedProtein
                  : calculated.correctedProteinPerServing;
                const percentDV = postProcessEnabled
                  ? postProcessed.adjustedPercentDV
                  : calculated.percentDV;
                const pdcaas = postProcessEnabled
                  ? postProcessed.adjustedPdcaas
                  : calculated.pdcaas;
                const claimColor =
                  percentDV >= 20
                    ? "#15803d"
                    : percentDV >= 10
                    ? "#b45309"
                    : "#b91c1c";
                const proteinClaim =
                  percentDV >= 20
                    ? "Excellent Source"
                    : percentDV >= 10
                    ? "Good Source"
                    : "No Claim";

                return (
                  <>
                    <div
                      style={{
                        padding: "10px",
                        background: `linear-gradient(135deg, ${claimColor}20, ${claimColor}30)`,
                        borderRadius: "8px",
                        border: `2px solid ${claimColor}50`,
                        gridColumn: "span 2",
                      }}
                    >
                      <div style={{ fontSize: "9px", color: "#6b7280" }}>
                        {t('claims.claimStatus')}
                      </div>
                      <div
                        style={{
                          fontSize: "18px",
                          fontWeight: "800",
                          color: claimColor,
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                        }}
                      >
                        {proteinClaim === "Excellent Source"
                          ? Icons.check
                          : proteinClaim === "Good Source"
                          ? Icons.zap
                          : Icons.alert}{" "}
                        {t('claims.' + (proteinClaim === "Excellent Source" ? 'excellentSource' : proteinClaim === "Good Source" ? 'goodSource' : 'noClaim'))}
                      </div>
                    </div>
                    <div
                      style={{
                        padding: "8px",
                        backgroundColor: "#f8fafc",
                        borderRadius: "6px",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "8px",
                          color: "#6b7280",
                          textTransform: "uppercase",
                        }}
                      >
                        {t('formulation.uncorrectedProtein')}
                      </div>
                      <div
                        style={{
                          fontSize: "18px",
                          fontWeight: "700",
                          color: "#1e1b4b",
                        }}
                      >
                        {proteinPerServing.toFixed(1)}g
                      </div>
                      <div style={{ fontSize: "8px", color: "#6b7280" }}>
                        {t('formulation.perServing')}
                      </div>
                    </div>
                    <div
                      style={{
                        padding: "8px",
                        backgroundColor: "#f8fafc",
                        borderRadius: "6px",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "8px",
                          color: "#6b7280",
                          textTransform: "uppercase",
                        }}
                      >
                        {t('formulation.pdcaasCorrected')}
                      </div>
                      <div
                        style={{
                          fontSize: "18px",
                          fontWeight: "700",
                          color: "#1e1b4b",
                        }}
                      >
                        {correctedProtein.toFixed(1)}g
                      </div>
                      <div style={{ fontSize: "8px", color: "#6b7280" }}>
                        {t('formulation.forDVCalc')}
                      </div>
                    </div>
                    <div
                      style={{
                        padding: "8px",
                        backgroundColor: "#f8fafc",
                        borderRadius: "6px",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "8px",
                          color: "#6b7280",
                          textTransform: "uppercase",
                        }}
                      >
                        {t('formulation.dailyValue')}
                      </div>
                      <div
                        style={{
                          fontSize: "18px",
                          fontWeight: "700",
                          color: "#1e1b4b",
                        }}
                      >
                        {percentDV.toFixed(0)}%
                      </div>
                      <div style={{ fontSize: "8px", color: "#6b7280" }}>
                        {t('formulation.of50gDRV')}
                      </div>
                    </div>
                    <div
                      style={{
                        padding: "8px",
                        backgroundColor: "#f8fafc",
                        borderRadius: "6px",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "8px",
                          color: "#6b7280",
                          textTransform: "uppercase",
                        }}
                      >
                        {t('formulation.pdcaasScore')}
                      </div>
                      <div
                        style={{
                          fontSize: "18px",
                          fontWeight: "700",
                          color: "#1e1b4b",
                        }}
                      >
                        {(pdcaas * 100).toFixed(1)}%
                      </div>
                      <div style={{ fontSize: "8px", color: "#6b7280" }}>
                        {t('formulation.truncated')}
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>

          {/* Post-Process Adjustments Panel */}
          <div
            className="card"
            ref={cardRefPostProcess}
            style={{ maxWidth: "1500px", margin: "20px auto", position: "relative" }}
          >
            <DownloadButton targetRef={cardRefPostProcess} formulaCode={sessionPrefix} sessionName={displaySessionName} cardName="Post_Process" />
            <button
              onClick={() => setShowPostProcess(!showPostProcess)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                width: "100%",
                padding: "8px 0",
                background: "none",
                border: "none",
                cursor: "pointer",
                fontSize: "12px",
                fontWeight: "700",
                color: "#1e1b4b",
                textAlign: "left",
              }}
            >
              <span
                style={{
                  transform: showPostProcess
                    ? "rotate(180deg)"
                    : "rotate(0deg)",
                  transition: "transform 0.2s",
                }}
              >
                {Icons.chevron}
              </span>
              <span
                style={{ display: "flex", alignItems: "center", gap: "6px" }}
              >
                {Icons.settings} {t('postProcess.title')}
              </span>
              <span
                style={{
                  marginLeft: "auto",
                  fontSize: "9px",
                  padding: "3px 8px",
                  backgroundColor: postProcessEnabled ? "#dcfce7" : "#f1f5f9",
                  color: postProcessEnabled ? "#16a34a" : "#6b7280",
                  borderRadius: "8px",
                  fontWeight: "600",
                }}
              >
                {postProcessEnabled ? t('postProcess.active') : t('postProcess.off')}
              </span>
            </button>

            {showPostProcess && (
              <div style={{ marginTop: "12px" }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    marginBottom: "16px",
                    padding: "12px",
                    backgroundColor: "#f8fafc",
                    borderRadius: "8px",
                  }}
                >
                  <button
                    onClick={() => setPostProcessEnabled(!postProcessEnabled)}
                    role="switch"
                    aria-checked={postProcessEnabled}
                    aria-label={t('postProcess.enableCalc')}
                    style={{
                      width: "48px",
                      height: "24px",
                      borderRadius: "12px",
                      border: "none",
                      cursor: "pointer",
                      backgroundColor: postProcessEnabled
                        ? "#22c55e"
                        : "#d1d5db",
                      position: "relative",
                      transition: "background-color 0.2s",
                    }}
                  >
                    <span
                      style={{
                        position: "absolute",
                        top: "2px",
                        left: postProcessEnabled ? "26px" : "2px",
                        width: "20px",
                        height: "20px",
                        borderRadius: "10px",
                        backgroundColor: "white",
                        transition: "left 0.2s",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                      }}
                    ></span>
                  </button>
                  <div>
                    <div
                      style={{
                        fontSize: "11px",
                        fontWeight: "600",
                        color: "#1e1b4b",
                      }}
                    >
                      {t('postProcess.enableCalc')}
                    </div>
                    <div style={{ fontSize: "9px", color: "#6b7280" }}>
                      {t('postProcess.enableDesc')}
                    </div>
                  </div>
                </div>

                {postProcessEnabled && (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "16px",
                    }}
                  >
                    {/* Moisture Section - Teal theme */}
                    <div
                      style={{
                        padding: "14px",
                        backgroundColor: "#f0fdfa",
                        borderRadius: "10px",
                        border: "1px solid #5eead4",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          marginBottom: "12px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <span style={{ color: "#0d9488" }}>
                            {Icons.droplet}
                          </span>
                          <div>
                            <div
                              style={{
                                fontSize: "11px",
                                fontWeight: "700",
                                color: "#134e4a",
                              }}
                            >
                              {t('postProcess.finalMoisture')}
                            </div>
                            <div style={{ fontSize: "8px", color: "#0f766e" }}>
                              {t('postProcess.enterFinalMoisture')}
                            </div>
                          </div>
                        </div>
                        <select
                          value={moistureMode}
                          onChange={(e) => {
                            const next = e.target.value;
                            if (next === "whole" && moistureMode === "components") {
                              const dM = postProcessed.moistureAdjustment.dough.finalMoisture;
                              const fM = postProcessed.moistureAdjustment.filling.finalMoisture;
                              const amDR = postProcessed.asMixedDoughRatio;
                              const amFR = postProcessed.asMixedFillingRatio;
                              const composite = (dM * amDR + fM * amFR) / 100;
                              setTargetWholeMoisture(Math.max(0, Math.min(80, Number(composite.toFixed(1)))));
                            } else if (next === "components" && moistureMode === "whole") {
                              const compositeAsMixed =
                                (calculated.doughMoisture * postProcessed.asMixedDoughRatio +
                                  calculated.fillingMoisture * postProcessed.asMixedFillingRatio) / 100;
                              if (compositeAsMixed > 0) {
                                const scale = targetWholeMoisture / compositeAsMixed;
                                setTargetDoughMoisture(
                                  Math.max(0, Math.min(80, Number((calculated.doughMoisture * scale).toFixed(1))))
                                );
                                setTargetFillingMoisture(
                                  Math.max(0, Math.min(80, Number((calculated.fillingMoisture * scale).toFixed(1))))
                                );
                              }
                            }
                            setMoistureMode(next);
                          }}
                          style={{
                            fontSize: "8px",
                            padding: "3px 6px",
                            border: "1px solid #5eead4",
                            borderRadius: "4px",
                            backgroundColor: "#f0fdfa",
                            color: "#0d9488",
                          }}
                        >
                          <option value="components">{t('postProcess.byComponent')}</option>
                          <option value="whole">{t('postProcess.wholeProduct')}</option>
                        </select>
                      </div>

                      {moistureMode === "components" ? (
                        <>
                          {/* Dough moisture */}
                          <div
                            style={{
                              marginBottom: "12px",
                              padding: "10px",
                              backgroundColor: "#ccfbf1",
                              borderRadius: "8px",
                              border: "1px solid #5eead4",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginBottom: "8px",
                              }}
                            >
                              <span
                                style={{
                                  fontSize: "10px",
                                  fontWeight: "600",
                                  color: "#134e4a",
                                }}
                              >
                                {t('postProcess.doughPhase')}
                              </span>
                              {Math.abs(
                                postProcessed.moistureAdjustment.dough.change
                              ) > 0.1 && (
                                <span
                                  style={{
                                    fontSize: "9px",
                                    padding: "2px 8px",
                                    borderRadius: "10px",
                                    fontWeight: "600",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "4px",
                                    backgroundColor: postProcessed
                                      .moistureAdjustment.dough.isPickup
                                      ? "#dbeafe"
                                      : "#fef3c7",
                                    color: postProcessed.moistureAdjustment
                                      .dough.isPickup
                                      ? "#1d4ed8"
                                      : "#b45309",
                                  }}
                                >
                                  {postProcessed.moistureAdjustment.dough
                                    .isPickup
                                    ? Icons.trendUp
                                    : Icons.trendDown}{" "}
                                  {Math.abs(
                                    postProcessed.moistureAdjustment.dough
                                      .change
                                  ).toFixed(1)}
                                  %{" "}
                                  {postProcessed.moistureAdjustment.dough
                                    .isPickup
                                    ? t('postProcess.pickup')
                                    : t('postProcess.loss')}
                                </span>
                              )}
                            </div>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                              }}
                            >
                              <div style={{ flex: 1 }}>
                                <div
                                  style={{
                                    fontSize: "8px",
                                    color: "#6b7280",
                                    marginBottom: "2px",
                                  }}
                                >
                                  {t('formulation.asMixed')}
                                </div>
                                <div
                                  style={{
                                    fontSize: "14px",
                                    fontWeight: "700",
                                    color: "#6b7280",
                                  }}
                                >
                                  {calculated.doughMoisture.toFixed(1)}%
                                </div>
                              </div>
                              <span
                                style={{ color: "#14b8a6", fontSize: "16px" }}
                              >
                                →
                              </span>
                              <div style={{ flex: 1 }}>
                                <div
                                  style={{
                                    fontSize: "8px",
                                    color: "#0d9488",
                                    marginBottom: "2px",
                                  }}
                                >
                                  {t('postProcess.finalMoisture')}
                                </div>
                                <input
                                  type="number"
                                  step="0.1"
                                  min="0"
                                  max="80"
                                  value={targetDoughMoisture < 0 ? "" : targetDoughMoisture}
                                  placeholder="—"
                                  onChange={(e) =>
                                    setTargetDoughMoisture(
                                      e.target.value === "" ? -1 : (parseFloat(e.target.value) || 0)
                                    )
                                  }
                                  style={{
                                    width: "60px",
                                    padding: "4px 8px",
                                    border: targetDoughMoisture < 0 ? "2px dashed #94a3b8" : "2px solid #14b8a6",
                                    borderRadius: "6px",
                                    fontSize: "14px",
                                    fontWeight: "700",
                                    color: targetDoughMoisture < 0 ? "#94a3b8" : "#0d9488",
                                    textAlign: "center",
                                    background: "white",
                                  }}
                                />
                              </div>
                            </div>
                            <input
                              type="range"
                              min="5"
                              max="60"
                              step="0.1"
                              value={targetDoughMoisture < 0 ? calculated.doughMoisture : targetDoughMoisture}
                              onChange={(e) =>
                                setTargetDoughMoisture(
                                  parseFloat(e.target.value)
                                )
                              }
                              style={{
                                width: "100%",
                                marginTop: "8px",
                                accentColor: "#14b8a6",
                                background: `linear-gradient(to right, #14b8a6 ${
                                  ((targetDoughMoisture - 5) / 55) * 100
                                }%, #d1d5db ${
                                  ((targetDoughMoisture - 5) / 55) * 100
                                }%)`,
                              }}
                            />
                            <div
                              style={{
                                marginTop: "6px",
                                padding: "4px 8px",
                                backgroundColor: "#f0fdfa",
                                borderRadius: "4px",
                                fontSize: "8px",
                                color: "#0f766e",
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                              }}
                            >
                              {Icons.lock} {t('postProcess.waterLabel')}{" "}
                              {postProcessed.moistureAdjustment.dough.originalWaterPct.toFixed(
                                1
                              )}
                              % →{" "}
                              <strong
                                style={{
                                  color:
                                    postProcessed.moistureAdjustment.dough
                                      .adjustedWaterPct < 0
                                      ? "#dc2626"
                                      : "#0d9488",
                                }}
                              >
                                {postProcessed.moistureAdjustment.dough.adjustedWaterPct.toFixed(
                                  1
                                )}
                                %
                              </strong>
                            </div>
                          </div>

                          {/* Filling moisture */}
                          <div
                            style={{
                              padding: "10px",
                              backgroundColor: "#ccfbf1",
                              borderRadius: "8px",
                              border: "1px solid #5eead4",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginBottom: "8px",
                              }}
                            >
                              <span
                                style={{
                                  fontSize: "10px",
                                  fontWeight: "600",
                                  color: "#134e4a",
                                }}
                              >
                                {t('postProcess.fillingPhase')}
                              </span>
                              {Math.abs(
                                postProcessed.moistureAdjustment.filling.change
                              ) > 0.1 && (
                                <span
                                  style={{
                                    fontSize: "9px",
                                    padding: "2px 8px",
                                    borderRadius: "10px",
                                    fontWeight: "600",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "4px",
                                    backgroundColor: postProcessed
                                      .moistureAdjustment.filling.isPickup
                                      ? "#dbeafe"
                                      : "#fef3c7",
                                    color: postProcessed.moistureAdjustment
                                      .filling.isPickup
                                      ? "#1d4ed8"
                                      : "#b45309",
                                  }}
                                >
                                  {postProcessed.moistureAdjustment.filling
                                    .isPickup
                                    ? Icons.trendUp
                                    : Icons.trendDown}{" "}
                                  {Math.abs(
                                    postProcessed.moistureAdjustment.filling
                                      .change
                                  ).toFixed(1)}
                                  %{" "}
                                  {postProcessed.moistureAdjustment.filling
                                    .isPickup
                                    ? t('postProcess.pickup')
                                    : t('postProcess.loss')}
                                </span>
                              )}
                            </div>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                              }}
                            >
                              <div style={{ flex: 1 }}>
                                <div
                                  style={{
                                    fontSize: "8px",
                                    color: "#6b7280",
                                    marginBottom: "2px",
                                  }}
                                >
                                  {t('formulation.asMixed')}
                                </div>
                                <div
                                  style={{
                                    fontSize: "14px",
                                    fontWeight: "700",
                                    color: "#6b7280",
                                  }}
                                >
                                  {calculated.fillingMoisture.toFixed(1)}%
                                </div>
                              </div>
                              <span
                                style={{ color: "#14b8a6", fontSize: "16px" }}
                              >
                                →
                              </span>
                              <div style={{ flex: 1 }}>
                                <div
                                  style={{
                                    fontSize: "8px",
                                    color: "#0d9488",
                                    marginBottom: "2px",
                                  }}
                                >
                                  {t('postProcess.finalMoisture')}
                                </div>
                                <input
                                  type="number"
                                  step="0.1"
                                  min="0"
                                  max="80"
                                  value={targetFillingMoisture < 0 ? "" : targetFillingMoisture}
                                  placeholder="—"
                                  onChange={(e) =>
                                    setTargetFillingMoisture(
                                      e.target.value === "" ? -1 : (parseFloat(e.target.value) || 0)
                                    )
                                  }
                                  style={{
                                    width: "60px",
                                    padding: "4px 8px",
                                    border: targetFillingMoisture < 0 ? "2px dashed #94a3b8" : "2px solid #14b8a6",
                                    borderRadius: "6px",
                                    fontSize: "14px",
                                    fontWeight: "700",
                                    color: targetFillingMoisture < 0 ? "#94a3b8" : "#0d9488",
                                    textAlign: "center",
                                    background: "white",
                                  }}
                                />
                              </div>
                            </div>
                            <input
                              type="range"
                              min="5"
                              max="70"
                              step="0.1"
                              value={targetFillingMoisture < 0 ? calculated.fillingMoisture : targetFillingMoisture}
                              onChange={(e) =>
                                setTargetFillingMoisture(
                                  parseFloat(e.target.value)
                                )
                              }
                              style={{
                                width: "100%",
                                marginTop: "8px",
                                accentColor: "#14b8a6",
                                background: `linear-gradient(to right, #14b8a6 ${
                                  ((targetFillingMoisture - 5) / 65) * 100
                                }%, #d1d5db ${
                                  ((targetFillingMoisture - 5) / 65) * 100
                                }%)`,
                              }}
                            />
                            <div
                              style={{
                                marginTop: "6px",
                                padding: "4px 8px",
                                backgroundColor: "#f0fdfa",
                                borderRadius: "4px",
                                fontSize: "8px",
                                color: "#0f766e",
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                              }}
                            >
                              {Icons.lock} {t('postProcess.waterLabel')}{" "}
                              {postProcessed.moistureAdjustment.filling.originalWaterPct.toFixed(
                                1
                              )}
                              % →{" "}
                              <strong
                                style={{
                                  color:
                                    postProcessed.moistureAdjustment.filling
                                      .adjustedWaterPct < 0
                                      ? "#dc2626"
                                      : "#0d9488",
                                }}
                              >
                                {postProcessed.moistureAdjustment.filling.adjustedWaterPct.toFixed(
                                  1
                                )}
                                %
                              </strong>
                            </div>
                          </div>
                        </>
                      ) : (
                        /* Whole product mode */
                        <div
                          style={{
                            padding: "10px",
                            backgroundColor: "#ccfbf1",
                            borderRadius: "8px",
                            border: "1px solid #5eead4",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              marginBottom: "8px",
                            }}
                          >
                            <span
                              style={{
                                fontSize: "10px",
                                fontWeight: "600",
                                color: "#134e4a",
                              }}
                            >
                              {t('postProcess.wholeProduct')}
                            </span>
                            {Math.abs(
                              (calculated.doughMoisture * doughRatio) / 100 +
                                (calculated.fillingMoisture * fillingRatio) /
                                  100 -
                                targetWholeMoisture
                            ) > 0.1 && (
                              <span
                                style={{
                                  fontSize: "9px",
                                  padding: "2px 8px",
                                  borderRadius: "10px",
                                  fontWeight: "600",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                  backgroundColor:
                                    targetWholeMoisture >
                                    (calculated.doughMoisture * doughRatio) /
                                      100 +
                                      (calculated.fillingMoisture *
                                        fillingRatio) /
                                        100
                                      ? "#dbeafe"
                                      : "#fef3c7",
                                  color:
                                    targetWholeMoisture >
                                    (calculated.doughMoisture * doughRatio) /
                                      100 +
                                      (calculated.fillingMoisture *
                                        fillingRatio) /
                                        100
                                      ? "#1d4ed8"
                                      : "#b45309",
                                }}
                              >
                                {targetWholeMoisture >
                                (calculated.doughMoisture * doughRatio) / 100 +
                                  (calculated.fillingMoisture * fillingRatio) /
                                    100
                                  ? Icons.trendUp
                                  : Icons.trendDown}{" "}
                                {Math.abs(
                                  (calculated.doughMoisture * doughRatio) /
                                    100 +
                                    (calculated.fillingMoisture *
                                      fillingRatio) /
                                      100 -
                                    targetWholeMoisture
                                ).toFixed(1)}
                                %
                              </span>
                            )}
                          </div>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            <div style={{ flex: 1 }}>
                              <div
                                style={{
                                  fontSize: "8px",
                                  color: "#6b7280",
                                  marginBottom: "2px",
                                }}
                              >
                                {t('formulation.asMixed')}
                              </div>
                              <div
                                style={{
                                  fontSize: "14px",
                                  fontWeight: "700",
                                  color: "#6b7280",
                                }}
                              >
                                {(
                                  (calculated.doughMoisture * doughRatio) /
                                    100 +
                                  (calculated.fillingMoisture * fillingRatio) /
                                    100
                                ).toFixed(1)}
                                %
                              </div>
                            </div>
                            <span
                              style={{ color: "#14b8a6", fontSize: "16px" }}
                            >
                              →
                            </span>
                            <div style={{ flex: 1 }}>
                              <div
                                style={{
                                  fontSize: "8px",
                                  color: "#0d9488",
                                  marginBottom: "2px",
                                }}
                              >
                                {t('postProcess.finalMoisture')}
                              </div>
                              <input
                                type="number"
                                step="0.1"
                                min="0"
                                max="80"
                                value={targetWholeMoisture}
                                onChange={(e) =>
                                  setTargetWholeMoisture(
                                    parseFloat(e.target.value) || 0
                                  )
                                }
                                style={{
                                  width: "60px",
                                  padding: "4px 8px",
                                  border: "2px solid #14b8a6",
                                  borderRadius: "6px",
                                  fontSize: "14px",
                                  fontWeight: "700",
                                  color: "#0d9488",
                                  textAlign: "center",
                                  background: "white",
                                }}
                              />
                            </div>
                          </div>
                          <input
                            type="range"
                            min="5"
                            max="60"
                            step="0.1"
                            value={targetWholeMoisture}
                            onChange={(e) =>
                              setTargetWholeMoisture(parseFloat(e.target.value))
                            }
                            style={{
                              width: "100%",
                              marginTop: "8px",
                              accentColor: "#14b8a6",
                              background: `linear-gradient(to right, #14b8a6 ${
                                ((targetWholeMoisture - 5) / 55) * 100
                              }%, #d1d5db ${
                                ((targetWholeMoisture - 5) / 55) * 100
                              }%)`,
                            }}
                          />
                          <div
                            style={{
                              marginTop: "8px",
                              fontSize: "8px",
                              color: "#6b7280",
                              textAlign: "center",
                            }}
                          >
                            {t('postProcess.lossesDistributed')}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Maillard Section - Amber theme with browning visualization */}
                    <div
                      style={{
                        padding: "14px",
                        backgroundColor: "#fef3c7",
                        borderRadius: "10px",
                        border: "1px solid #fcd34d",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          marginBottom: "12px",
                        }}
                      >
                        <span style={{ color: "#d97706" }}>{Icons.flame}</span>
                        <div>
                          <div
                            style={{
                              fontSize: "11px",
                              fontWeight: "700",
                              color: "#92400e",
                            }}
                          >
                            {t('postProcess.maillardDegradation')}
                          </div>
                          <div style={{ fontSize: "8px", color: "#b45309" }}>
                            {t('postProcess.consumerPrepBrowning')}
                          </div>
                        </div>
                      </div>

                      {/* Prep method buttons */}
                      <div
                        style={{
                          marginBottom: "12px",
                          display: "grid",
                          gridTemplateColumns: "repeat(4, 1fr)",
                          gap: "4px",
                        }}
                      >
                        {[
                          {
                            id: "none",
                            label: t('postProcess.noAdjust'),
                            temp: "—",
                            tooltip: t('postProcess.noThermalDegradation'),
                          },
                          {
                            id: "steam",
                            label: t('postProcess.steam'),
                            temp: "100°C",
                            tooltip: t('postProcess.steamMinimalMaillard'),
                          },
                          {
                            id: "light_brown",
                            label: t('postProcess.lightBrown'),
                            temp: "150°C",
                            tooltip:
                              t('postProcess.lightBrowningTooltip'),
                          },
                          {
                            id: "dark_brown",
                            label: t('postProcess.darkBrown'),
                            temp: "180°C+",
                            tooltip:
                              t('postProcess.darkBrowningTooltip'),
                          },
                        ].map((m) => (
                          <button
                            key={m.id}
                            onClick={() => setConsumerPrepMethod(m.id)}
                            title={m.tooltip}
                            style={{
                              padding: "6px 2px",
                              borderRadius: "6px",
                              border: "none",
                              cursor: "pointer",
                              backgroundColor:
                                consumerPrepMethod === m.id
                                  ? "#b45309"
                                  : "#fef9c3",
                              color:
                                consumerPrepMethod === m.id
                                  ? "white"
                                  : "#a16207",
                              fontWeight:
                                consumerPrepMethod === m.id ? "700" : "500",
                              fontSize: "8px",
                              transition: "all 0.2s",
                            }}
                          >
                            <div>{m.label}</div>
                            <div style={{ fontSize: "7px", opacity: 0.8 }}>
                              {m.temp}
                            </div>
                          </button>
                        ))}
                      </div>

                      {/* Browning Visualization - Cross section of dumpling */}
                      <div
                        style={{
                          padding: "12px",
                          backgroundColor: "#fffbeb",
                          borderRadius: "8px",
                          marginBottom: "10px",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "9px",
                            fontWeight: "600",
                            color: "#92400e",
                            marginBottom: "8px",
                            textAlign: "center",
                          }}
                        >
                          {t('postProcess.crossSectionView')}
                        </div>
                        <div
                          style={{
                            position: "relative",
                            width: "100%",
                            height: "80px",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                        >
                          {/* Outer dough layer with browning gradient */}
                          <div
                            style={{
                              position: "relative",
                              width: "140px",
                              height: "70px",
                              borderRadius: "50%",
                              background:
                                consumerPrepMethod === "none" ||
                                consumerPrepMethod === "steam"
                                  ? "#fef3c7"
                                  : consumerPrepMethod === "light_brown"
                                  ? `radial-gradient(ellipse at center, #fef3c7 ${
                                      100 - crustFraction * 2
                                    }%, #d97706 ${
                                      100 - crustFraction
                                    }%, #b45309 100%)`
                                  : `radial-gradient(ellipse at center, #fef3c7 ${
                                      100 - crustFraction * 2
                                    }%, #92400e ${
                                      100 - crustFraction
                                    }%, #451a03 100%)`,
                              border:
                                consumerPrepMethod === "none" ||
                                consumerPrepMethod === "steam"
                                  ? "3px solid #fcd34d"
                                  : "none",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              boxShadow:
                                consumerPrepMethod !== "none" &&
                                consumerPrepMethod !== "steam"
                                  ? "inset 0 0 20px rgba(0,0,0,0.2)"
                                  : "none",
                            }}
                          >
                            {/* Inner filling */}
                            <div
                              style={{
                                width: "70px",
                                height: "35px",
                                borderRadius: "50%",
                                background:
                                  "linear-gradient(180deg, #dcfce7 0%, #86efac 100%)",
                                border: "2px solid #22c55e",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                fontSize: "8px",
                                color: "#166534",
                                fontWeight: "600",
                              }}
                            >
                              {t('formulation.fillingLabel')}
                            </div>
                          </div>
                          {/* Crust label only */}
                          {consumerPrepMethod !== "none" &&
                            consumerPrepMethod !== "steam" && (
                              <div
                                style={{
                                  position: "absolute",
                                  top: "2px",
                                  right: "10px",
                                  fontSize: "7px",
                                  color: "#78350f",
                                  fontWeight: "600",
                                }}
                              >
                                {t('postProcess.crustPct', { pct: crustFraction })}
                              </div>
                            )}
                        </div>
                      </div>

                      {/* Crust fraction slider - only if browning */}
                      {consumerPrepMethod !== "none" &&
                        consumerPrepMethod !== "steam" && (
                          <div
                            style={{
                              padding: "10px",
                              backgroundColor: "#fde68a",
                              borderRadius: "8px",
                              marginBottom: "10px",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginBottom: "4px",
                              }}
                            >
                              <span
                                style={{
                                  fontSize: "10px",
                                  fontWeight: "600",
                                  color: "#92400e",
                                }}
                              >
                                {t('postProcess.crustThickness')}
                              </span>
                              <span
                                style={{
                                  fontSize: "12px",
                                  fontWeight: "700",
                                  color: "#b45309",
                                }}
                              >
                                {crustFraction}%
                              </span>
                            </div>
                            <input
                              type="range"
                              min="5"
                              max="30"
                              value={crustFraction}
                              onChange={(e) =>
                                setCrustFraction(parseInt(e.target.value))
                              }
                              style={{
                                width: "100%",
                                accentColor: "#f59e0b",
                                background: `linear-gradient(to right, #f59e0b ${
                                  ((crustFraction - 5) / 25) * 100
                                }%, #fef3c7 ${
                                  ((crustFraction - 5) / 25) * 100
                                }%)`,
                              }}
                            />
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                fontSize: "7px",
                                color: "#a16207",
                                marginTop: "2px",
                              }}
                            >
                              <span>{t('postProcess.thin')}</span>
                              <span>{t('postProcess.thick')}</span>
                            </div>
                          </div>
                        )}

                      {/* Simplified Lysine Impact Summary */}
                      <div
                        style={{
                          padding: "10px",
                          backgroundColor: "white",
                          borderRadius: "8px",
                          border: "1px solid #fcd34d",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "9px",
                            fontWeight: "600",
                            color: "#92400e",
                            marginBottom: "8px",
                          }}
                        >
                          {t('postProcess.lysineRetentionSummary')}
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <div style={{ flex: 1 }}>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                padding: "4px 8px",
                                backgroundColor: "#dcfce7",
                                borderRadius: "4px",
                                marginBottom: "4px",
                              }}
                            >
                              <span
                                style={{ fontSize: "8px", color: "#166534" }}
                              >
                                {t('postProcess.fillingProtected')}
                              </span>
                              <span
                                style={{
                                  fontSize: "11px",
                                  fontWeight: "700",
                                  color: "#15803d",
                                }}
                              >
                                {(
                                  postProcessed.maillardAdjustment
                                    .lysineRetention.filling * 100
                                ).toFixed(0)}
                                %
                              </span>
                            </div>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                padding: "4px 8px",
                                backgroundColor:
                                  consumerPrepMethod === "none"
                                    ? "#f3f4f6"
                                    : consumerPrepMethod === "steam"
                                    ? "#dbeafe"
                                    : "#fed7aa",
                                borderRadius: "4px",
                              }}
                            >
                              <span
                                style={{
                                  fontSize: "8px",
                                  color:
                                    consumerPrepMethod === "none"
                                      ? "#6b7280"
                                      : consumerPrepMethod === "steam"
                                      ? "#1e40af"
                                      : "#92400e",
                                }}
                              >
                                {t('postProcess.doughNet')}
                              </span>
                              <span
                                style={{
                                  fontSize: "11px",
                                  fontWeight: "700",
                                  color:
                                    consumerPrepMethod === "none"
                                      ? "#374151"
                                      : consumerPrepMethod === "steam"
                                      ? "#1d4ed8"
                                      : "#b45309",
                                }}
                              >
                                {(
                                  postProcessed.maillardAdjustment
                                    .lysineRetention.netDough * 100
                                ).toFixed(1)}
                                %
                              </span>
                            </div>
                          </div>
                          <div
                            style={{
                              textAlign: "center",
                              padding: "8px",
                              backgroundColor: "#f3e8ff",
                              borderRadius: "6px",
                              minWidth: "70px",
                            }}
                          >
                            <div
                              style={{
                                fontSize: "7px",
                                color: "#6b21a8",
                                textTransform: "uppercase",
                              }}
                            >
                              {t('postProcess.overall')}
                            </div>
                            <div
                              style={{
                                fontSize: "14px",
                                fontWeight: "700",
                                color: "#7c3aed",
                              }}
                            >
                              {(
                                (postProcessed.maillardAdjustment.proteinWeights
                                  .dough *
                                  postProcessed.maillardAdjustment
                                    .lysineRetention.netDough +
                                  postProcessed.maillardAdjustment
                                    .proteinWeights.filling *
                                    postProcessed.maillardAdjustment
                                      .lysineRetention.filling) *
                                100
                              ).toFixed(1)}
                              %
                            </div>
                          </div>
                        </div>
                        {consumerPrepMethod !== "none" && (
                          <div
                            style={{
                              marginTop: "6px",
                              fontSize: "7px",
                              color: "#6b7280",
                              textAlign: "center",
                            }}
                          >
                            {consumerPrepMethod === "steam"
                              ? t('postProcess.minimalDegradation')
                              : t('postProcess.surfaceBrowning')}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Results Summary */}
                {postProcessEnabled && (
                  <div
                    style={{
                      marginTop: "16px",
                      padding: "14px",
                      backgroundColor:
                        postProcessed.pdcaasChange < 0 ? "#fef2f2" : "#f0fdf4",
                      borderRadius: "10px",
                      border:
                        "1px solid " +
                        (postProcessed.pdcaasChange < 0
                          ? "#fecaca"
                          : "#86efac"),
                    }}
                  >
                    <div
                      style={{
                        fontSize: "11px",
                        fontWeight: "700",
                        color:
                          postProcessed.pdcaasChange < 0
                            ? "#991b1b"
                            : "#166534",
                        marginBottom: "10px",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      {Icons.refresh} {t('postProcess.adjustedResults')}
                    </div>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(4, 1fr)",
                        gap: "12px",
                      }}
                    >
                      <div
                        style={{
                          padding: "10px",
                          backgroundColor: "white",
                          borderRadius: "8px",
                          textAlign: "center",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "8px",
                            color: "#6b7280",
                            textTransform: "uppercase",
                          }}
                        >
                          {t('postProcess.originalPDCAAS')}
                        </div>
                        <div
                          style={{
                            fontSize: "16px",
                            fontWeight: "700",
                            color: "#6b7280",
                          }}
                        >
                          {(calculated.pdcaas * 100).toFixed(1)}%
                        </div>
                      </div>
                      <div
                        style={{
                          padding: "10px",
                          backgroundColor: "white",
                          borderRadius: "8px",
                          textAlign: "center",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "8px",
                            color: "#6b7280",
                            textTransform: "uppercase",
                          }}
                        >
                          {t('postProcess.adjustedPDCAAS')}
                        </div>
                        <div
                          style={{
                            fontSize: "16px",
                            fontWeight: "700",
                            color:
                              postProcessed.pdcaasChange < 0
                                ? "#dc2626"
                                : "#16a34a",
                          }}
                        >
                          {(postProcessed.adjustedPdcaas * 100).toFixed(1)}%
                        </div>
                        <div
                          style={{
                            fontSize: "9px",
                            color:
                              postProcessed.pdcaasChange < 0
                                ? "#dc2626"
                                : "#16a34a",
                          }}
                        >
                          ({postProcessed.pdcaasChange >= 0 ? "+" : ""}
                          {(postProcessed.pdcaasChange * 100).toFixed(2)}%)
                        </div>
                      </div>
                      <div
                        style={{
                          padding: "10px",
                          backgroundColor: "white",
                          borderRadius: "8px",
                          textAlign: "center",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "8px",
                            color: "#6b7280",
                            textTransform: "uppercase",
                          }}
                        >
                          {t('postProcess.adjProtein')}
                        </div>
                        <div
                          style={{
                            fontSize: "16px",
                            fontWeight: "700",
                            color: "#1e1b4b",
                          }}
                        >
                          {postProcessed.adjustedCorrectedProtein.toFixed(2)}g
                        </div>
                      </div>
                      <div
                        style={{
                          padding: "10px",
                          backgroundColor: "white",
                          borderRadius: "8px",
                          textAlign: "center",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "8px",
                            color: "#6b7280",
                            textTransform: "uppercase",
                          }}
                        >
                          {t('postProcess.adjDV')}
                        </div>
                        <div
                          style={{
                            fontSize: "16px",
                            fontWeight: "700",
                            color: "#1e1b4b",
                          }}
                        >
                          {postProcessed.adjustedPercentDV.toFixed(1)}%
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Save formula settings to Supabase */}
                {sourceMode === "supabase" && selectedFormula && (
                  <div
                    style={{
                      marginTop: "12px",
                      padding: "12px",
                      backgroundColor: "#eff6ff",
                      borderRadius: "8px",
                      border: "1px solid #bfdbfe",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <div>
                        <div style={{ fontSize: "10px", fontWeight: "700", color: "#1e40af" }}>
                          {t('postProcess.saveAsFork', { code: selectedFormula })}
                        </div>
                        <div style={{ fontSize: "8px", color: "#3b82f6", marginTop: "2px" }}>
                          {t('postProcess.saveAsForkHint')}
                        </div>
                      </div>
                      <button
                        onClick={saveFormulaSettings}
                        disabled={formulaSettingsSaving}
                        style={{
                          padding: "6px 16px",
                          fontSize: "10px",
                          fontWeight: "700",
                          backgroundColor: formulaSettingsSaving ? "#93c5fd" : "#2563eb",
                          color: "white",
                          border: "none",
                          borderRadius: "6px",
                          cursor: formulaSettingsSaving ? "wait" : "pointer",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {formulaSettingsSaving ? t('postProcess.saving') : t('postProcess.saveAsNew')}
                      </button>
                    </div>
                    {formulaSettingsMsg && (
                      <div
                        style={{
                          marginTop: "6px",
                          fontSize: "9px",
                          fontWeight: "600",
                          color: formulaSettingsMsg.startsWith("Error") ? "#dc2626" : "#16a34a",
                        }}
                      >
                        {formulaSettingsMsg}
                      </div>
                    )}
                  </div>
                )}

                <div
                  style={{
                    marginTop: "12px",
                    padding: "10px",
                    backgroundColor: "#f8fafc",
                    borderRadius: "8px",
                    fontSize: "8px",
                    color: "#6b7280",
                    lineHeight: "1.5",
                  }}
                >
                  <strong>{t('postProcess.scientificBasis')}</strong> {t('postProcess.maillardFactors')}
                </div>
              </div>
            )}
          </div>

          {/* Formulation Helper (AAS Optimization) */}
          <div
            className="card"
            ref={cardRefFormHelper}
            style={{ maxWidth: "1500px", margin: "20px auto", position: "relative" }}
          >
            <DownloadButton targetRef={cardRefFormHelper} formulaCode={sessionPrefix} sessionName={displaySessionName} cardName="Formulation_Helper" />
            <button
              onClick={() => setShowFormulationHelper(!showFormulationHelper)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                width: "100%",
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: "0",
                marginBottom: showFormulationHelper ? "10px" : "0",
              }}
            >
              <span
                style={{
                  transform: showFormulationHelper
                    ? "rotate(180deg)"
                    : "rotate(0deg)",
                  transition: "transform 0.2s",
                }}
              >
                {Icons.chevron}
              </span>
              <h3
                style={{
                  fontSize: "12px",
                  fontWeight: "700",
                  color: "#1e1b4b",
                  margin: 0,
                }}
              >
                {t('formulation.formulationHelper')}
              </h3>
              <span
                style={{
                  marginLeft: "auto",
                  fontSize: "9px",
                  padding: "3px 8px",
                  backgroundColor: "#dbeafe",
                  color: "#1d4ed8",
                  borderRadius: "8px",
                  fontWeight: "600",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                {Icons.sparkles} {t('formulation.aiSuggestions')}
              </span>
            </button>

            {/* AI Suggestions - Always visible */}
            {aiSuggestions.length > 0 && !calculated.allAbove100 && (
              <div
                style={{
                  marginTop: "10px",
                  marginBottom: showFormulationHelper ? "12px" : "0",
                  padding: "10px",
                  backgroundColor: "#fefce8",
                  borderRadius: "8px",
                  border: "1px solid #fef08a",
                }}
              >
                <div
                  style={{
                    fontSize: "10px",
                    fontWeight: "600",
                    color: "#a16207",
                    marginBottom: "6px",
                  }}
                >
                  {t('formulation.toImproveAA', { aa: AA_FULL_NAMES[calculated.limitingAA] })}
                </div>
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                  {aiSuggestions.map((s) => (
                    <div
                      key={s.name}
                      style={{
                        padding: "6px 10px",
                        backgroundColor: "white",
                        borderRadius: "6px",
                        border: `2px solid ${s.color}`,
                        fontSize: "10px",
                      }}
                    >
                      <span style={{ fontWeight: "600", color: s.color }}>
                        {ingName(s.name)}
                      </span>
                      <span style={{ color: "#6b7280", marginLeft: "6px" }}>
                        {AA_NAMES[calculated.limitingAA]}:{" "}
                        {(s.scores[calculated.limitingAA] * 100).toFixed(0)}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {calculated.allAbove100 && (
              <div
                style={{
                  marginTop: "10px",
                  marginBottom: showFormulationHelper ? "12px" : "0",
                  padding: "10px",
                  backgroundColor: "#dbeafe",
                  borderRadius: "8px",
                  border: "1px solid #93c5fd",
                }}
              >
                <div
                  style={{
                    fontSize: "10px",
                    fontWeight: "600",
                    color: "#1d4ed8",
                  }}
                >
                  {t('formulation.optimalAAProfile')}
                </div>
              </div>
            )}

            {/* Collapsible table */}
            {showFormulationHelper && (
              <div style={{ overflowX: "auto" }}>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: "10px",
                  }}
                >
                  <thead>
                    <tr style={{ backgroundColor: "#f8fafc" }}>
                      <th
                        style={{
                          padding: "8px",
                          textAlign: "left",
                          borderBottom: "2px solid #e5e7eb",
                        }}
                      >
                        {t('formulation.ingredientHeader')}
                      </th>
                      <th
                        style={{
                          padding: "8px",
                          textAlign: "center",
                          borderBottom: "2px solid #e5e7eb",
                        }}
                      >
                        {t('formulation.proteinHeader')}
                      </th>
                      <th
                        style={{
                          padding: "8px",
                          textAlign: "center",
                          borderBottom: "2px solid #e5e7eb",
                        }}
                      >
                        {t('formulation.digestHeader')}
                      </th>
                      {Object.keys(AA_NAMES).map((aa) => (
                        <th
                          key={aa}
                          style={{
                            padding: "6px 3px",
                            textAlign: "center",
                            borderBottom: "2px solid #e5e7eb",
                            backgroundColor:
                              calculated.limitingAA === aa &&
                              !calculated.allAbove100
                                ? "#fef2f2"
                                : calculated.allAbove100
                                ? "#dbeafe"
                                : "transparent",
                            color:
                              calculated.limitingAA === aa &&
                              !calculated.allAbove100
                                ? "#dc2626"
                                : calculated.allAbove100
                                ? "#1d4ed8"
                                : "#374151",
                            fontSize: "9px",
                          }}
                        >
                          {AA_NAMES[aa]}
                        </th>
                      ))}
                      <th
                        style={{
                          padding: "8px",
                          textAlign: "center",
                          borderBottom: "2px solid #e5e7eb",
                        }}
                      >
                        PDCAAS
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {helperData.ingredients.map((ing) => (
                      <tr
                        key={ing.name}
                        style={{ borderBottom: "1px solid #f1f5f9" }}
                      >
                        <td
                          style={{
                            padding: "6px",
                            display: "flex",
                            alignItems: "center",
                            gap: "5px",
                          }}
                        >
                          <span
                            style={{
                              width: "8px",
                              height: "8px",
                              backgroundColor: ing.color,
                              borderRadius: "2px",
                            }}
                          ></span>
                          {ingName(ing.name)}
                        </td>
                        <td style={{ padding: "6px", textAlign: "center" }}>
                          {ing.protein}g
                        </td>
                        <td style={{ padding: "6px", textAlign: "center" }}>
                          {(ing.digestibility * 100).toFixed(0)}%
                        </td>
                        {Object.keys(AA_NAMES).map((aa) => {
                          const score = ing.scores[aa];
                          return (
                            <td
                              key={aa}
                              style={{
                                padding: "4px 2px",
                                textAlign: "center",
                                backgroundColor:
                                  calculated.limitingAA === aa &&
                                  !calculated.allAbove100
                                    ? "#fef2f2"
                                    : score >= 1
                                    ? "#f0fdf4"
                                    : score >= 0.8
                                    ? "#fffbeb"
                                    : "#fff1f2",
                                fontWeight:
                                  ing.limitingAA === aa ? "700" : "400",
                                color:
                                  score >= 1
                                    ? "#16a34a"
                                    : score >= 0.8
                                    ? "#ca8a04"
                                    : "#dc2626",
                                fontSize: "9px",
                              }}
                            >
                              {(score * 100).toFixed(0)}%
                            </td>
                          );
                        })}
                        <td
                          style={{
                            padding: "6px",
                            textAlign: "center",
                            fontWeight: "700",
                            color: "#7c3aed",
                          }}
                        >
                          {(ing.pdcaas * 100).toFixed(0)}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Ingredient Contributions Protein */}
          <div
            className="card"
            ref={cardRefIngContrib}
            style={{ maxWidth: "1500px", margin: "20px auto", position: "relative" }}
          >
            <DownloadButton targetRef={cardRefIngContrib} formulaCode={sessionPrefix} sessionName={displaySessionName} cardName="Ingredient_Breakdown" />
            <button
              onClick={() =>
                setShowIngredientContributions(!showIngredientContributions)
              }
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                width: "100%",
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: "0",
                marginBottom: showIngredientContributions ? "10px" : "0",
              }}
            >
              <span
                style={{
                  transform: showIngredientContributions
                    ? "rotate(180deg)"
                    : "rotate(0deg)",
                  transition: "transform 0.2s",
                }}
              >
                {Icons.chevron}
              </span>
              <h3
                style={{
                  fontSize: "12px",
                  fontWeight: "700",
                  color: "#1e1b4b",
                  margin: 0,
                }}
              >
                {t('formulation.ingredientContribProtein')}
              </h3>
              <span
                style={{
                  marginLeft: "auto",
                  fontSize: "9px",
                  padding: "3px 8px",
                  backgroundColor: "#f3e8ff",
                  color: "#7c3aed",
                  borderRadius: "8px",
                  fontWeight: "600",
                }}
              >
                {calculated.totalProtein.toFixed(1)}g total
              </span>
            </button>

            {showIngredientContributions && (
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "11px",
                }}
              >
                <thead>
                  <tr style={{ backgroundColor: "#f8fafc" }}>
                    <th
                      style={{
                        padding: "8px",
                        textAlign: "left",
                        borderBottom: "2px solid #e5e7eb",
                      }}
                    >
                      Ingredient
                    </th>
                    <th
                      style={{
                        padding: "8px",
                        textAlign: "right",
                        borderBottom: "2px solid #e5e7eb",
                      }}
                    >
                      % Formula
                    </th>
                    <th
                      style={{
                        padding: "8px",
                        textAlign: "right",
                        borderBottom: "2px solid #e5e7eb",
                      }}
                    >
                      Protein/100g
                    </th>
                    <th
                      style={{
                        padding: "8px",
                        textAlign: "right",
                        borderBottom: "2px solid #e5e7eb",
                      }}
                    >
                      Contribution
                    </th>
                    <th
                      style={{
                        padding: "8px",
                        textAlign: "right",
                        borderBottom: "2px solid #e5e7eb",
                      }}
                    >
                      Digestibility
                    </th>
                    <th
                      style={{
                        padding: "8px",
                        textAlign: "right",
                        borderBottom: "2px solid #e5e7eb",
                      }}
                    >
                      % Total Protein
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(calculated.proteinContributions).map(
                    ([name, data]) => (
                      <tr
                        key={name}
                        style={{ borderBottom: "1px solid #f1f5f9" }}
                      >
                        <td
                          style={{
                            padding: "8px",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                          }}
                        >
                          <span
                            style={{
                              width: "10px",
                              height: "10px",
                              backgroundColor: data.color,
                              borderRadius: "3px",
                            }}
                          ></span>
                          <span
                            onClick={() => {
                              const ingData = ALL_INGREDIENTS[name];
                              setIngredientDetailName(name);
                              setEditingDataSources(ingData?.data_sources ? { ...ingData.data_sources } : {});
                              setDataSourceSaveMsg("");
                              setShowAllSources(false);
                            }}
                            style={{ cursor: "pointer", textDecoration: "underline", textDecorationColor: "#d1d5db", textUnderlineOffset: "2px" }}
                            title={t('formulation.clickToViewIngredient')}
                          >
                            {ingName(name)}
                          </span>
                        </td>
                        <td style={{ padding: "8px", textAlign: "right" }}>
                          {calculated.totalFormula[name]?.toFixed(1)}%
                        </td>
                        <td style={{ padding: "8px", textAlign: "right" }}>
                          {ALL_INGREDIENTS[name]?.protein_per_100g}g
                        </td>
                        <td
                          style={{
                            padding: "8px",
                            textAlign: "right",
                            fontWeight: "600",
                            color: "#7c3aed",
                          }}
                        >
                          {data.protein.toFixed(2)}g
                        </td>
                        <td style={{ padding: "8px", textAlign: "right" }}>
                          {(data.digestibility * 100).toFixed(0)}%
                        </td>
                        <td style={{ padding: "8px", textAlign: "right" }}>
                          {(
                            (data.protein / calculated.totalProtein) *
                            100
                          ).toFixed(1)}
                          %
                        </td>
                      </tr>
                    )
                  )}
                  <tr style={{ backgroundColor: "#ede9fe", fontWeight: "700" }}>
                    <td style={{ padding: "8px", color: "#5b21b6" }}>{t('formulation.total')}</td>
                    <td
                      style={{
                        padding: "8px",
                        textAlign: "right",
                        color: "#5b21b6",
                      }}
                    >
                      —
                    </td>
                    <td style={{ padding: "8px", textAlign: "right" }}>—</td>
                    <td
                      style={{
                        padding: "8px",
                        textAlign: "right",
                        color: "#5b21b6",
                      }}
                    >
                      {calculated.totalProtein.toFixed(2)}g
                    </td>
                    <td
                      style={{
                        padding: "8px",
                        textAlign: "right",
                        color: "#5b21b6",
                      }}
                    >
                      {(calculated.weightedDigestibility * 100).toFixed(1)}%
                    </td>
                    <td
                      style={{
                        padding: "8px",
                        textAlign: "right",
                        color: "#5b21b6",
                      }}
                    >
                      100%
                    </td>
                  </tr>
                </tbody>
              </table>
            )}
          </div>

          {/* Nutrition Facts Section */}
          <div
            className="card"
            ref={cardRefNFLabel}
            style={{ maxWidth: "1500px", margin: "20px auto", position: "relative" }}
          >
            <DownloadButton targetRef={cardRefNFLabel} formulaCode={sessionPrefix} sessionName={displaySessionName} cardName="Nutrition_Facts" />
            <h3
              style={{
                fontSize: "12px",
                fontWeight: "700",
                color: "#1e1b4b",
                marginBottom: "12px",
              }}
            >
              Nutrition Facts & Label
            </h3>

            <div
              style={{
                marginBottom: "16px",
                padding: "12px",
                backgroundColor: "#fef3c7",
                borderRadius: "8px",
                border: "1px solid #fcd34d",
              }}
            >
              <div
                style={{
                  fontSize: "11px",
                  fontWeight: "700",
                  color: "#92400e",
                  marginBottom: "6px",
                }}
              >
                {t('formulation.importantProteinRules')}
              </div>
              <div
                style={{
                  fontSize: "10px",
                  color: "#78350f",
                  lineHeight: "1.5",
                }}
              >
                <strong>{t('formulation.onTheLabel')}</strong> {t('formulation.declare')}{" "}
                <strong>{t('formulation.uncorrected')}</strong> {t('formulation.proteinWord')} (
                {nf.proteinPerServing.toFixed(1)}g/{t('formulation.servingWord')} → {t('formulation.roundsTo')}{" "}
                {fdaRound.protein(nf.proteinPerServing)}g)
                <br />
                <strong>{t('formulation.forDVCalc')}</strong> {t('formulation.calculateUsing')}{" "}
                <strong>{t('formulation.pdcaasCorrected')}</strong> {t('formulation.proteinWord')} (
                {nf.correctedProteinPerServing.toFixed(1)}g →{" "}
                {Math.round(nf.percentDV)}% DV)
                <br />
                <strong>{t('formulation.forClaims')}</strong> {t('formulation.claimsBasedOn')}{" "}
                ({t('formulation.currently')}: {nf.proteinClaim}
                )
              </div>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 280px",
                gap: "24px",
              }}
            >
              <div>
                <div
                  style={{
                    marginBottom: "10px",
                    display: "flex",
                    gap: "10px",
                    alignItems: "center",
                  }}
                >
                  <select
                    value={nfTableView}
                    onChange={(e) => setNfTableView(e.target.value)}
                    style={{
                      fontSize: "10px",
                      padding: "4px 8px",
                      border: "1px solid #e5e7eb",
                      borderRadius: "4px",
                    }}
                  >
                    <option value="serving">
                      Per Serving ({servingSize}g)
                    </option>
                    <option value="100g">Per 100g</option>
                  </select>
                  <span style={{ fontSize: "9px", color: "#6b7280" }}>
                    {t('formulation.hoverNutrientInfo')}
                  </span>
                </div>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: "11px",
                  }}
                >
                  <thead>
                    <tr style={{ backgroundColor: "#f8fafc" }}>
                      <th
                        style={{
                          padding: "8px",
                          textAlign: "left",
                          borderBottom: "2px solid #e5e7eb",
                        }}
                      >
                        Nutrient
                      </th>
                      <th
                        style={{
                          padding: "8px",
                          textAlign: "right",
                          borderBottom: "2px solid #e5e7eb",
                        }}
                      >
                        Amount
                      </th>
                      <th
                        style={{
                          padding: "8px",
                          textAlign: "right",
                          borderBottom: "2px solid #e5e7eb",
                        }}
                      >
                        %DV
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      {
                        key: "calories",
                        name: t('nutrient.calories'),
                        val:
                          nfTableView === "serving"
                            ? nf.caloriesPerServing
                            : nf.calories,
                        unit: "",
                        dv: null,
                      },
                      {
                        key: "fat",
                        name: t('nutrient.totalFat'),
                        val:
                          nfTableView === "serving"
                            ? nf.fatPerServing
                            : nf.totalFat,
                        unit: "g",
                        dv: DRV.fat,
                      },
                      {
                        key: "satfat",
                        name: "  " + t('nutrient.saturatedFat'),
                        val:
                          nfTableView === "serving"
                            ? nf.satfatPerServing
                            : nf.totalSatFat,
                        unit: "g",
                        dv: DRV.satfat,
                      },
                      {
                        key: "sodium",
                        name: t('nutrient.sodium'),
                        val:
                          nfTableView === "serving"
                            ? nf.sodiumPerServing
                            : nf.totalSodium,
                        unit: "mg",
                        dv: DRV.sodium,
                      },
                      {
                        key: "carbs",
                        name: t('nutrient.totalCarbohydrate'),
                        val:
                          nfTableView === "serving"
                            ? nf.carbsPerServing
                            : nf.totalCarbs,
                        unit: "g",
                        dv: DRV.carbs,
                      },
                      {
                        key: "fiber",
                        name: "  " + t('nutrient.dietaryFiber'),
                        val:
                          nfTableView === "serving"
                            ? nf.fiberPerServing
                            : nf.totalFiber,
                        unit: "g",
                        dv: DRV.fiber,
                      },
                      {
                        key: "sugar",
                        name: "  " + t('nutrient.totalSugars'),
                        val:
                          nfTableView === "serving"
                            ? nf.sugarPerServing
                            : nf.totalSugar,
                        unit: "g",
                        dv: null,
                      },
                      {
                        key: "protein",
                        name: t('nutrient.protein'),
                        val:
                          nfTableView === "serving"
                            ? nf.proteinPerServing
                            : nf.totalProtein,
                        unit: "g",
                        dv: DRV.protein,
                        useCorrected: true,
                      },
                    ].map((n, i) => (
                      <tr key={i} style={{ borderBottom: "1px solid #f1f5f9" }}>
                        <td
                          style={{
                            padding: "6px 8px",
                            fontWeight: n.name.startsWith("  ") ? "400" : "600",
                          }}
                        >
                          <InfoTooltip
                            content={
                              <div>
                                <div
                                  style={{
                                    fontWeight: "700",
                                    marginBottom: "4px",
                                  }}
                                >
                                  {FDA_INFO_KEYS[n.key]?.reg}
                                </div>
                                <div style={{ marginBottom: "4px" }}>
                                  <strong>{t('packaging.rounding')}</strong>{" "}
                                  {t(FDA_INFO_KEYS[n.key]?.ruleKey)}
                                </div>
                                <div>
                                  <strong>{t('packaging.drv')}</strong> {t(FDA_INFO_KEYS[n.key]?.drvKey)}
                                </div>
                                {FDA_INFO_KEYS[n.key]?.noteKey && (
                                  <div
                                    style={{
                                      marginTop: "6px",
                                      padding: "6px",
                                      backgroundColor: "rgba(251,191,36,0.2)",
                                      borderRadius: "4px",
                                    }}
                                  >
                                    {t(FDA_INFO_KEYS[n.key]?.noteKey)}
                                  </div>
                                )}
                              </div>
                            }
                          >
                            {n.name}{" "}
                            <span style={{ color: "#a78bfa" }}>
                              {Icons.info}
                            </span>
                          </InfoTooltip>
                        </td>
                        <td style={{ padding: "6px 8px", textAlign: "right" }}>
                          {n.val.toFixed(2)}
                          {n.unit}
                        </td>
                        <td
                          style={{
                            padding: "6px 8px",
                            textAlign: "right",
                            color: "#6b7280",
                          }}
                        >
                          {n.dv
                            ? Math.round(
                                ((n.useCorrected
                                  ? nfTableView === "serving"
                                    ? nf.correctedProteinPerServing
                                    : nf.totalProtein *
                                      (postProcessEnabled
                                        ? postProcessed.adjustedPdcaas
                                        : calculated.pdcaas)
                                  : n.val) /
                                  n.dv) *
                                  100
                              ) + "%"
                            : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* FDA Nutrition Facts Panel */}
              <div>
                {postProcessEnabled && (
                  <div style={{ fontSize: "9px", color: "#92400e", backgroundColor: "#fef3c7", padding: "6px 8px", borderRadius: "4px", marginBottom: "8px" }}>
                    <strong>Post-bake values:</strong> Nutrients concentrated by {((postProcessed.concentrationFactor?.overall || 1) * 100 - 100).toFixed(1)}% due to {postProcessed.weightLossPct.toFixed(1)}% moisture loss
                  </div>
                )}
                <div className="nf-panel">
                  <p className="nf-title">Nutrition Facts</p>
                  <div className="nf-line"></div>
                  <p style={{ fontSize: "12px", fontWeight: 700, margin: "2px 0 0" }}>
                    {servingsRounded.needsAbout ? "about " : ""}{servingsRounded.display} servings per container
                  </p>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: "14px", fontWeight: 700, margin: "0 0 1px" }}>
                    <span>Serving size</span>
                    <span>{piecesPerServing} piece{piecesPerServing !== 1 ? "s" : ""} ({servingSize}g)</span>
                  </div>
                  <div className="nf-line-thick"></div>
                  <div style={{ fontSize: "10px", fontWeight: 900 }}>Amount per serving</div>
                  <div className="nf-row" style={{ alignItems: "baseline" }}>
                    <span style={{ fontSize: "22px", fontWeight: 900 }}>Calories</span>
                    <span style={{ fontSize: "30px", fontWeight: 900 }}>{fdaRound.calories(nf.caloriesPerServing)}</span>
                  </div>
                  <div className="nf-line-medium"></div>
                  <div style={{ fontSize: "10px", textAlign: "right" as const, fontWeight: 900 }}>% Daily Value*</div>
                  <div className="nf-line"></div>
                  {/* Total Fat */}
                  <div className="nf-row">
                    <span><span className="nf-bold">Total Fat</span> {fdaRound.fat(nf.fatPerServing)}g</span>
                    <span className="nf-bold">{Math.round((nf.fatPerServing / DRV.fat) * 100)}%</span>
                  </div>
                  <div className="nf-line"></div>
                  {/* Saturated Fat */}
                  <div className="nf-row nf-indent">
                    <span>Saturated Fat {fdaRound.satfat(nf.satfatPerServing)}g</span>
                    <span className="nf-bold">{Math.round((nf.satfatPerServing / DRV.satfat) * 100)}%</span>
                  </div>
                  <div className="nf-line"></div>
                  {/* Trans Fat */}
                  <div className="nf-row nf-indent">
                    <span><em>Trans</em> Fat {fdaRound.transfat(calculated.transfatPerServing)}g</span>
                  </div>
                  <div className="nf-line"></div>
                  {/* Cholesterol */}
                  <div className="nf-row">
                    <span><span className="nf-bold">Cholesterol</span> {String(fdaRound.cholesterol(calculated.cholesterolPerServing))}mg</span>
                    <span className="nf-bold">{Math.round((calculated.cholesterolPerServing / DRV.cholesterol) * 100)}%</span>
                  </div>
                  <div className="nf-line"></div>
                  {/* Sodium */}
                  <div className="nf-row">
                    <span><span className="nf-bold">Sodium</span> {fdaRound.sodium(nf.sodiumPerServing)}mg</span>
                    <span className="nf-bold">{Math.round((nf.sodiumPerServing / DRV.sodium) * 100)}%</span>
                  </div>
                  <div className="nf-line"></div>
                  {/* Total Carbohydrate */}
                  <div className="nf-row">
                    <span><span className="nf-bold">Total Carbohydrate</span> {fdaRound.carbs(nf.carbsPerServing)}g</span>
                    <span className="nf-bold">{Math.round((nf.carbsPerServing / DRV.carbs) * 100)}%</span>
                  </div>
                  <div className="nf-line"></div>
                  {/* Dietary Fiber */}
                  <div className="nf-row nf-indent">
                    <span>Dietary Fiber {fdaRound.fiber(nf.fiberPerServing)}g</span>
                    <span className="nf-bold">{Math.round((nf.fiberPerServing / DRV.fiber) * 100)}%</span>
                  </div>
                  <div className="nf-line"></div>
                  {/* Total Sugars */}
                  <div className="nf-row nf-indent">
                    <span>Total Sugars {fdaRound.sugar(nf.sugarPerServing)}g</span>
                  </div>
                  {/* Added Sugars hairline starts at indent level */}
                  <div style={{ borderTop: "0.5px solid black", margin: "1px 0", marginLeft: "32px" }}></div>
                  {/* Added Sugars (= Total Sugars per user directive) */}
                  <div className="nf-row nf-indent2">
                    <span>Includes {fdaRound.sugar(nf.sugarPerServing)}g Added Sugars</span>
                    <span className="nf-bold">{Math.round((nf.sugarPerServing / 50) * 100)}%</span>
                  </div>
                  <div className="nf-line"></div>
                  {/* Protein */}
                  {calculated.pdcaas < 0.2 && (
                    <div style={{ fontSize: "9px", fontStyle: "italic" }}>
                      Not a significant source of protein
                    </div>
                  )}
                  <div className="nf-row">
                    <span><span className="nf-bold">Protein</span> {fdaRound.protein(nf.proteinPerServing)}g</span>
                    <span className="nf-bold">{Math.round(nf.percentDV)}%</span>
                  </div>
                  {/* 7pt heavy bar before vitamins */}
                  <div className="nf-line-thick"></div>
                  {/* Vitamin D */}
                  <div className="nf-row">
                    <span>Vitamin D {fdaRound.vitaminD(nf.vitaminDPerServing)}mcg</span>
                    <span className="nf-bold">{Math.round((nf.vitaminDPerServing / DRV.vitaminD) * 100)}%</span>
                  </div>
                  <div className="nf-line"></div>
                  {/* Calcium */}
                  <div className="nf-row">
                    <span>Calcium {fdaRound.calcium(nf.calciumPerServing)}mg</span>
                    <span className="nf-bold">{Math.round((nf.calciumPerServing / DRV.calcium) * 100)}%</span>
                  </div>
                  <div className="nf-line"></div>
                  {/* Iron */}
                  <div className="nf-row">
                    <span>Iron {fdaRound.iron(nf.ironPerServing)}mg</span>
                    <span className="nf-bold">{Math.round((nf.ironPerServing / DRV.iron) * 100)}%</span>
                  </div>
                  <div className="nf-line"></div>
                  {/* Potassium */}
                  <div className="nf-row">
                    <span>Potassium {fdaRound.potassium(nf.potassiumPerServing)}mg</span>
                    <span className="nf-bold">{Math.round((nf.potassiumPerServing / DRV.potassium) * 100)}%</span>
                  </div>
                  <div className="nf-line"></div>
                  <p style={{ fontSize: "8px", margin: "4px 0", lineHeight: "1.3" }}>
                    * The % Daily Value (DV) tells you how much a nutrient in a serving of food contributes to a daily diet. 2,000 calories a day is used for general nutrition advice.
                  </p>
                </div>
              </div>
            </div>

            {/* Ingredients Statement */}
            <div
              style={{
                marginTop: "16px",
                padding: "12px",
                backgroundColor: "#f8fafc",
                borderRadius: "8px",
              }}
            >
              <div
                style={{
                  fontSize: "11px",
                  fontWeight: "700",
                  marginBottom: "6px",
                  color: "#1e1b4b",
                }}
              >
                INGREDIENTS:
              </div>
              <p
                style={{
                  fontSize: "11px",
                  color: "#374151",
                  lineHeight: "1.5",
                  margin: 0,
                }}
              >
                {calculated.sortedIngredients.join(", ")}.
              </p>
              <p
                style={{
                  fontSize: "9px",
                  color: "#6b7280",
                  marginTop: "6px",
                  fontStyle: "italic",
                }}
              >
                Contains: Wheat, Soy. Per FDA 21 CFR 101.4 - listed in
                descending order of predominance by weight.
              </p>
            </div>
          </div>

          {/* Golden Ratio — live, recalculating with the sliders above. The maths
              is in src/engine/goldenRatio.ts and is shared with the full screen,
              so the two can never drift apart (see BUGS.md #9). */}
          <div
            className="card"
            style={{ maxWidth: "1500px", margin: "20px auto" }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "baseline",
                justifyContent: "space-between",
                gap: "12px",
                flexWrap: "wrap",
                marginBottom: "10px",
              }}
            >
              <h3 style={{ fontSize: "12px", fontWeight: 700, color: "#1e1b4b", margin: 0 }}>
                {t("goldenRatio.title", { defaultValue: "Golden Ratio" })}
                {goldenRatio.guessCount > 0 && (
                  <span
                    style={{
                      marginLeft: "8px",
                      fontSize: "10px",
                      fontWeight: 600,
                      color: "#92400e",
                      background: "#fef3c7",
                      borderRadius: "999px",
                      padding: "2px 8px",
                    }}
                  >
                    {t("goldenRatio.provisional", {
                      defaultValue: `Provisional · ${goldenRatio.guessCount} guessed`,
                      count: goldenRatio.guessCount,
                    })}
                  </span>
                )}
              </h3>
              <button
                type="button"
                onClick={() => setRoute("goldenratio")}
                style={{
                  background: "none",
                  border: "none",
                  color: "#7c3aed",
                  fontSize: "11px",
                  fontWeight: 600,
                  cursor: "pointer",
                  padding: 0,
                }}
              >
                {t("goldenRatio.openFull", { defaultValue: "Full view" })}
              </button>
            </div>

            {goldenRatio.weightSum <= 0 ? (
              <p style={{ fontSize: "11px", color: "#6b7280", margin: 0 }}>
                {t("goldenRatio.noProtein", {
                  defaultValue: "No classified protein in this formula.",
                })}
              </p>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "11px" }}>
                <thead>
                  <tr style={{ color: "#6b7280", textAlign: "right" }}>
                    <th style={{ textAlign: "left", fontWeight: 600, padding: "4px 6px" }}>
                      {t("goldenRatio.bucket", { defaultValue: "Bucket" })}
                    </th>
                    <th style={{ fontWeight: 600, padding: "4px 6px" }}>
                      {t("goldenRatio.target", { defaultValue: "Target" })}
                    </th>
                    <th style={{ fontWeight: 600, padding: "4px 6px" }}>
                      {t("goldenRatio.byWeight", { defaultValue: "By weight" })}
                    </th>
                    <th style={{ fontWeight: 600, padding: "4px 6px" }}>
                      {t("goldenRatio.byProtein", { defaultValue: "By protein" })}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {GOLDEN_RATIO_ROWS.map((r) => {
                    const tgt = grTargetNum[r.bucket as TargetBucket];
                    const w = goldenRatio.pctByWeight[r.bucket];
                    const p = goldenRatio.pctByProtein[r.bucket];
                    const dw = w - tgt;
                    const dp = p - tgt;
                    // On target within 10 points is green; within 1 point also
                    // glows. Outside 10 is red. Arrow as well as colour, so
                    // meaning never depends on colour alone.
                    const mark = (d: number) =>
                      Math.abs(d) <= 1 ? "" : d > 0 ? " ↑" : " ↓";
                    const cell = (d: number) => {
                      const a = Math.abs(d);
                      if (a <= 1) {
                        return {
                          color: "#15803d",
                          textShadow: "0 0 6px rgba(34,197,94,0.9), 0 0 12px rgba(34,197,94,0.5)",
                        };
                      }
                      if (a <= 10) return { color: "#15803d", textShadow: "none" };
                      return { color: "#b91c1c", textShadow: "none" };
                    };
                    return (
                      <tr key={r.bucket} style={{ borderTop: "1px solid #e5e7eb" }}>
                        <td style={{ padding: "4px 6px", color: "#1e1b4b" }}>{t(`goldenRatio.rows.${r.bucket}`)}</td>
                        <td style={{ padding: "4px 6px", textAlign: "right" }}>
                          <input
                            type="number"
                            step="1"
                            min="0"
                            max="100"
                            value={grTargets[r.bucket as TargetBucket]}
                            onChange={(e) =>
                              setGrTargets((prev) => ({ ...prev, [r.bucket]: e.target.value }))
                            }
                            style={{
                              width: "52px",
                              padding: "2px 4px",
                              textAlign: "right",
                              fontSize: "11px",
                              color: "#6b7280",
                              background: "#f9fafb",
                              border: "1px solid #e5e7eb",
                              borderRadius: "4px",
                            }}
                          />
                        </td>
                        <td
                          style={{ padding: "4px 6px", textAlign: "right", fontWeight: 600, ...cell(dw) }}
                        >
                          {w.toFixed(1)}
                          {mark(dw)}
                        </td>
                        <td
                          style={{ padding: "4px 6px", textAlign: "right", fontWeight: 600, ...cell(dp) }}
                        >
                          {p.toFixed(1)}
                          {mark(dp)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
            {Math.abs(grTargetSum - 100) > 0.05 && (
              <p style={{ fontSize: "10px", color: "#92400e", margin: "6px 0 0" }}>
                {t("goldenRatio.sumWarn", {
                  defaultValue: `Targets sum to ${grTargetSum.toFixed(1)}, not 100.`,
                  sum: grTargetSum.toFixed(1),
                })}
              </p>
            )}
            {!grTargetsAreDefault(grTargets) && (
              <button
                type="button"
                onClick={() => setGrTargets({ ...GR_DEFAULT_TARGETS })}
                style={{
                  background: "none",
                  border: "none",
                  color: "#7c3aed",
                  fontSize: "10px",
                  fontWeight: 600,
                  cursor: "pointer",
                  padding: 0,
                  marginTop: "6px",
                }}
              >
                {t("goldenRatio.resetTargets", { defaultValue: "Reset to 25 / 55 / 20" })}
              </button>
            )}
          </div>

          {/* Whole Foods Compliance — button-triggered AI check (Kimi K3), cached
              per composition. Retailer compliance against the vendored WFM banned
              list, distinct from any UPF score. */}
          <WholeFoodsComplianceCard
            items={Object.entries(calculated.totalFormula)
              .filter(([, pct]) => pct > 0)
              .sort((a, b) => b[1] - a[1])
              .map(([name, pct]) => ({
                commonName: name,
                fdaName: ALL_INGREDIENTS[name]?.fdaName || null,
                pct,
              }))}
            formulaCode={sessionPrefix}
          />

          {/* Calculation Validation */}
          <div
            className="card"
            ref={cardRefCalcValidation}
            style={{ maxWidth: "1500px", margin: "20px auto", position: "relative" }}
          >
            <DownloadButton targetRef={cardRefCalcValidation} formulaCode={sessionPrefix} sessionName={displaySessionName} cardName="Calculation_Validation" />
            <button
              onClick={() => setShowCalcAudit(!showCalcAudit)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                width: "100%",
                padding: "8px 0",
                background: "none",
                border: "none",
                cursor: "pointer",
                fontSize: "12px",
                fontWeight: "700",
                color: "#1e1b4b",
                textAlign: "left",
              }}
            >
              <span
                style={{
                  transform: showCalcAudit ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.2s",
                }}
              >
                {Icons.chevron}
              </span>
              {t('packaging.calcValidation')}
            </button>
            {showCalcAudit && (
              <div style={{ marginTop: "12px" }}>
                <button
                  onClick={() =>
                    navigator.clipboard.writeText(generateCalcAudit())
                  }
                  style={{
                    padding: "6px 12px",
                    backgroundColor: "#8b5cf6",
                    color: "white",
                    border: "none",
                    borderRadius: "6px",
                    fontSize: "10px",
                    cursor: "pointer",
                    marginBottom: "8px",
                  }}
                >
                  {t('packaging.copyToClipboard')}
                </button>
                <div className="audit-text">{generateCalcAudit()}</div>
              </div>
            )}
          </div>
        </>
      )}

      {/* PACKAGING TAB - Full Screen Dark Mode */}
      {activeTab === "packaging" && (
        <>
          {/* Full-screen dark background with hexagon pattern */}
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background:
                "linear-gradient(135deg, rgba(10,10,15,0.99) 0%, rgba(15,20,30,0.99) 50%, rgba(10,15,25,0.99) 100%)",
              zIndex: -1,
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='28' height='49' viewBox='0 0 28 49'%3E%3Cg fill-rule='evenodd'%3E%3Cg fill='%2360a5fa' fill-opacity='0.03'%3E%3Cpath d='M13.99 9.25l13 7.5v15l-13 7.5L1 31.75v-15l12.99-7.5zM3 17.9v12.7l10.99 6.34 11-6.35V17.9l-11-6.34L3 17.9zM0 15l12.98-7.5V0h-2v6.35L0 12.69v2.3zm0 18.5L12.98 41v8h-2v-6.85L0 35.81v-2.3zM15 0v7.5L27.99 15H28v-2.31h-.01L17 6.35V0h-2zm0 49v-8l12.99-7.5H28v2.31h-.01L17 42.15V49h-2z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          ></div>

          <div
            style={{
              maxWidth: "1600px",
              margin: "0 auto",
              padding: "20px",
              position: "relative",
            }}
          >
            {/* Header with Title and Return Button */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "24px",
              }}
            >
              <div>
                <h1
                  style={{
                    fontSize: "28px",
                    fontWeight: "900",
                    background:
                      "linear-gradient(135deg, #60a5fa 0%, #a78bfa 50%, #60a5fa 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    margin: 0,
                  }}
                >
                  {t('packaging.packagingDashboard')}
                </h1>
                <p
                  style={{
                    color: "rgba(255,255,255,0.5)",
                    fontSize: "11px",
                    margin: "4px 0 0",
                  }}
                >
                  {t('packaging.fdaSubtitle')}
                </p>
              </div>
              <button
                onClick={() => setActiveTab("formulation")}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "12px 24px",
                  backgroundColor: "rgba(139,92,246,0.3)",
                  border: "2px solid rgba(139,92,246,0.6)",
                  borderRadius: "8px",
                  color: "#ede9fe",
                  fontSize: "13px",
                  fontWeight: "700",
                  cursor: "pointer",
                }}
              >
                {Icons.arrowLeft} {t('packaging.returnToFormulation')}
              </button>
            </div>

            {/* Collapsible Bento Card Grid */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(12, 1fr)",
                gap: "12px",
                gridAutoRows: "minmax(auto, auto)",
              }}
            >
              {/* Card: Servings Configuration - Small */}
              <div
                ref={cardRefPkgServings}
                style={{
                  gridColumn: packagingCardCollapsed.servings
                    ? "span 2"
                    : "span 3",
                  backgroundColor: "rgba(255,255,255,0.97)",
                  borderRadius: "10px",
                  overflow: "hidden",
                  transition: "all 0.3s",
                  position: "relative",
                }}
              >
                <DownloadButton targetRef={cardRefPkgServings} formulaCode={sessionPrefix} sessionName={displaySessionName} cardName="Servings_Config" />
                <div
                  onClick={() =>
                    setPackagingCardCollapsed((prev) => ({
                      ...prev,
                      servings: !prev.servings,
                    }))
                  }
                  style={{
                    padding: "10px 14px",
                    backgroundColor: "#7c3aed",
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    cursor: "pointer",
                  }}
                >
                  <span style={{ opacity: 0.8 }}>{Icons.servings}</span>
                  <span
                    style={{ fontWeight: "700", fontSize: "11px", flex: 1 }}
                  >
                    {t('packaging.servingsConfig')}
                  </span>
                  <span
                    style={{
                      transform: packagingCardCollapsed.servings
                        ? "rotate(0deg)"
                        : "rotate(180deg)",
                      transition: "transform 0.2s",
                    }}
                  >
                    {Icons.chevron}
                  </span>
                </div>
                {!packagingCardCollapsed.servings && (
                  <div style={{ padding: "14px" }}>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "10px",
                      }}
                    >
                      <div>
                        <div
                          style={{
                            fontSize: "9px",
                            color: "#6b7280",
                            marginBottom: "4px",
                          }}
                        >
                          {t('packaging.servingsPerPackage')}
                        </div>
                        <input
                          type="number"
                          value={servingsPerContainer}
                          onChange={(e) =>
                            setServingsPerContainer(
                              Math.max(1, parseInt(e.target.value) || 1)
                            )
                          }
                          style={{
                            width: "100%",
                            padding: "8px",
                            fontSize: "18px",
                            fontWeight: "700",
                            textAlign: "center",
                            borderRadius: "6px",
                            border: "2px solid #e5e7eb",
                          }}
                        />
                      </div>
                      <div>
                        <div
                          style={{
                            fontSize: "9px",
                            color: "#6b7280",
                            marginBottom: "4px",
                          }}
                        >
                          {t('packaging.servingSize')}
                        </div>
                        <div
                          style={{
                            padding: "8px",
                            fontSize: "18px",
                            fontWeight: "700",
                            textAlign: "center",
                            backgroundColor: "#f1f5f9",
                            borderRadius: "6px",
                          }}
                        >
                          {servingSize}g
                        </div>
                      </div>
                    </div>

                    {/* Piece weight and calculation */}
                    <div
                      style={{
                        marginTop: "10px",
                        padding: "8px",
                        backgroundColor: postProcessed.enabled
                          ? "#fef3c7"
                          : "#f8fafc",
                        borderRadius: "6px",
                        fontSize: "10px",
                      }}
                    >
                      <div
                        style={{
                          fontWeight: "700",
                          color: "#374151",
                          marginBottom: "4px",
                        }}
                      >
                        {t('packaging.pieceCalculation')}
                      </div>
                      <div style={{ color: "#6b7280" }}>
                        <strong>{t('packaging.pieceWeight')}</strong> {pieceWeight}g{" "}
                        {postProcessed.enabled && t('packaging.finishedPostBake')}
                      </div>
                      <div style={{ color: "#6b7280" }}>
                        <strong>{t('packaging.piecesPerServing')}</strong> {servingSize}g ÷{" "}
                        {pieceWeight}g ={" "}
                        <strong style={{ color: "#7c3aed" }}>
                          {piecesPerServing} {t('packaging.pieces')}
                        </strong>
                      </div>
                      {postProcessed.enabled &&
                        postProcessed.asMixedPieceWeight && (
                          <div
                            style={{
                              color: "#d97706",
                              marginTop: "4px",
                              fontSize: "9px",
                            }}
                          >
                            ℹ️ {t('packaging.asMixedPieceWould')}
                            {postProcessed.asMixedPieceWeight.toFixed(1)}g
                            {t('packaging.before')} {postProcessed.weightLossPct.toFixed(1)}%
                            {t('packaging.moistureLoss')}
                          </div>
                        )}
                    </div>

                    <div
                      style={{
                        marginTop: "8px",
                        padding: "8px",
                        backgroundColor: "#f8fafc",
                        borderRadius: "6px",
                        fontSize: "10px",
                        color: "#6b7280",
                      }}
                    >
                      <div>
                        <strong>{t('packaging.netWeight')}</strong>{" "}
                        {servingSize * servingsPerContainer}g (
                        {((servingSize * servingsPerContainer) / 28.35).toFixed(
                          1
                        )}{" "}
                        oz)
                      </div>
                      <div>
                        <strong>{t('packaging.piecesPerPackage')}</strong>{" "}
                        {Math.round(servingsPerContainer * piecesPerServing)}
                      </div>
                      <div>
                        <strong>{t('packaging.perFDA')}</strong>{" "}
                        {servingsRounded.needsAbout ? t('packaging.about') + " " : ""}
                        {servingsRounded.display} {t('packaging.servings')}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Card: Nutrition Facts & Ingredients */}
              <div
                ref={cardRefPkgNutrition}
                style={{
                  gridColumn: packagingCardCollapsed.nutrition
                    ? "span 2"
                    : "span 4",
                  backgroundColor: "rgba(255,255,255,0.97)",
                  borderRadius: "10px",
                  overflow: "hidden",
                  transition: "all 0.3s",
                  position: "relative",
                }}
              >
                <DownloadButton targetRef={cardRefPkgNutrition} formulaCode={sessionPrefix} sessionName={displaySessionName} cardName="Packaging_Nutrition" />
                <div
                  onClick={() =>
                    setPackagingCardCollapsed((prev) => ({
                      ...prev,
                      nutrition: !prev.nutrition,
                    }))
                  }
                  style={{
                    padding: "10px 14px",
                    backgroundColor: "#1e1b4b",
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    cursor: "pointer",
                  }}
                >
                  <span style={{ opacity: 0.8 }}>{Icons.clipboardList}</span>
                  <span
                    style={{ fontWeight: "700", fontSize: "11px", flex: 1 }}
                  >
                    {t('packaging.nutritionFactsIngredients')}
                  </span>
                  {postProcessed.enabled && (
                    <span
                      style={{
                        fontSize: "8px",
                        backgroundColor: "rgba(251,191,36,0.3)",
                        padding: "2px 6px",
                        borderRadius: "3px",
                      }}
                    >
                      {t('packaging.postBake')}
                    </span>
                  )}
                  <span
                    style={{
                      transform: packagingCardCollapsed.nutrition
                        ? "rotate(0deg)"
                        : "rotate(180deg)",
                      transition: "transform 0.2s",
                    }}
                  >
                    {Icons.chevron}
                  </span>
                </div>
                {!packagingCardCollapsed.nutrition && (
                  <div style={{ padding: "12px" }}>
                    {/* Use FINISHED product values when post-processing is enabled */}
                    {(() => {
                      // Per FDA 21 CFR 101.9: Nutrition labeling is based on the food "as packaged" or "as prepared"
                      // For baked goods, this means the FINISHED product after moisture loss
                      const useFinished = postProcessed.enabled;
                      const nf = useFinished
                        ? postProcessed.finishedNutritionals
                        : {
                            protein: calculated.proteinPerServing,
                            fat: calculated.fatPerServing,
                            satfat: calculated.satfatPerServing,
                            transfat: calculated.transfatPerServing,
                            cholesterol: calculated.cholesterolPerServing,
                            carbs: calculated.carbsPerServing,
                            fiber: calculated.fiberPerServing,
                            sugar: calculated.sugarPerServing,
                            sodium: calculated.sodiumPerServing,
                            calories: calculated.caloriesPerServing,
                            vitaminD: calculated.vitaminDPerServing,
                            calcium: calculated.calciumPerServing,
                            iron: calculated.ironPerServing,
                            potassium: calculated.potassiumPerServing,
                          };
                      // Pieces per serving based on FINISHED piece weight
                      const finishedPiecesPerServing =
                        fdaRound.piecesPerServing(servingSize / pieceWeight);
                      // PDCAAS-corrected protein %DV
                      const proteinPercentDV = useFinished
                        ? postProcessed.adjustedPercentDV
                        : calculated.percentDV;

                      return (
                        <>
                          {useFinished && (
                            <div
                              style={{
                                fontSize: "9px",
                                color: "#92400e",
                                backgroundColor: "#fef3c7",
                                padding: "6px 8px",
                                borderRadius: "4px",
                                marginBottom: "8px",
                              }}
                            >
                              <strong>{t('packaging.postBakeValues')}</strong> {t('packaging.nutrientsConcentratedBy')}{" "}
                              {(
                                (postProcessed.concentrationFactor.overall -
                                  1) *
                                100
                              ).toFixed(1)}
                              {t('packaging.dueToMoistureLoss')} {postProcessed.weightLossPct.toFixed(1)}%
                              {t('packaging.moistureLoss')}
                            </div>
                          )}
                          <div
                            className="nf-panel"
                            style={{
                              transform: "scale(0.8)",
                              transformOrigin: "top left",
                              marginBottom: "-60px",
                              marginRight: "-50px",
                            }}
                          >
                            <div className="nf-title">Nutrition Facts</div>
                            <div className="nf-line"></div>
                            <div style={{ fontSize: "14px", fontWeight: 700 }}>
                              {servingsRounded.needsAbout ? "about " : ""}
                              {servingsRounded.display} servings per container
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "16px", fontWeight: 700 }}>
                              <span>Serving size</span>
                              <span>{servingSize}g ({finishedPiecesPerServing} piece{finishedPiecesPerServing !== 1 ? "s" : ""})</span>
                            </div>
                            <div className="nf-line-thick"></div>
                            <div style={{ fontSize: "11px", fontWeight: 900 }}>Amount per serving</div>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                              <span style={{ fontSize: "26px", fontWeight: 900 }}>Calories</span>
                              <span style={{ fontSize: "34px", fontWeight: 900 }}>{fdaRound.calories(nf.calories ?? 0)}</span>
                            </div>
                            <div className="nf-line-medium"></div>
                            <div style={{ textAlign: "right" as const, fontSize: "12px", fontWeight: 900 }}>% Daily Value*</div>
                            <div className="nf-line"></div>
                            {/* Total Fat */}
                            <div className="nf-row">
                              <span><span className="nf-bold">Total Fat</span> {fdaRound.fat(nf.fat)}g</span>
                              <span className="nf-bold">{Math.round((nf.fat / DRV.fat) * 100)}%</span>
                            </div>
                            <div className="nf-line"></div>
                            {/* Saturated Fat */}
                            <div className="nf-row nf-indent">
                              <span>Saturated Fat {fdaRound.satfat(nf.satfat)}g</span>
                              <span className="nf-bold">{Math.round((nf.satfat / DRV.satfat) * 100)}%</span>
                            </div>
                            <div className="nf-line"></div>
                            {/* Trans Fat */}
                            <div className="nf-row nf-indent">
                              <span><em>Trans</em> Fat {fdaRound.transfat(nf.transfat ?? 0)}g</span>
                            </div>
                            <div className="nf-line"></div>
                            {/* Cholesterol */}
                            <div className="nf-row">
                              <span><span className="nf-bold">Cholesterol</span> {String(fdaRound.cholesterol(nf.cholesterol ?? 0))}mg</span>
                              <span className="nf-bold">{Math.round(((nf.cholesterol ?? 0) / DRV.cholesterol) * 100)}%</span>
                            </div>
                            <div className="nf-line"></div>
                            {/* Sodium */}
                            <div className="nf-row">
                              <span><span className="nf-bold">Sodium</span> {fdaRound.sodium(nf.sodium)}mg</span>
                              <span className="nf-bold">{Math.round((nf.sodium / DRV.sodium) * 100)}%</span>
                            </div>
                            <div className="nf-line"></div>
                            {/* Total Carbohydrate */}
                            <div className="nf-row">
                              <span><span className="nf-bold">Total Carbohydrate</span> {fdaRound.carbs(nf.carbs)}g</span>
                              <span className="nf-bold">{Math.round((nf.carbs / DRV.carbs) * 100)}%</span>
                            </div>
                            <div className="nf-line"></div>
                            {/* Dietary Fiber */}
                            <div className="nf-row nf-indent">
                              <span>Dietary Fiber {fdaRound.fiber(nf.fiber)}g</span>
                              <span className="nf-bold">{Math.round((nf.fiber / DRV.fiber) * 100)}%</span>
                            </div>
                            <div className="nf-line"></div>
                            {/* Total Sugars */}
                            <div className="nf-row nf-indent">
                              <span>Total Sugars {fdaRound.sugar(nf.sugar)}g</span>
                            </div>
                            {/* Added Sugars hairline starts at indent level */}
                            <div style={{ borderTop: "0.5px solid black", margin: "1px 0", marginLeft: "32px" }}></div>
                            {/* Added Sugars (= Total Sugars) */}
                            <div className="nf-row nf-indent2">
                              <span>Includes {fdaRound.sugar(nf.sugar)}g Added Sugars</span>
                              <span className="nf-bold">{Math.round(((nf.sugar ?? 0) / 50) * 100)}%</span>
                            </div>
                            <div className="nf-line"></div>
                            {/* Protein */}
                            {calculated.pdcaas < 0.2 && (
                              <div style={{ fontSize: "10px", fontStyle: "italic" }}>
                                Not a significant source of protein
                              </div>
                            )}
                            <div className="nf-row">
                              <span><span className="nf-bold">Protein</span> {fdaRound.protein(nf.protein)}g</span>
                              <span className="nf-bold">{Math.round(proteinPercentDV)}%</span>
                            </div>
                            {/* 7pt heavy bar before vitamins */}
                            <div className="nf-line-thick"></div>
                            {/* Vitamin D */}
                            <div className="nf-row">
                              <span>Vitamin D {fdaRound.vitaminD(nf.vitaminD)}mcg</span>
                              <span className="nf-bold">{Math.round((nf.vitaminD / DRV.vitaminD) * 100)}%</span>
                            </div>
                            <div className="nf-line"></div>
                            {/* Calcium */}
                            <div className="nf-row">
                              <span>Calcium {fdaRound.calcium(nf.calcium)}mg</span>
                              <span className="nf-bold">{Math.round((nf.calcium / DRV.calcium) * 100)}%</span>
                            </div>
                            <div className="nf-line"></div>
                            {/* Iron */}
                            <div className="nf-row">
                              <span>Iron {fdaRound.iron(nf.iron)}mg</span>
                              <span className="nf-bold">{Math.round((nf.iron / DRV.iron) * 100)}%</span>
                            </div>
                            <div className="nf-line"></div>
                            {/* Potassium */}
                            <div className="nf-row">
                              <span>Potassium {fdaRound.potassium(nf.potassium)}mg</span>
                              <span className="nf-bold">{Math.round((nf.potassium / DRV.potassium) * 100)}%</span>
                            </div>
                            <div className="nf-line"></div>
                            <div style={{ fontSize: "10px", margin: "4px 0", lineHeight: "1.3" }}>
                              * The % Daily Value (DV) tells you how much a nutrient in a serving of food contributes to a daily diet. 2,000 calories a day is used for general nutrition advice.
                            </div>
                          </div>
                          <div
                            style={{
                              marginTop: "12px",
                              padding: "8px",
                              backgroundColor: "#f8fafc",
                              borderRadius: "6px",
                              fontSize: "9px",
                            }}
                          >
                            <strong>INGREDIENTS:</strong>{" "}
                            {calculated.sortedIngredients.join(", ")}.
                            <div
                              style={{
                                color: "#6b7280",
                                marginTop: "4px",
                                fontStyle: "italic",
                              }}
                            >
                              Contains: Wheat, Soy
                            </div>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>

              {/* Card: Formulation Summary */}
              <div
                ref={cardRefPkgFormulation}
                style={{
                  gridColumn: packagingCardCollapsed.formulation
                    ? "span 2"
                    : "span 5",
                  backgroundColor: "rgba(255,255,255,0.97)",
                  borderRadius: "10px",
                  overflow: "hidden",
                  transition: "all 0.3s",
                  position: "relative",
                }}
              >
                <DownloadButton targetRef={cardRefPkgFormulation} formulaCode={sessionPrefix} sessionName={displaySessionName} cardName="Formulation_Summary" />
                <div
                  onClick={() =>
                    setPackagingCardCollapsed((prev) => ({
                      ...prev,
                      formulation: !prev.formulation,
                    }))
                  }
                  style={{
                    padding: "10px 14px",
                    backgroundColor: "#1e1b4b",
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    cursor: "pointer",
                  }}
                >
                  <span style={{ opacity: 0.8 }}>{Icons.pieChart}</span>
                  <span
                    style={{ fontWeight: "700", fontSize: "11px", flex: 1 }}
                  >
                    {t('packaging.formulationSummary')}
                  </span>
                  {postProcessed.enabled && (
                    <span
                      style={{
                        fontSize: "8px",
                        backgroundColor: "rgba(251,191,36,0.3)",
                        padding: "2px 6px",
                        borderRadius: "3px",
                      }}
                    >
                      {t('packaging.postBakeAdjusted')}
                    </span>
                  )}
                  <span
                    style={{
                      transform: packagingCardCollapsed.formulation
                        ? "rotate(0deg)"
                        : "rotate(180deg)",
                      transition: "transform 0.2s",
                    }}
                  >
                    {Icons.chevron}
                  </span>
                </div>
                {!packagingCardCollapsed.formulation && (
                  <div style={{ padding: "10px" }}>
                    {/* Dropdown - only show view selector, basis is determined by post-process toggle */}
                    <div
                      style={{
                        display: "flex",
                        gap: "8px",
                        marginBottom: "10px",
                        alignItems: "center",
                      }}
                    >
                      <select
                        value={packagingFormulationView}
                        onChange={(e) =>
                          setPackagingFormulationView(e.target.value)
                        }
                        style={{
                          fontSize: "10px",
                          padding: "6px 8px",
                          borderRadius: "4px",
                          border: "1px solid #e5e7eb",
                          backgroundColor: "#f8fafc",
                          fontWeight: "600",
                        }}
                      >
                        <option value="blended">{t('packaging.blendedTotal')}</option>
                        <option value="components">{t('packaging.byComponent')}</option>
                      </select>
                      {postProcessed.enabled && (
                        <span
                          style={{
                            fontSize: "9px",
                            color: "#059669",
                            backgroundColor: "#d1fae5",
                            padding: "4px 8px",
                            borderRadius: "4px",
                          }}
                        >
                          {t('packaging.showingBothPct')}
                        </span>
                      )}
                    </div>

                    {/* Info banner when post-processing is enabled */}
                    {postProcessed.enabled && (
                      <div
                        style={{
                          fontSize: "9px",
                          color: "#1e40af",
                          backgroundColor: "#dbeafe",
                          padding: "6px 8px",
                          borderRadius: "4px",
                          marginBottom: "8px",
                        }}
                      >
                        <strong>{t('packaging.postBakeAdjustment')}</strong> {t('packaging.waterReducedFrom')}{" "}
                        {calculated.totalFormula["Water"]?.toFixed(1) || 0}
                        % →{" "}
                        {postProcessed.finishedTotalFormula["Water"]?.toFixed(
                          1
                        ) || 0}
                        % | {t('packaging.weightLoss')}{" "}
                        {postProcessed.weightLossPct.toFixed(1)}% |
                        {t('packaging.concentration')}{" "}
                        {(
                          (postProcessed.concentrationFactor.overall - 1) *
                          100
                        ).toFixed(1)}
                        %
                      </div>
                    )}

                    <div style={{ maxHeight: "320px", overflowY: "auto" }}>
                      {packagingFormulationView === "blended" ? (
                        (() => {
                          const showBothColumns = postProcessed.enabled;
                          const asMixedEntries = Object.entries(
                            calculated.totalFormula
                          )
                            .filter(([, pct]) => pct > 0.01)
                            .sort(([, a], [, b]) => b - a);
                          const finishedFormula =
                            postProcessed.finishedTotalFormula ||
                            calculated.totalFormula;

                          return (
                            <table
                              style={{
                                width: "100%",
                                fontSize: "10px",
                                borderCollapse: "collapse",
                                border: "1px solid #1e1b4b",
                              }}
                            >
                              <thead>
                                <tr
                                  style={{
                                    backgroundColor: "#1e1b4b",
                                    color: "white",
                                  }}
                                >
                                  <th
                                    style={{
                                      padding: "6px 8px",
                                      textAlign: "left",
                                      fontWeight: "700",
                                      borderBottom: "2px solid #000",
                                    }}
                                  >
                                    {t('packaging.ingredient')}
                                  </th>
                                  {showBothColumns ? (
                                    <>
                                      <th
                                        style={{
                                          padding: "6px 8px",
                                          textAlign: "right",
                                          fontWeight: "700",
                                          borderBottom: "2px solid #000",
                                          backgroundColor: "#374151",
                                        }}
                                      >
                                        {t('packaging.asMixedPct')}
                                      </th>
                                      <th
                                        style={{
                                          padding: "6px 8px",
                                          textAlign: "right",
                                          fontWeight: "700",
                                          borderBottom: "2px solid #000",
                                          backgroundColor: "#059669",
                                        }}
                                      >
                                        {t('packaging.finishedPct')}
                                      </th>
                                    </>
                                  ) : (
                                    <th
                                      style={{
                                        padding: "6px 8px",
                                        textAlign: "right",
                                        fontWeight: "700",
                                        borderBottom: "2px solid #000",
                                      }}
                                    >
                                      %
                                    </th>
                                  )}
                                  <th
                                    style={{
                                      padding: "6px 8px",
                                      textAlign: "right",
                                      fontWeight: "700",
                                      borderBottom: "2px solid #000",
                                    }}
                                  >
                                    {t('packaging.gPerSrv')}
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {asMixedEntries.map(
                                  ([name, asMixedPct], idx) => {
                                    const finishedPct =
                                      finishedFormula[name] || 0;
                                    const gPerServing =
                                      (finishedPct / 100) * servingSize; // Use finished % for g/serving
                                    const isWater = name === "Water";
                                    const pctChanged =
                                      showBothColumns &&
                                      Math.abs(finishedPct - asMixedPct) > 0.1;

                                    return (
                                      <tr
                                        key={name}
                                        style={{
                                          backgroundColor:
                                            isWater && showBothColumns
                                              ? "#fef3c7"
                                              : idx % 2 === 0
                                              ? "#ffffff"
                                              : "#f1f5f9",
                                          borderBottom: "1px solid #374151",
                                        }}
                                      >
                                        <td
                                          style={{
                                            padding: "5px 8px",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "6px",
                                            borderRight: "1px solid #e5e7eb",
                                          }}
                                        >
                                          <span
                                            style={{
                                              width: "10px",
                                              height: "10px",
                                              backgroundColor:
                                                ALL_INGREDIENTS[name]?.color ||
                                                "#ccc",
                                              borderRadius: "2px",
                                              flexShrink: 0,
                                            }}
                                          ></span>
                                          <span style={{ fontWeight: "500" }}>
                                            {ingName(name)}
                                          </span>
                                          {isWater && showBothColumns && (
                                            <span
                                              style={{
                                                fontSize: "8px",
                                                color: "#d97706",
                                              }}
                                            >
                                              ↓
                                            </span>
                                          )}
                                        </td>
                                        {showBothColumns ? (
                                          <>
                                            <td
                                              style={{
                                                padding: "5px 8px",
                                                textAlign: "right",
                                                fontWeight: "600",
                                                borderRight:
                                                  "1px solid #e5e7eb",
                                                color: "#6b7280",
                                              }}
                                            >
                                              {asMixedPct.toFixed(2)}%
                                            </td>
                                            <td
                                              style={{
                                                padding: "5px 8px",
                                                textAlign: "right",
                                                fontWeight: "700",
                                                borderRight:
                                                  "1px solid #e5e7eb",
                                                color: pctChanged
                                                  ? "#059669"
                                                  : "#374151",
                                              }}
                                            >
                                              {finishedPct.toFixed(2)}%
                                              {pctChanged && (
                                                <span
                                                  style={{
                                                    fontSize: "8px",
                                                    marginLeft: "2px",
                                                  }}
                                                >
                                                  {finishedPct > asMixedPct
                                                    ? "↑"
                                                    : "↓"}
                                                </span>
                                              )}
                                            </td>
                                          </>
                                        ) : (
                                          <td
                                            style={{
                                              padding: "5px 8px",
                                              textAlign: "right",
                                              fontWeight: "700",
                                              borderRight: "1px solid #e5e7eb",
                                            }}
                                          >
                                            {asMixedPct.toFixed(2)}%
                                          </td>
                                        )}
                                        <td
                                          style={{
                                            padding: "5px 8px",
                                            textAlign: "right",
                                            color: "#374151",
                                          }}
                                        >
                                          {gPerServing.toFixed(2)}g
                                        </td>
                                      </tr>
                                    );
                                  }
                                )}
                                <tr
                                  style={{
                                    backgroundColor: "#1e1b4b",
                                    color: "white",
                                    fontWeight: "700",
                                  }}
                                >
                                  <td style={{ padding: "6px 8px" }}>{t('packaging.total')}</td>
                                  {showBothColumns ? (
                                    <>
                                      <td
                                        style={{
                                          padding: "6px 8px",
                                          textAlign: "right",
                                        }}
                                      >
                                        100.0%
                                      </td>
                                      <td
                                        style={{
                                          padding: "6px 8px",
                                          textAlign: "right",
                                        }}
                                      >
                                        100.0%
                                      </td>
                                    </>
                                  ) : (
                                    <td
                                      style={{
                                        padding: "6px 8px",
                                        textAlign: "right",
                                      }}
                                    >
                                      100.0%
                                    </td>
                                  )}
                                  <td
                                    style={{
                                      padding: "6px 8px",
                                      textAlign: "right",
                                    }}
                                  >
                                    {servingSize.toFixed(1)}g
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          );
                        })()
                      ) : (
                        /* Component view */
                        <>
                          <div style={{ marginBottom: "12px" }}>
                            <div
                              style={{
                                fontSize: "11px",
                                fontWeight: "700",
                                color: "white",
                                backgroundColor: "#8b5cf6",
                                padding: "6px 8px",
                                borderRadius: "4px 4px 0 0",
                              }}
                            >
                              {t('packaging.dough')} ({doughRatio}% {t('packaging.ofRecipe')})
                              {postProcessed.enabled && (
                                <span
                                  style={{
                                    marginLeft: "8px",
                                    fontSize: "9px",
                                    opacity: 0.8,
                                  }}
                                >
                                  | {t('packaging.moistureLabel')}{" "}
                                  {calculated.doughMoisture.toFixed(1)}% →{" "}
                                  {targetDoughMoisture}%
                                </span>
                              )}
                            </div>
                            <table
                              style={{
                                width: "100%",
                                fontSize: "10px",
                                borderCollapse: "collapse",
                                border: "1px solid #8b5cf6",
                              }}
                            >
                              <thead>
                                {postProcessed.enabled && (
                                  <tr style={{ backgroundColor: "#ede9fe" }}>
                                    <th
                                      style={{
                                        padding: "4px 8px",
                                        textAlign: "left",
                                        fontSize: "9px",
                                      }}
                                    >
                                      {t('packaging.ingredient')}
                                    </th>
                                    <th
                                      style={{
                                        padding: "4px 8px",
                                        textAlign: "right",
                                        fontSize: "9px",
                                        color: "#6b7280",
                                      }}
                                    >
                                      {t('packaging.asMixed')}
                                    </th>
                                    <th
                                      style={{
                                        padding: "4px 8px",
                                        textAlign: "right",
                                        fontSize: "9px",
                                        color: "#059669",
                                      }}
                                    >
                                      {t('packaging.finished')}
                                    </th>
                                  </tr>
                                )}
                              </thead>
                              <tbody>
                                {Object.entries(doughFormula)
                                  .filter(([, p]) => p > 0)
                                  .sort(([, a], [, b]) => b - a)
                                  .map(([name, pct], idx) => {
                                    const finishedPct =
                                      postProcessed.finishedDoughFormula?.[
                                        name
                                      ] || pct;
                                    return (
                                      <tr
                                        key={name}
                                        style={{
                                          backgroundColor:
                                            name === "Water" &&
                                            postProcessed.enabled
                                              ? "#fef3c7"
                                              : idx % 2 === 0
                                              ? "#ffffff"
                                              : "#f5f3ff",
                                          borderBottom: "1px solid #c4b5fd",
                                        }}
                                      >
                                        <td
                                          style={{
                                            padding: "4px 8px",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "5px",
                                          }}
                                        >
                                          <span
                                            style={{
                                              width: "8px",
                                              height: "8px",
                                              backgroundColor:
                                                ALL_INGREDIENTS[name]?.color ||
                                                "#ccc",
                                              borderRadius: "2px",
                                            }}
                                          ></span>
                                          {ingName(name)}
                                        </td>
                                        {postProcessed.enabled ? (
                                          <>
                                            <td
                                              style={{
                                                padding: "4px 8px",
                                                textAlign: "right",
                                                color: "#6b7280",
                                              }}
                                            >
                                              {pct.toFixed(1)}%
                                            </td>
                                            <td
                                              style={{
                                                padding: "4px 8px",
                                                textAlign: "right",
                                                fontWeight: "700",
                                                color:
                                                  Math.abs(finishedPct - pct) >
                                                  0.1
                                                    ? "#059669"
                                                    : "#374151",
                                              }}
                                            >
                                              {finishedPct.toFixed(1)}%
                                            </td>
                                          </>
                                        ) : (
                                          <td
                                            style={{
                                              padding: "4px 8px",
                                              textAlign: "right",
                                              fontWeight: "700",
                                            }}
                                          >
                                            {pct.toFixed(1)}%
                                          </td>
                                        )}
                                      </tr>
                                    );
                                  })}
                              </tbody>
                            </table>
                          </div>
                          <div>
                            <div
                              style={{
                                fontSize: "11px",
                                fontWeight: "700",
                                color: "white",
                                backgroundColor: "#ec4899",
                                padding: "6px 8px",
                                borderRadius: "4px 4px 0 0",
                              }}
                            >
                              {t('packaging.filling')} ({fillingRatio}% {t('packaging.ofRecipe')})
                              {postProcessed.enabled && (
                                <span
                                  style={{
                                    marginLeft: "8px",
                                    fontSize: "9px",
                                    opacity: 0.8,
                                  }}
                                >
                                  | {t('packaging.moistureLabel')}{" "}
                                  {calculated.fillingMoisture.toFixed(1)}% →{" "}
                                  {targetFillingMoisture}%
                                </span>
                              )}
                            </div>
                            <table
                              style={{
                                width: "100%",
                                fontSize: "10px",
                                borderCollapse: "collapse",
                                border: "1px solid #ec4899",
                              }}
                            >
                              <thead>
                                {postProcessed.enabled && (
                                  <tr style={{ backgroundColor: "#fce7f3" }}>
                                    <th
                                      style={{
                                        padding: "4px 8px",
                                        textAlign: "left",
                                        fontSize: "9px",
                                      }}
                                    >
                                      {t('packaging.ingredient')}
                                    </th>
                                    <th
                                      style={{
                                        padding: "4px 8px",
                                        textAlign: "right",
                                        fontSize: "9px",
                                        color: "#6b7280",
                                      }}
                                    >
                                      {t('packaging.asMixed')}
                                    </th>
                                    <th
                                      style={{
                                        padding: "4px 8px",
                                        textAlign: "right",
                                        fontSize: "9px",
                                        color: "#059669",
                                      }}
                                    >
                                      {t('packaging.finished')}
                                    </th>
                                  </tr>
                                )}
                              </thead>
                              <tbody>
                                {Object.entries(fillingFormula)
                                  .filter(([, p]) => p > 0)
                                  .sort(([, a], [, b]) => b - a)
                                  .map(([name, pct], idx) => {
                                    const finishedPct =
                                      postProcessed.finishedFillingFormula?.[
                                        name
                                      ] || pct;
                                    return (
                                      <tr
                                        key={name}
                                        style={{
                                          backgroundColor:
                                            name === "Water" &&
                                            postProcessed.enabled
                                              ? "#fef3c7"
                                              : idx % 2 === 0
                                              ? "#ffffff"
                                              : "#fdf2f8",
                                          borderBottom: "1px solid #f9a8d4",
                                        }}
                                      >
                                        <td
                                          style={{
                                            padding: "4px 8px",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "5px",
                                          }}
                                        >
                                          <span
                                            style={{
                                              width: "8px",
                                              height: "8px",
                                              backgroundColor:
                                                ALL_INGREDIENTS[name]?.color ||
                                                "#ccc",
                                              borderRadius: "2px",
                                            }}
                                          ></span>
                                          {ingName(name)}
                                        </td>
                                        {postProcessed.enabled ? (
                                          <>
                                            <td
                                              style={{
                                                padding: "4px 8px",
                                                textAlign: "right",
                                                color: "#6b7280",
                                              }}
                                            >
                                              {pct.toFixed(1)}%
                                            </td>
                                            <td
                                              style={{
                                                padding: "4px 8px",
                                                textAlign: "right",
                                                fontWeight: "700",
                                                color:
                                                  Math.abs(finishedPct - pct) >
                                                  0.1
                                                    ? "#059669"
                                                    : "#374151",
                                              }}
                                            >
                                              {finishedPct.toFixed(1)}%
                                            </td>
                                          </>
                                        ) : (
                                          <td
                                            style={{
                                              padding: "4px 8px",
                                              textAlign: "right",
                                              fontWeight: "700",
                                            }}
                                          >
                                            {pct.toFixed(1)}%
                                          </td>
                                        )}
                                      </tr>
                                    );
                                  })}
                              </tbody>
                            </table>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Card: Front of Pack Claims */}
              <div
                ref={cardRefPkgClaims}
                style={{
                  gridColumn: packagingCardCollapsed.claims
                    ? "span 2"
                    : "span 4",
                  backgroundColor: "rgba(255,255,255,0.97)",
                  borderRadius: "10px",
                  overflow: "hidden",
                  transition: "all 0.3s",
                  position: "relative",
                }}
              >
                <DownloadButton targetRef={cardRefPkgClaims} formulaCode={sessionPrefix} sessionName={displaySessionName} cardName="Front_of_Pack_Claims" />
                <div
                  onClick={() =>
                    setPackagingCardCollapsed((prev) => ({
                      ...prev,
                      claims: !prev.claims,
                    }))
                  }
                  style={{
                    padding: "10px 14px",
                    backgroundColor: "#059669",
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    cursor: "pointer",
                  }}
                >
                  <span style={{ opacity: 0.8 }}>{Icons.calculator}</span>
                  <span
                    style={{ fontWeight: "700", fontSize: "11px", flex: 1 }}
                  >
                    {t('packaging.frontOfPackClaims')}
                  </span>
                  {postProcessed.enabled && (
                    <span
                      style={{
                        fontSize: "8px",
                        backgroundColor: "rgba(251,191,36,0.3)",
                        padding: "2px 6px",
                        borderRadius: "3px",
                      }}
                    >
                      {t('packaging.postBake')}
                    </span>
                  )}
                  <span
                    style={{
                      transform: packagingCardCollapsed.claims
                        ? "rotate(0deg)"
                        : "rotate(180deg)",
                      transition: "transform 0.2s",
                    }}
                  >
                    {Icons.chevron}
                  </span>
                </div>
                {!packagingCardCollapsed.claims && (
                  <div style={{ padding: "14px" }}>
                    {(() => {
                      // Use FINISHED values for claims when post-processing is enabled
                      const useFinished = postProcessed.enabled;
                      const nf = useFinished
                        ? postProcessed.finishedNutritionals
                        : {
                            protein: calculated.proteinPerServing,
                            fiber: calculated.fiberPerServing,
                          };
                      const pdcaas = useFinished
                        ? postProcessed.adjustedPdcaas
                        : calculated.pdcaas;
                      const percentDV = useFinished
                        ? postProcessed.adjustedPercentDV
                        : calculated.percentDV;
                      const fiberDV = (nf.fiber / DRV.fiber) * 100;

                      return (
                        <>
                          {useFinished && (
                            <div
                              style={{
                                fontSize: "9px",
                                color: "#92400e",
                                backgroundColor: "#fef3c7",
                                padding: "6px 8px",
                                borderRadius: "4px",
                                marginBottom: "10px",
                              }}
                            >
                              <strong>{t('packaging.postBakeClaims')}</strong> {t('packaging.basedOnFinished')}{" "}
                              {(
                                (postProcessed.concentrationFactor.overall -
                                  1) *
                                100
                              ).toFixed(1)}
                              % {t('packaging.nutrientConcentration')}
                            </div>
                          )}
                          <div
                            style={{
                              display: "flex",
                              gap: "10px",
                              marginBottom: "12px",
                              justifyContent: "center",
                            }}
                          >
                            <div
                              style={{
                                width: "80px",
                                height: "80px",
                                borderRadius: "50%",
                                background:
                                  percentDV >= 20
                                    ? "linear-gradient(135deg, #059669, #10b981)"
                                    : percentDV >= 10
                                    ? "linear-gradient(135deg, #d97706, #f59e0b)"
                                    : "linear-gradient(135deg, #6b7280, #9ca3af)",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "white",
                                boxShadow: "0 3px 10px rgba(0,0,0,0.2)",
                              }}
                            >
                              <div
                                style={{
                                  fontSize: "22px",
                                  fontWeight: "900",
                                  lineHeight: 1,
                                }}
                              >
                                {fdaRound.protein(nf.protein)}g
                              </div>
                              <div
                                style={{ fontSize: "8px", fontWeight: "600" }}
                              >
                                {t('packaging.proteinLabel')}
                              </div>
                            </div>
                            <div
                              style={{
                                width: "80px",
                                height: "80px",
                                borderRadius: "50%",
                                background:
                                  fiberDV >= 20
                                    ? "linear-gradient(135deg, #059669, #10b981)"
                                    : fiberDV >= 10
                                    ? "linear-gradient(135deg, #d97706, #f59e0b)"
                                    : "linear-gradient(135deg, #6b7280, #9ca3af)",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "white",
                                boxShadow: "0 3px 10px rgba(0,0,0,0.2)",
                              }}
                            >
                              <div
                                style={{
                                  fontSize: "22px",
                                  fontWeight: "900",
                                  lineHeight: 1,
                                }}
                              >
                                {fdaRound.fiber(nf.fiber)}g
                              </div>
                              <div
                                style={{ fontSize: "8px", fontWeight: "600" }}
                              >
                                {t('packaging.fiberLabel')}
                              </div>
                            </div>
                          </div>
                          <div
                            style={{
                              padding: "10px",
                              backgroundColor: "#ecfdf5",
                              borderRadius: "6px",
                              border: "1px solid #a7f3d0",
                              fontSize: "9px",
                            }}
                          >
                            <div
                              style={{
                                fontWeight: "700",
                                color: "#047857",
                                marginBottom: "6px",
                              }}
                            >
                              {t('packaging.allowableClaims')}
                            </div>
                            {percentDV >= 20 && (
                              <div
                                style={{
                                  color: "#065f46",
                                  marginBottom: "3px",
                                }}
                              >
                                • "EXCELLENT SOURCE OF PROTEIN"{" "}
                                <span style={{ color: "#9ca3af" }}>
                                  [21 CFR 101.54(c)]
                                </span>
                              </div>
                            )}
                            {percentDV >= 10 && percentDV < 20 && (
                              <div
                                style={{
                                  color: "#065f46",
                                  marginBottom: "3px",
                                }}
                              >
                                • "GOOD SOURCE OF PROTEIN"{" "}
                                <span style={{ color: "#9ca3af" }}>
                                  [21 CFR 101.54(c)]
                                </span>
                              </div>
                            )}
                            <div
                              style={{ color: "#065f46", marginBottom: "3px" }}
                            >
                              • "{fdaRound.protein(nf.protein)}g PROTEIN"{" "}
                              <span style={{ color: "#9ca3af" }}>
                                [21 CFR 101.13(i)]
                              </span>
                            </div>
                            {fiberDV >= 20 && (
                              <div
                                style={{
                                  color: "#065f46",
                                  marginBottom: "3px",
                                }}
                              >
                                • "EXCELLENT SOURCE OF FIBER"{" "}
                                <span style={{ color: "#9ca3af" }}>
                                  [21 CFR 101.54(b)]
                                </span>
                              </div>
                            )}
                            {fiberDV >= 10 && fiberDV < 20 && (
                              <div
                                style={{
                                  color: "#065f46",
                                  marginBottom: "3px",
                                }}
                              >
                                • "GOOD SOURCE OF FIBER"{" "}
                                <span style={{ color: "#9ca3af" }}>
                                  [21 CFR 101.54(b)]
                                </span>
                              </div>
                            )}
                            {pdcaas >= 1 && (
                              <div style={{ color: "#065f46" }}>
                                • "COMPLETE PROTEIN"{" "}
                                <span style={{ color: "#9ca3af" }}>
                                  [PDCAAS ≥ 100%]
                                </span>
                              </div>
                            )}
                          </div>
                          <div
                            style={{
                              marginTop: "6px",
                              padding: "6px",
                              backgroundColor: "#fef3c7",
                              borderRadius: "4px",
                              fontSize: "8px",
                              color: "#92400e",
                            }}
                          >
                            <strong>{t('packaging.noteLabel')}</strong> {t('packaging.claimNote')}
                          </div>
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>

              {/* Card: PDCAAS Logic */}
              <div
                ref={cardRefPkgPDCAAS}
                style={{
                  gridColumn: packagingCardCollapsed.pdcaas
                    ? "span 2"
                    : "span 4",
                  backgroundColor: "rgba(255,255,255,0.97)",
                  borderRadius: "10px",
                  overflow: "hidden",
                  transition: "all 0.3s",
                  position: "relative",
                }}
              >
                <DownloadButton targetRef={cardRefPkgPDCAAS} formulaCode={sessionPrefix} sessionName={displaySessionName} cardName="PDCAAS_Score" />
                <div
                  onClick={() =>
                    setPackagingCardCollapsed((prev) => ({
                      ...prev,
                      pdcaas: !prev.pdcaas,
                    }))
                  }
                  style={{
                    padding: "10px 14px",
                    backgroundColor: "#7c3aed",
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    cursor: "pointer",
                  }}
                >
                  <span style={{ opacity: 0.8 }}>{Icons.speedometer}</span>
                  <span
                    style={{ fontWeight: "700", fontSize: "11px", flex: 1 }}
                  >
                    {t('packaging.pdcaasCalculation')}
                  </span>
                  {postProcessed.enabled && (
                    <span
                      style={{
                        fontSize: "8px",
                        backgroundColor: "rgba(251,191,36,0.3)",
                        padding: "2px 6px",
                        borderRadius: "3px",
                      }}
                    >
                      {t('packaging.postBake')}
                    </span>
                  )}
                  <span
                    style={{
                      transform: packagingCardCollapsed.pdcaas
                        ? "rotate(0deg)"
                        : "rotate(180deg)",
                      transition: "transform 0.2s",
                    }}
                  >
                    {Icons.chevron}
                  </span>
                </div>
                {!packagingCardCollapsed.pdcaas && (
                  <div style={{ padding: "14px" }}>
                    {(() => {
                      const useFinished = postProcessed.enabled;
                      const proteinPerServ = useFinished
                        ? postProcessed.finishedNutritionals.protein
                        : calculated.proteinPerServing;
                      const pdcaas = useFinished
                        ? postProcessed.adjustedPdcaas
                        : calculated.pdcaas;
                      const limitingAA = useFinished
                        ? postProcessed.adjustedLimitingAA
                        : calculated.limitingAA;
                      const limitingScore = useFinished
                        ? postProcessed.adjustedLimitingScore
                        : calculated.limitingScore;
                      const correctedProtein = useFinished
                        ? postProcessed.adjustedCorrectedProtein
                        : calculated.correctedProteinPerServing;
                      const percentDV = useFinished
                        ? postProcessed.adjustedPercentDV
                        : calculated.percentDV;
                      const allAbove = useFinished
                        ? Math.min(limitingScore, 1) >= 0.999
                        : calculated.allAbove100;

                      return (
                        <>
                          {useFinished && (
                            <div
                              style={{
                                fontSize: "9px",
                                color: "#92400e",
                                backgroundColor: "#fef3c7",
                                padding: "6px 8px",
                                borderRadius: "4px",
                                marginBottom: "10px",
                              }}
                            >
                              <strong>{t('packaging.postBakeValues')}</strong> {t('packaging.postBakeProteinConcentrated')}
                            </div>
                          )}
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: "1fr 1fr",
                              gap: "6px",
                              marginBottom: "10px",
                            }}
                          >
                            <div
                              style={{
                                padding: "8px",
                                backgroundColor: "#fef2f2",
                                borderRadius: "6px",
                                textAlign: "center",
                              }}
                            >
                              <div
                                style={{
                                  fontSize: "8px",
                                  color: "#991b1b",
                                  fontWeight: "600",
                                }}
                              >
                                {t('packaging.uncorrected')}
                              </div>
                              <div
                                style={{
                                  fontSize: "18px",
                                  fontWeight: "800",
                                  color: "#dc2626",
                                }}
                              >
                                {proteinPerServ.toFixed(1)}g
                              </div>
                              {useFinished && (
                                <div
                                  style={{ fontSize: "7px", color: "#9ca3af" }}
                                >
                                  was {calculated.proteinPerServing.toFixed(1)}g
                                </div>
                              )}
                            </div>
                            <div
                              style={{
                                padding: "8px",
                                backgroundColor: "#dcfce7",
                                borderRadius: "6px",
                                textAlign: "center",
                              }}
                            >
                              <div
                                style={{
                                  fontSize: "8px",
                                  color: "#166534",
                                  fontWeight: "600",
                                }}
                              >
                                {t('packaging.digestibility')}
                              </div>
                              <div
                                style={{
                                  fontSize: "18px",
                                  fontWeight: "800",
                                  color: "#16a34a",
                                }}
                              >
                                {(
                                  calculated.weightedDigestibility * 100
                                ).toFixed(0)}
                                %
                              </div>
                            </div>
                            <div
                              style={{
                                padding: "8px",
                                backgroundColor: "#fef9c3",
                                borderRadius: "6px",
                                textAlign: "center",
                              }}
                            >
                              <div
                                style={{
                                  fontSize: "8px",
                                  color: "#854d0e",
                                  fontWeight: "600",
                                }}
                              >
                                {t('packaging.aaScore')}
                              </div>
                              <div
                                style={{
                                  fontSize: "18px",
                                  fontWeight: "800",
                                  color: "#ca8a04",
                                }}
                              >
                                {(Math.min(1, limitingScore) * 100).toFixed(0)}%
                              </div>
                              <div
                                style={{ fontSize: "7px", color: "#6b7280" }}
                              >
                                {allAbove
                                  ? t('packaging.cappedAt100')
                                  : `${t('packaging.limitedBy')} ${AA_NAMES[limitingAA] || limitingAA}`}
                              </div>
                            </div>
                            <div
                              style={{
                                padding: "8px",
                                backgroundColor: "#dbeafe",
                                borderRadius: "6px",
                                textAlign: "center",
                              }}
                            >
                              <div
                                style={{
                                  fontSize: "8px",
                                  color: "#1e40af",
                                  fontWeight: "600",
                                }}
                              >
                                {t('packaging.pdcaasLabel')}
                              </div>
                              <div
                                style={{
                                  fontSize: "18px",
                                  fontWeight: "800",
                                  color: "#2563eb",
                                }}
                              >
                                {(Math.min(1, pdcaas) * 100).toFixed(0)}%
                              </div>
                              {useFinished &&
                                postProcessed.pdcaasChange !== 0 && (
                                  <div
                                    style={{
                                      fontSize: "7px",
                                      color:
                                        postProcessed.pdcaasChange < 0
                                          ? "#dc2626"
                                          : "#16a34a",
                                    }}
                                  >
                                    {postProcessed.pdcaasChange > 0 ? "+" : ""}
                                    {(postProcessed.pdcaasChange * 100).toFixed(
                                      1
                                    )}
                                    %
                                  </div>
                                )}
                            </div>
                          </div>
                          <div
                            style={{
                              padding: "10px",
                              background:
                                "linear-gradient(135deg, #7c3aed, #a855f7)",
                              borderRadius: "6px",
                              textAlign: "center",
                              color: "white",
                            }}
                          >
                            <div style={{ fontSize: "9px", fontWeight: "600" }}>
                              {t('packaging.correctedProtein')}
                            </div>
                            <div
                              style={{ fontSize: "26px", fontWeight: "900" }}
                            >
                              {correctedProtein.toFixed(1)}g
                            </div>
                            <div style={{ fontSize: "9px", opacity: 0.8 }}>
                              {Math.round(percentDV)}% DV
                            </div>
                          </div>
                          <div
                            style={{
                              marginTop: "6px",
                              fontSize: "7px",
                              color: "#6b7280",
                            }}
                          >
                            <strong>Ref:</strong> {t('packaging.pdcaasRef')}
                          </div>
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>

              {/* Card: Cost of Goods - Visual */}
              <div
                ref={cardRefPkgCOGS}
                style={{
                  gridColumn: packagingCardCollapsed.cogs ? "span 2" : "span 4",
                  backgroundColor: "rgba(255,255,255,0.97)",
                  borderRadius: "10px",
                  overflow: "hidden",
                  transition: "all 0.3s",
                  position: "relative",
                }}
              >
                <DownloadButton targetRef={cardRefPkgCOGS} formulaCode={sessionPrefix} sessionName={displaySessionName} cardName="Cost_Analysis" />
                <div
                  onClick={() =>
                    setPackagingCardCollapsed((prev) => ({
                      ...prev,
                      cogs: !prev.cogs,
                    }))
                  }
                  style={{
                    padding: "10px 14px",
                    backgroundColor: "#0891b2",
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    cursor: "pointer",
                  }}
                >
                  <span style={{ opacity: 0.8 }}>{Icons.bookOpen}</span>
                  <span
                    style={{ fontWeight: "700", fontSize: "11px", flex: 1 }}
                  >
                    {t('packaging.costOfGoods')}
                  </span>
                  <span
                    style={{
                      transform: packagingCardCollapsed.cogs
                        ? "rotate(0deg)"
                        : "rotate(180deg)",
                      transition: "transform 0.2s",
                    }}
                  >
                    {Icons.chevron}
                  </span>
                </div>
                {!packagingCardCollapsed.cogs && (
                  <div style={{ padding: "14px" }}>
                    {(() => {
                      const ingredientCosts = Object.entries(
                        calculated.totalFormula
                      )
                        .map(([name, pct]) => {
                          const costPerKg =
                            ALL_INGREDIENTS[name]?.cost_per_kg || 0;
                          const costPerServing =
                            (pct / 100) * (costPerKg / 1000) * servingSize;
                          return {
                            name,
                            pct,
                            costPerKg,
                            costPerServing,
                            color: ALL_INGREDIENTS[name]?.color || "#ccc",
                          };
                        })
                        .filter((item) => item.costPerServing > 0.0001)
                        .sort((a, b) => b.costPerServing - a.costPerServing);

                      const totalCost = ingredientCosts.reduce(
                        (sum, item) => sum + item.costPerServing,
                        0
                      );
                      const maxCost = ingredientCosts[0]?.costPerServing || 1;
                      const displayItems = cogsExpanded
                        ? ingredientCosts
                        : ingredientCosts.slice(0, 6);

                      return (
                        <>
                          {/* Cost Driver Bars */}
                          <div style={{ marginBottom: "12px" }}>
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginBottom: "6px",
                              }}
                            >
                              <span
                                style={{
                                  fontSize: "9px",
                                  color: "#6b7280",
                                  fontWeight: "600",
                                }}
                              >
                                {t('packaging.costDriversPerServing')}
                              </span>
                              {ingredientCosts.length > 6 && (
                                <button
                                  onClick={() => setCogsExpanded(!cogsExpanded)}
                                  style={{
                                    fontSize: "9px",
                                    color: "#0891b2",
                                    background: "none",
                                    border: "none",
                                    cursor: "pointer",
                                    fontWeight: "600",
                                    textDecoration: "underline",
                                  }}
                                >
                                  {cogsExpanded
                                    ? t('packaging.showTop6')
                                    : `${t('packaging.showAll')} ${ingredientCosts.length}`}
                                </button>
                              )}
                            </div>
                            <div
                              style={{
                                maxHeight: cogsExpanded ? "300px" : "none",
                                overflowY: cogsExpanded ? "auto" : "visible",
                              }}
                            >
                              {displayItems.map((item, idx) => (
                                <div
                                  key={item.name}
                                  style={{
                                    marginBottom: "6px",
                                    backgroundColor:
                                      idx % 2 === 0 ? "#ffffff" : "#f8fafc",
                                    padding: "4px",
                                    borderRadius: "4px",
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      fontSize: "9px",
                                      marginBottom: "2px",
                                    }}
                                  >
                                    <span
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "4px",
                                      }}
                                    >
                                      <span
                                        style={{
                                          width: "8px",
                                          height: "8px",
                                          backgroundColor: item.color,
                                          borderRadius: "2px",
                                        }}
                                      ></span>
                                      {ingName(item.name)}
                                    </span>
                                    <span style={{ fontWeight: "700" }}>
                                      €{item.costPerServing.toFixed(4)}
                                    </span>
                                  </div>
                                  <div
                                    style={{
                                      height: "10px",
                                      backgroundColor: "#e5e7eb",
                                      borderRadius: "3px",
                                      overflow: "hidden",
                                    }}
                                  >
                                    <div
                                      style={{
                                        height: "100%",
                                        width: `${
                                          (item.costPerServing / maxCost) * 100
                                        }%`,
                                        backgroundColor: item.color,
                                        borderRadius: "3px",
                                      }}
                                    ></div>
                                  </div>
                                </div>
                              ))}
                            </div>
                            {!cogsExpanded && ingredientCosts.length > 6 && (
                              <div
                                style={{
                                  fontSize: "8px",
                                  color: "#0891b2",
                                  textAlign: "center",
                                  marginTop: "4px",
                                  cursor: "pointer",
                                }}
                                onClick={() => setCogsExpanded(true)}
                              >
                                +{ingredientCosts.length - 6} {t('packaging.moreIngredientsExpand')}
                              </div>
                            )}
                          </div>

                          {/* Summary boxes */}
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: "1fr 1fr",
                              gap: "6px",
                            }}
                          >
                            <div
                              style={{
                                padding: "8px",
                                backgroundColor: "#f0fdf4",
                                borderRadius: "6px",
                                textAlign: "center",
                              }}
                            >
                              <div
                                style={{
                                  fontSize: "8px",
                                  color: "#166534",
                                  fontWeight: "600",
                                }}
                              >
                                {t('packaging.perServing')}
                              </div>
                              <div
                                style={{
                                  fontSize: "16px",
                                  fontWeight: "800",
                                  color: "#16a34a",
                                }}
                              >
                                €{totalCost.toFixed(4)}
                              </div>
                            </div>
                            <div
                              style={{
                                padding: "8px",
                                backgroundColor: "#eff6ff",
                                borderRadius: "6px",
                                textAlign: "center",
                              }}
                            >
                              <div
                                style={{
                                  fontSize: "8px",
                                  color: "#1e40af",
                                  fontWeight: "600",
                                }}
                              >
                                {t('packaging.perPackage')}
                              </div>
                              <div
                                style={{
                                  fontSize: "16px",
                                  fontWeight: "800",
                                  color: "#2563eb",
                                }}
                              >
                                €{(totalCost * servingsPerContainer).toFixed(3)}
                              </div>
                            </div>
                            <div
                              style={{
                                padding: "8px",
                                backgroundColor: "#fef3c7",
                                borderRadius: "6px",
                                textAlign: "center",
                              }}
                            >
                              <div
                                style={{
                                  fontSize: "8px",
                                  color: "#92400e",
                                  fontWeight: "600",
                                }}
                              >
                                {t('packaging.per100g')}
                              </div>
                              <div
                                style={{
                                  fontSize: "16px",
                                  fontWeight: "800",
                                  color: "#d97706",
                                }}
                              >
                                €{((totalCost / servingSize) * 100).toFixed(4)}
                              </div>
                            </div>
                            <div
                              style={{
                                padding: "8px",
                                backgroundColor: "#fdf2f8",
                                borderRadius: "6px",
                                textAlign: "center",
                              }}
                            >
                              <div
                                style={{
                                  fontSize: "8px",
                                  color: "#9d174d",
                                  fontWeight: "600",
                                }}
                              >
                                {t('packaging.perKg')}
                              </div>
                              <div
                                style={{
                                  fontSize: "16px",
                                  fontWeight: "800",
                                  color: "#db2777",
                                }}
                              >
                                €{((totalCost / servingSize) * 1000).toFixed(2)}
                              </div>
                            </div>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>

              {/* Card: Protein & Cost Chart */}
              <div
                ref={cardRefPkgProtein}
                style={{
                  gridColumn: packagingCardCollapsed.protein
                    ? "span 2"
                    : "span 8",
                  backgroundColor: "rgba(255,255,255,0.97)",
                  borderRadius: "10px",
                  overflow: "hidden",
                  transition: "all 0.3s",
                  position: "relative",
                }}
              >
                <DownloadButton targetRef={cardRefPkgProtein} formulaCode={sessionPrefix} sessionName={displaySessionName} cardName="Protein_Cost_Chart" />
                <div
                  onClick={() =>
                    setPackagingCardCollapsed((prev) => ({
                      ...prev,
                      protein: !prev.protein,
                    }))
                  }
                  style={{
                    padding: "10px 14px",
                    backgroundColor: "#dc2626",
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    cursor: "pointer",
                  }}
                >
                  <span style={{ opacity: 0.8 }}>{Icons.barChart}</span>
                  <span
                    style={{ fontWeight: "700", fontSize: "11px", flex: 1 }}
                  >
                    {t('packaging.proteinContribCost')}
                  </span>
                  <span
                    style={{
                      transform: packagingCardCollapsed.protein
                        ? "rotate(0deg)"
                        : "rotate(180deg)",
                      transition: "transform 0.2s",
                    }}
                  >
                    {Icons.chevron}
                  </span>
                </div>
                {!packagingCardCollapsed.protein && (
                  <div style={{ padding: "14px" }}>
                    {(() => {
                      const contributions = Object.entries(
                        calculated.totalFormula
                      )
                        .map(([name, pct]) => {
                          const ing = ALL_INGREDIENTS[name] || {};
                          const proteinPer100g = ing.protein_per_100g || 0;
                          const costPerKg = ing.cost_per_kg || 0;
                          const gramsPerServing = (pct / 100) * servingSize;
                          const proteinRaw =
                            (proteinPer100g / 100) * gramsPerServing;
                          // Corrected protein per serving
                          const proteinCorrected =
                            proteinRaw * Math.min(1, calculated.pdcaas);
                          const costContrib =
                            (pct / 100) * (costPerKg / 1000) * servingSize;
                          return {
                            name: ingName(name),
                            proteinCorrected,
                            costContrib,
                            color: ing.color || "#ccc",
                          };
                        })
                        .filter((item) => item.proteinCorrected > 0.01)
                        .sort((a, b) => b.proteinCorrected - a.proteinCorrected)
                        .slice(0, 5); // Top 5 only

                      if (contributions.length === 0) {
                        return (
                          <div
                            style={{
                              height: "200px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "#6b7280",
                              fontSize: "12px",
                            }}
                          >
                            {t('packaging.noProteinIngredients')}
                          </div>
                        );
                      }

                      return (
                        <ChartErrorBoundary fallbackHeight="240px">
                        <div style={{ height: "240px" }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                              data={contributions}
                              margin={{
                                top: 10,
                                right: 20,
                                left: 10,
                                bottom: 50,
                              }}
                            >
                              <CartesianGrid
                                strokeDasharray="3 3"
                                stroke="#e5e7eb"
                              />
                              <XAxis
                                dataKey="name"
                                tick={{ fontSize: 10, fill: "#374151" }}
                                angle={-25}
                                textAnchor="end"
                                interval={0}
                                height={50}
                              />
                              <YAxis
                                yAxisId="left"
                                tick={{ fontSize: 9, fill: "#22c55e" }}
                                label={{
                                  value: t('packaging.gProteinCorrected'),
                                  angle: -90,
                                  position: "insideLeft",
                                  fontSize: 9,
                                  fill: "#22c55e",
                                }}
                              />
                              <YAxis
                                yAxisId="right"
                                orientation="right"
                                tick={{ fontSize: 9, fill: "#3b82f6" }}
                                label={{
                                  value: t('packaging.euroPerServing'),
                                  angle: 90,
                                  position: "insideRight",
                                  fontSize: 9,
                                  fill: "#3b82f6",
                                }}
                              />
                              <Tooltip
                                formatter={(value, name) => {
                                  const v = Number(value);
                                  if (name === "Protein (g)")
                                    return [
                                      `${v.toFixed(2)}g`,
                                      "Corrected Protein",
                                    ];
                                  if (name === "Cost (€)")
                                    return [`€${v.toFixed(4)}`, "Cost"];
                                  return [String(value), String(name)];
                                }}
                                contentStyle={{ fontSize: "11px" }}
                              />
                              <Legend wrapperStyle={{ fontSize: "11px" }} formatter={(value: any) => <span style={{ color: "#374151" }}>{value}</span>} />
                              <Bar
                                yAxisId="left"
                                dataKey="proteinCorrected"
                                name="Protein (g)"
                                fill="#22c55e"
                                radius={[4, 4, 0, 0]}
                              />
                              <Bar
                                yAxisId="right"
                                dataKey="costContrib"
                                name="Cost (€)"
                                fill="#3b82f6"
                                radius={[4, 4, 0, 0]}
                              />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                        </ChartErrorBoundary>
                      );
                    })()}

                    {/* Amino Acid Ninja */}
                    {(postProcessEnabled
                      ? postProcessed.adjustedPdcaas
                      : calculated.pdcaas) >= 0.85 && (
                      <div
                        style={{
                          marginTop: "10px",
                          padding: "10px",
                          background:
                            "linear-gradient(135deg, #fef3c7, #fde68a)",
                          borderRadius: "8px",
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                        }}
                      >
                        <div style={{ fontSize: "26px" }}>🥷</div>
                        <div>
                          <div
                            style={{
                              fontSize: "11px",
                              fontWeight: "700",
                              color: "#92400e",
                            }}
                          >
                            {t('packaging.aminoAcidNinja')}
                          </div>
                          <div style={{ fontSize: "9px", color: "#a16207" }}>
                            PDCAAS{" "}
                            {(Math.min(
                              1,
                              postProcessEnabled
                                ? postProcessed.adjustedPdcaas
                                : calculated.pdcaas
                            ) * 100).toFixed(0)}%
                            = {t('packaging.excellentEfficiency')}
                            {calculated.allAbove100 &&
                              ` • ${t('packaging.allEAAsExceed')}`}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Footer */}
      <div
        style={{
          maxWidth: "1500px",
          margin: "20px auto 0",
          padding: "10px",
          backgroundColor:
            activeTab === "packaging"
              ? "rgba(59,130,246,0.1)"
              : "rgba(139,92,246,0.1)",
          borderRadius: "8px",
          border:
            activeTab === "packaging"
              ? "1px solid rgba(59,130,246,0.2)"
              : "1px solid rgba(139,92,246,0.2)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: "16px",
            fontSize: "9px",
          }}
        >
          <span style={{ color: "#fecaca" }}>
            {t('claims.noClaimThreshold')}
          </span>
          <span style={{ color: "#fde68a" }}>
            {t('claims.goodSourceThreshold')}
          </span>
          <span style={{ color: "#bbf7d0" }}>
            {t('claims.excellentSourceThreshold')}
          </span>
        </div>
        <p
          style={{
            textAlign: "center",
            marginTop: "6px",
            fontSize: "8px",
            color: "rgba(255,255,255,0.4)",
          }}
        >
          21 CFR 101.9 Rounding • 21 CFR 101.4 Ingredient Statement • Canada/WHO
          Reference Pattern • PDCAAS for %DV
        </p>
        <p
          style={{
            textAlign: "center",
            marginTop: "4px",
            fontSize: "7px",
            color: "rgba(255,255,255,0.3)",
          }}
        >
          Costs in EUR (€) • Source: Supabase Postgres
        </p>
      </div>

      {/* ─── Ingredient Detail Panel (slide-out) ─── */}
      {ingredientDetailName && ALL_INGREDIENTS[ingredientDetailName] && (() => {
        const ing = ALL_INGREDIENTS[ingredientDetailName] as AppIngredient;
        const ds = editingDataSources;

        // Field definitions grouped for display
        const nutritionFields: { key: string; label: string; unit: string; value: number | string }[] = [
          { key: "protein_per_100g", label: "Protein", unit: "g/100g", value: ing.protein_per_100g },
          { key: "digestibility", label: "Digestibility", unit: "(0-1)", value: ing.digestibility },
          { key: "fat", label: "Total Fat", unit: "g/100g", value: ing.fat },
          { key: "satfat", label: "Saturated Fat", unit: "g/100g", value: ing.satfat },
          { key: "transfat", label: "Trans Fat", unit: "g/100g", value: ing.transfat },
          { key: "cholesterol", label: "Cholesterol", unit: "mg/100g", value: ing.cholesterol },
          { key: "carbs", label: "Total Carbs", unit: "g/100g", value: ing.carbs },
          { key: "fiber", label: "Fiber", unit: "g/100g", value: ing.fiber },
          { key: "sugar", label: "Sugar", unit: "g/100g", value: ing.sugar },
          { key: "sodium", label: "Sodium", unit: "mg/100g", value: ing.sodium },
          { key: "moisture", label: "Moisture", unit: "%", value: ing.moisture },
          { key: "vitamin_d", label: "Vitamin D", unit: "mcg/100g", value: ing.vitaminD },
          { key: "calcium", label: "Calcium", unit: "mg/100g", value: ing.calcium },
          { key: "iron", label: "Iron", unit: "mg/100g", value: ing.iron },
          { key: "potassium", label: "Potassium", unit: "mg/100g", value: ing.potassium },
        ];

        const aaFields: { key: string; label: string; value: number }[] = [
          { key: "aa_his", label: "Histidine", value: ing.amino_acids.histidine },
          { key: "aa_ile", label: "Isoleucine", value: ing.amino_acids.isoleucine },
          { key: "aa_leu", label: "Leucine", value: ing.amino_acids.leucine },
          { key: "aa_lys", label: "Lysine", value: ing.amino_acids.lysine },
          { key: "aa_mc", label: "Met+Cys", value: ing.amino_acids.methionine_cysteine },
          { key: "aa_pt", label: "Phe+Tyr", value: ing.amino_acids.phenylalanine_tyrosine },
          { key: "aa_thr", label: "Threonine", value: ing.amino_acids.threonine },
          { key: "aa_trp", label: "Tryptophan", value: ing.amino_acids.tryptophan },
          { key: "aa_val", label: "Valine", value: ing.amino_acids.valine },
        ];

        const fattyAcidFields: { key: string; label: string; value: number }[] = [
          { key: "fa_ala", label: "ALA 18:3 n-3", value: ing.fa_ala },
          { key: "fa_la", label: "LA 18:2 n-6", value: ing.fa_la },
          { key: "fa_oleic", label: "Oleic 18:1", value: ing.fa_oleic },
          { key: "fa_palmitic", label: "Palmitic 16:0", value: ing.fa_palmitic },
          { key: "fa_stearic", label: "Stearic 18:0", value: ing.fa_stearic },
        ];

        const fiberFractionFields: { key: string; label: string; value: number }[] = [
          { key: "fiber_insoluble", label: "Insoluble", value: ing.fiber_insoluble },
          { key: "fiber_soluble", label: "Soluble", value: ing.fiber_soluble },
          { key: "resistant_starch", label: "Resistant Starch", value: ing.resistant_starch },
        ];

        const otherFields: { key: string; label: string; value: string }[] = [
          { key: "cost_per_kg", label: "Cost", value: `€${ing.cost_per_kg}/kg` },
          { key: "fda_name", label: "FDA Name", value: ing.fdaName },
          { key: "supplier", label: "Supplier", value: ing.supplier || "" },
          { key: "sub_ingredients", label: "Sub-ingredients", value: ing.sub_ingredients || "" },
        ];

        const handleSourceChange = (fieldKey: string, value: string) => {
          setEditingDataSources((prev) => ({ ...prev, [fieldKey]: value }));
        };

        const handleSave = async () => {
          if (sourceMode !== "supabase") {
            setDataSourceSaveMsg("Saving requires Supabase mode");
            return;
          }
          setDataSourceSaving(true);
          setDataSourceSaveMsg("");
          try {
            await updateRow("ingredients", ing.id, {
              data_sources: editingDataSources,
              updated_at: new Date().toISOString(),
            });
            // Update local state
            if (dbIngredients) {
              const updated = { ...dbIngredients };
              Object.keys(updated).forEach((k) => {
                if (updated[k]?.id === ing.id) {
                  updated[k] = { ...updated[k], data_sources: { ...editingDataSources } };
                }
              });
              setDbIngredients(updated);
            }
            setDataSourceSaveMsg("Saved");
            setTimeout(() => setDataSourceSaveMsg(""), 2000);
          } catch (e: any) {
            setDataSourceSaveMsg(`Error: ${e?.message || String(e)}`);
          } finally {
            setDataSourceSaving(false);
          }
        };

        const SourceIndicator = ({ fieldKey }: { fieldKey: string }) => {
          const source = getDataSource(ds, fieldKey);
          const isFieldSpecific = ds[fieldKey] && ds[fieldKey] !== ds["_default"];
          return (
            <div style={{ display: "flex", alignItems: "center", gap: "4px", marginTop: "2px" }}>
              <span style={{
                fontSize: "9px",
                color: source ? "#6b7280" : "#d1d5db",
                fontStyle: source ? "normal" : "italic",
                flex: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}>
                {source ? (
                  <>
                    {isFieldSpecific && (
                      <span style={{ color: "#8b5cf6", marginRight: "2px" }} title={t('packaging.fieldSpecificSource')}>●</span>
                    )}
                    {source}
                  </>
                ) : "No source"}
              </span>
              <button
                onClick={() => {
                  const newVal = prompt(
                    `Source for ${fieldKey}:\n(Leave empty to use default)`,
                    ds[fieldKey] || "",
                  );
                  if (newVal !== null) {
                    if (newVal === "") {
                      const next = { ...ds };
                      delete next[fieldKey];
                      setEditingDataSources(next);
                    } else {
                      handleSourceChange(fieldKey, newVal);
                    }
                  }
                }}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: "0 2px",
                  fontSize: "9px",
                  color: "#9ca3af",
                  flexShrink: 0,
                }}
                title={`Edit source for ${fieldKey}`}
              >
                ✎
              </button>
            </div>
          );
        };

        return (
          <div
            style={{
              position: "fixed",
              top: 0,
              right: 0,
              width: "420px",
              maxWidth: "90vw",
              height: "100vh",
              backgroundColor: "#fff",
              boxShadow: "-4px 0 20px rgba(0,0,0,0.15)",
              zIndex: 10000,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            {/* Header */}
            <div style={{
              padding: "16px",
              borderBottom: "2px solid #e5e7eb",
              backgroundColor: "#f8fafc",
              flexShrink: 0,
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <h3 style={{ margin: 0, fontSize: "16px", fontWeight: "700", color: "#1e1b4b" }}>
                  {ingredientDetailName}
                </h3>
                <button
                  onClick={() => { setIngredientDetailName(null); setShowAllSources(false); }}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    fontSize: "20px",
                    color: "#6b7280",
                    padding: "4px",
                  }}
                >
                  ×
                </button>
              </div>
              {ing.fdaName !== ingredientDetailName && (
                <div style={{ fontSize: "11px", color: "#6b7280", marginTop: "2px" }}>
                  FDA: {ing.fdaName}
                </div>
              )}
              {/* Primary Data Source */}
              <div style={{
                marginTop: "10px",
                padding: "8px",
                backgroundColor: "#ede9fe",
                borderRadius: "6px",
                border: "1px solid #c4b5fd",
              }}>
                <div style={{ fontSize: "9px", fontWeight: "600", color: "#7c3aed", marginBottom: "4px" }}>
                  {t('ingredientDetail.primaryDataSource')}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <input
                    type="text"
                    value={ds["_default"] || ""}
                    onChange={(e) => handleSourceChange("_default", e.target.value)}
                    placeholder={t('packaging.sourceExample')}
                    style={{
                      flex: 1,
                      padding: "4px 8px",
                      fontSize: "11px",
                      border: "1px solid #c4b5fd",
                      borderRadius: "4px",
                      backgroundColor: "#fff",
                    }}
                  />
                </div>
              </div>
              {/* Save + toggle */}
              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "8px" }}>
                <button
                  onClick={handleSave}
                  disabled={dataSourceSaving || sourceMode !== "supabase"}
                  style={{
                    padding: "4px 12px",
                    fontSize: "11px",
                    fontWeight: "600",
                    backgroundColor: sourceMode === "supabase" ? "#7c3aed" : "#d1d5db",
                    color: "#fff",
                    border: "none",
                    borderRadius: "4px",
                    cursor: sourceMode === "supabase" ? "pointer" : "not-allowed",
                    opacity: dataSourceSaving ? 0.6 : 1,
                  }}
                >
                  {dataSourceSaving ? t('packaging.savingSources') : t('packaging.saveSources')}
                </button>
                <button
                  onClick={() => setShowAllSources(!showAllSources)}
                  style={{
                    padding: "4px 10px",
                    fontSize: "10px",
                    backgroundColor: showAllSources ? "#e0e7ff" : "#f1f5f9",
                    color: showAllSources ? "#4338ca" : "#6b7280",
                    border: `1px solid ${showAllSources ? "#a5b4fc" : "#e5e7eb"}`,
                    borderRadius: "4px",
                    cursor: "pointer",
                  }}
                >
                  {showAllSources ? t('ingredientDetail.hideSources') : t('packaging.showAllSources')}
                </button>
                {dataSourceSaveMsg && (
                  <span style={{
                    fontSize: "10px",
                    color: dataSourceSaveMsg.startsWith("Error") ? "#dc2626" : "#16a34a",
                    fontWeight: "600",
                  }}>
                    {dataSourceSaveMsg}
                  </span>
                )}
                {sourceMode !== "supabase" && (
                  <span style={{ fontSize: "9px", color: "#9ca3af", fontStyle: "italic" }}>
                    {t('common.readOnlyDemoMode')}
                  </span>
                )}
              </div>
            </div>

            {/* Scrollable content */}
            <div style={{ flex: 1, overflowY: "auto", padding: "12px 16px" }}>
              {/* Notes / Legacy Spec Ref */}
              {ing.notes_spec_ref && (
                <div style={{
                  padding: "8px",
                  backgroundColor: "#fefce8",
                  borderRadius: "6px",
                  border: "1px solid #fde68a",
                  marginBottom: "12px",
                  fontSize: "10px",
                  color: "#92400e",
                }}>
                  <strong>{t('ingredientForm.notesSpecRef')}:</strong> {ing.notes_spec_ref}
                </div>
              )}

              {/* Nutrition Fields */}
              <div style={{ marginBottom: "16px" }}>
                <div style={{
                  fontSize: "11px",
                  fontWeight: "700",
                  color: "#374151",
                  marginBottom: "6px",
                  borderBottom: "1px solid #e5e7eb",
                  paddingBottom: "4px",
                }}>
                  {t('ingredientDetail.nutritionPer100g')}
                </div>
                {nutritionFields.map((f) => (
                  <div key={f.key} style={{
                    padding: "4px 0",
                    borderBottom: "1px solid #f3f4f6",
                  }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                      <span style={{ fontSize: "11px", color: "#374151" }}>{f.label}</span>
                      <span style={{ fontSize: "11px", fontWeight: "600", color: "#1e1b4b" }}>
                        {typeof f.value === "number" ? f.value.toFixed(2) : f.value} <span style={{ fontSize: "9px", color: "#9ca3af" }}>{f.unit}</span>
                      </span>
                    </div>
                    {showAllSources && <SourceIndicator fieldKey={f.key} />}
                  </div>
                ))}
              </div>

              {/* Amino Acid Profile */}
              <div style={{ marginBottom: "16px" }}>
                <div style={{
                  fontSize: "11px",
                  fontWeight: "700",
                  color: "#374151",
                  marginBottom: "6px",
                  borderBottom: "1px solid #e5e7eb",
                  paddingBottom: "4px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "baseline",
                }}>
                  <span>{t('ingredientForm.aminoAcids')}</span>
                  {showAllSources && (
                    <span style={{ fontWeight: "400" }}>
                      <SourceIndicator fieldKey="aa_profile" />
                    </span>
                  )}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "2px" }}>
                  {aaFields.map((f) => (
                    <div key={f.key} style={{
                      padding: "3px 6px",
                      backgroundColor: "#f8fafc",
                      borderRadius: "3px",
                      display: "flex",
                      justifyContent: "space-between",
                      fontSize: "10px",
                    }}>
                      <span style={{ color: "#6b7280" }}>{f.label}</span>
                      <span style={{ fontWeight: "600", color: "#1e1b4b" }}>{f.value}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* BCAA Summary */}
              <div style={{
                padding: "4px 0",
                borderBottom: "1px solid #f3f4f6",
                marginBottom: "4px",
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                  <span style={{ fontSize: "11px", color: "#374151", fontStyle: "italic" }}>{t('ingredientForm.bcaa')}</span>
                  <span style={{ fontSize: "11px", fontWeight: "600", color: "#1e1b4b" }}>
                    {ing.bcaa_mg_per_g != null ? `${ing.bcaa_mg_per_g} mg/g` : "—"}
                    <span style={{ fontSize: "9px", color: "#9ca3af", marginLeft: "4px" }}>calc</span>
                  </span>
                </div>
                {showAllSources && <SourceIndicator fieldKey="bcaa_mg_per_g" />}
              </div>

              {/* Fatty Acid Profile */}
              <div style={{ marginBottom: "16px" }}>
                <div style={{
                  fontSize: "11px",
                  fontWeight: "700",
                  color: "#374151",
                  marginBottom: "6px",
                  borderBottom: "1px solid #e5e7eb",
                  paddingBottom: "4px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "baseline",
                }}>
                  <span>{t('ingredientForm.fattyAcidProfile')}</span>
                  {showAllSources && (
                    <span style={{ fontWeight: "400" }}>
                      <SourceIndicator fieldKey="fatty_acids" />
                    </span>
                  )}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "2px" }}>
                  {fattyAcidFields.map((f) => (
                    <div key={f.key} style={{
                      padding: "3px 6px",
                      backgroundColor: "#f8fafc",
                      borderRadius: "3px",
                      display: "flex",
                      justifyContent: "space-between",
                      fontSize: "10px",
                    }}>
                      <span style={{ color: "#6b7280" }}>{f.label}</span>
                      <span style={{ fontWeight: "600", color: "#1e1b4b" }}>{f.value}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Fiber Fractions */}
              <div style={{ marginBottom: "16px" }}>
                <div style={{
                  fontSize: "11px",
                  fontWeight: "700",
                  color: "#374151",
                  marginBottom: "6px",
                  borderBottom: "1px solid #e5e7eb",
                  paddingBottom: "4px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "baseline",
                }}>
                  <span>{t('ingredientDetail.fiberFractions')}</span>
                  {showAllSources && (
                    <span style={{ fontWeight: "400" }}>
                      <SourceIndicator fieldKey="fiber_fractions" />
                    </span>
                  )}
                </div>
                {fiberFractionFields.map((f) => (
                  <div key={f.key} style={{
                    padding: "4px 0",
                    borderBottom: "1px solid #f3f4f6",
                  }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                      <span style={{ fontSize: "11px", color: "#374151" }}>{f.label}</span>
                      <span style={{ fontSize: "11px", fontWeight: "600", color: "#1e1b4b" }}>
                        {f.value} <span style={{ fontSize: "9px", color: "#9ca3af" }}>g/100g</span>
                      </span>
                    </div>
                    {showAllSources && <SourceIndicator fieldKey={f.key} />}
                  </div>
                ))}
              </div>

              {/* Reference Digestibility */}
              {(ing.ref_digest_product || ing.ref_digest_value != null) && (
                <div style={{ marginBottom: "16px" }}>
                  <div style={{
                    fontSize: "11px",
                    fontWeight: "700",
                    color: "#374151",
                    marginBottom: "6px",
                    borderBottom: "1px solid #e5e7eb",
                    paddingBottom: "4px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "baseline",
                  }}>
                    <span>{t('ingredientForm.referenceDigestibility')}</span>
                    {showAllSources && (
                      <span style={{ fontWeight: "400" }}>
                        <SourceIndicator fieldKey="ref_digestibility" />
                      </span>
                    )}
                  </div>
                  {ing.ref_digest_product && (
                    <div style={{ padding: "4px 0", borderBottom: "1px solid #f3f4f6" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                        <span style={{ fontSize: "11px", color: "#374151" }}>{t('ingredientForm.referenceProduct')}</span>
                        <span style={{ fontSize: "10px", color: "#1e1b4b", maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {ing.ref_digest_product}
                        </span>
                      </div>
                    </div>
                  )}
                  {ing.ref_digest_value != null && (
                    <div style={{ padding: "4px 0", borderBottom: "1px solid #f3f4f6" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                        <span style={{ fontSize: "11px", color: "#374151" }}>{t('ingredientForm.refDigestibility')}</span>
                        <span style={{ fontSize: "11px", fontWeight: "600", color: "#1e1b4b" }}>{ing.ref_digest_value}</span>
                      </div>
                    </div>
                  )}
                  {ing.ref_digest_source && (
                    <div style={{ padding: "4px 0", borderBottom: "1px solid #f3f4f6" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                        <span style={{ fontSize: "11px", color: "#374151" }}>{t('formulation.citation')}</span>
                        <span style={{ fontSize: "10px", color: "#6b7280", maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {ing.ref_digest_source}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Other Fields */}
              <div style={{ marginBottom: "16px" }}>
                <div style={{
                  fontSize: "11px",
                  fontWeight: "700",
                  color: "#374151",
                  marginBottom: "6px",
                  borderBottom: "1px solid #e5e7eb",
                  paddingBottom: "4px",
                }}>
                  Other
                </div>
                {otherFields.map((f) => (
                  <div key={f.key} style={{
                    padding: "4px 0",
                    borderBottom: "1px solid #f3f4f6",
                  }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                      <span style={{ fontSize: "11px", color: "#374151" }}>{f.label}</span>
                      <span style={{
                        fontSize: "11px",
                        fontWeight: "600",
                        color: "#1e1b4b",
                        maxWidth: "200px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}>
                        {f.value || "—"}
                      </span>
                    </div>
                    {showAllSources && <SourceIndicator fieldKey={f.key} />}
                  </div>
                ))}
              </div>

              {/* All Sources Audit View */}
              {showAllSources && (
                <div style={{
                  padding: "10px",
                  backgroundColor: "#f0fdf4",
                  borderRadius: "6px",
                  border: "1px solid #bbf7d0",
                  marginBottom: "16px",
                }}>
                  <div style={{
                    fontSize: "10px",
                    fontWeight: "700",
                    color: "#166534",
                    marginBottom: "6px",
                  }}>
                    {t('ingredientDetail.allDataSources')}
                  </div>
                  {Object.entries(ds).length === 0 ? (
                    <div style={{ fontSize: "10px", color: "#6b7280", fontStyle: "italic" }}>
                      {t('ingredientDetail.noSourcesConfigured')}
                    </div>
                  ) : (
                    Object.entries(ds).map(([key, val]) => (
                      <div key={key} style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "2px 0",
                        borderBottom: "1px solid #dcfce7",
                        fontSize: "10px",
                      }}>
                        <span style={{
                          color: key === "_default" ? "#7c3aed" : "#374151",
                          fontWeight: key === "_default" ? "600" : "400",
                        }}>
                          {key === "_default" ? "Default" : key}
                        </span>
                        <span style={{
                          color: "#6b7280",
                          maxWidth: "220px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}>
                          {val}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Backdrop for ingredient detail panel */}
      {ingredientDetailName && (
        <div
          onClick={() => { setIngredientDetailName(null); setShowAllSources(false); }}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0,0,0,0.3)",
            zIndex: 9999,
          }}
        />
      )}

      {/* Sticky Session Indicator - Draggable & Rotatable */}
      <div
        style={{
          position: "fixed",
          left: `${stickyPosition.x}px`,
          top: `${stickyPosition.y}px`,
          padding: stickyOrientation === "horizontal" ? "8px 16px" : "16px 8px",
          backgroundColor: "rgba(139,92,246,0.95)",
          border: "2px solid rgba(199,210,254,0.6)",
          borderRadius: "8px",
          color: "white",
          fontSize: "11px",
          fontWeight: "700",
          display: "flex",
          alignItems: "center",
          gap: "8px",
          cursor: isDragging ? "grabbing" : "grab",
          boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
          zIndex: 9999,
          userSelect: "none",
          writingMode:
            stickyOrientation === "vertical" ? "vertical-rl" : "horizontal-tb",
          textOrientation:
            stickyOrientation === "vertical" ? "mixed" : "initial",
        }}
        onMouseDown={handleMouseDown}
      >
        <span>
          {sessionPrefix} Mod {displaySessionName}
        </span>
        <button
          onClick={(e) => {
            e.stopPropagation();
            setStickyOrientation((prev) =>
              prev === "horizontal" ? "vertical" : "horizontal"
            );
          }}
          style={{
            background: "rgba(255,255,255,0.2)",
            border: "none",
            borderRadius: "4px",
            padding: "4px 6px",
            cursor: "pointer",
            color: "white",
            fontSize: "10px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          title="Rotate"
        >
          {Icons.refresh}
        </button>
      </div>
    </div>
  );
}
